/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback, useMemo, useDeferredValue } from "react";
import { createPortal } from "react-dom";
import { SHIFTIQ_ENABLED } from "../../features";
import { API_BASE, SHIFTIQ_API, getAuthHeaders, getOpsToken, isAbortError } from "../../api/client";
import { usePgStores } from "../../stores/StoresProvider";
import StoreSwitcherDialog from "./StoreSwitcherDialog";
import {
  allowedStoresForUser,
  locationIdForStore,
  locationScopeQuery,
  storeNameForLocationId,
  storeRecordForLabel,
} from "../../lib/storeScope";
import { useSettledValue } from "../../hooks/useSettledValue";
import { useStaff, useShiftIQ, usePeopleCost, useHeadcountPlanning, useFoxlineSlotSync, useErganiSlots, useScheduleUnseenChanges } from "../../hooks";
import { Icon, LoadingPulse, BlockingActionOverlay } from "../../ui";
import { useIsMobile } from "../../hooks/useIsMobile";
import { GROUPS, schedulingGroupFor, ganttDisplayRoleKey, isKitchenStaffL, employeeSpecialtyLabel } from "../../lib/roles";
import { parseISODateLocal, getScheduleBuilderInitialWeekOffset, weekOffsetForMondayISO } from "../../lib/dates";
import { builderQueryFromSearch, historyTitle, pageFromPathname, rememberHistoryEntry, searchForBuilder } from "../../routing/paths";
import { noteBuilderHref, setLeaveGuard } from "../../routing/leaveGuard";
import { defaultBuilderStore, storeSupportsShiftIq } from "../../theme";
import { resolveSharedStoreName, rememberStoreByName } from "../../lib/selectedStore";
import { findStaffForRotaRow, foxlineStaffRecordForCode, guestRecordForRotaCode, rosterScheduleKey, staffRecordMatchesCode } from "../../employeeMatch";
import { isOpsTestEmployeeCode } from "../../lib/softone";
import { employeeMatchesManagerStore, homeStoreForEmployee, isCrossBranchEmployee } from "../../lib/employeeStore";
import { STORE_DEFAULT_HOURS } from "../../lib/leave";
import { parseShiftEndValue, isValidShiftRange, intervalEndHourFloat, timelineBarGeometry } from "../../shiftTimeUtils";
import { slotKey, lookupSlotByKey, SLOT_CLASS, displaySlotValue, foxlineGridHint, slotClassTone, slotIsFoxlineDriftAlert, slotSoftoneChangeLocked, softoneValueWhenInternalBlank, displayedLabourHours, displayedScheduleValue, peopleCostPctWithDisplayedSlots, formatSoftoneWriteNotice, employeeCodeDigits, softoneChangeLockLabel } from "../../lib/softoneSlotSync";
import { internalMatchesSoftone, scheduleRibbonsVisible, scheduleSourceCell } from "../../lib/scheduleSource";
import { collapseScheduleChangeRows, slotNeedsPendingReviewForUser } from "../../lib/scheduleUnseenChanges";

import { GanttCompareView } from "./GanttCompareView";
import { SEED_SCHEDULE, buildSeedSchedule } from "./scheduleSeed";
import {
  scheduleBuilderTableColGroup,
  scheduleBuilderNameColWidthFromStorage,
  SCHEDULE_NAME_COL_DEFAULT,
  SCHEDULE_NAME_COL_MIN,
  SCHEDULE_NAME_COL_MAX,
} from "./scheduleTable";
import {
  SPECIAL_STATUSES,
  mergeFetchedSchedulePreservingDirtyLocal,
  formatScheduleMergeConflictToast,
  SCHEDULE_CONFLICT_TOAST_MS,
  mergeScheduleConflictCellMaps,
  mergeAuxMapForDirtyCells,
  hasRequestedRestDay,
  isScheduleCellDirtyAgainstBaseline,
  isScheduleWeekDirtyAgainstBaseline,
  normalizeContractedShiftHours,
  shiftPresetsForHours,
  isRestDayMarkedRequested,
  restDayPendingKey,
  findRequestedRestDayRow,
  draftSlotsFromDisplayedDay,
  enrichGanttDataWithDisplayedSchedule,
  isDraftSlotPlaceholder,
  mergeSidebarDraftSlots,
  resolveEmployeeName,
  scheduleToastMessage,
  scheduleToastDurationMs,
  scheduleToastTone,
  slotDraftDisplayName,
  specialStatusDisplay,
  EmployeeWarningRichTooltip,
} from "./scheduleHelpers";
import {
  DailyStaffingPanel,
  ScheduleBuilderDayHeaderMetrics,
  ScheduleBuilderPeopleCostTheadRow,
  ScheduleBuilderGridLegend,
  PersonTimelineView,
  EmployeeWarningBadgePortalTooltip,
  buildScheduleAlertDigest,
  scheduleLogChangeSilent,
  getTimeShiftCellVisual,
  ScheduleSectionCollapsedHeader,
} from "./scheduleComponents";
import { DayOffOfferDialog } from "./DayOffOfferDialog";
import { ScheduleLadderBar } from "./ScheduleLadderBar";
import { canBuildSchedule, canEditLadderWeek, canRejectLadder, LADDER_ENDPOINTS, ladderActionLabel, ladderActionRequiresValidation, ladderAllowsSoftonePush, ladderLockedContactHint, ladderRejectNotice, ladderRejectReturnLabel, ladderStatusAfterAction, ladderStatusLabel, primaryLadderAction } from "../../lib/scheduleLadder";
import {
  LoanModal,
  LoanRequestModal,
  findStaffLoanForCell,
  loanShowsShiftAtHost,
  ShiftPicker,
  StaffingHeatmap,
  OtherStoreLoanCell,
} from "./scheduleLoans";
import { FoxlineDriftCellBody, FoxlineSlotMarker, FoxlineSlotPopup, SoftoneLockedCellBody, SoftoneNotOpsMarker } from "./FoxlineSlotPopup";
import { ErganiSlotMarker, erganiGridHint } from "./ErganiSlotMarker";
import { ScheduleSourceBar } from "./ScheduleSourceBar";
import { SlotHistoryModal } from "./SlotHistoryModal";
import { ScheduleUnseenBanner, UnseenSlotMarker } from "./ScheduleUnseenBanner";
import { WorkMonitorCacheBar } from "./WorkMonitorCacheBar";
import { canPushFoxline, isEmployeeRole } from "../../identity/access";
import { PendingReviewMarker, WeeklyChangesPanel } from "./WeeklyChangesPanel";

/** Timeline beta on the builder. Flip back on when the view should show again. */
const BUILDER_TIMELINE_ENABLED = false;

export function WeekPicker({ weekStartISO, goLiveWeek, lang, onCommit }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(weekStartISO);
  const [cursor, setCursor] = useState(() => new Date(`${weekStartISO}T00:00:00`));
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setDraft(weekStartISO);
    setCursor(new Date(`${weekStartISO}T00:00:00`));
    const onDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, weekStartISO]);

  function toISO(date) {
    const yr = date.getFullYear();
    const mo = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${yr}-${mo}-${day}`;
  }

  function mondayOf(iso) {
    const date = new Date(`${iso}T00:00:00`);
    const day = date.getDay();
    date.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
    return toISO(date);
  }

  function addDays(iso, days) {
    const date = new Date(`${iso}T00:00:00`);
    date.setDate(date.getDate() + days);
    return toISO(date);
  }

  function rangeLabel(iso) {
    const start = new Date(`${iso}T00:00:00`);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const fmt = (date) => date.toLocaleDateString(lang === "gr" ? "el-GR" : "en-GB", { day: "numeric", month: "short" });
    return `${fmt(start)} – ${fmt(end)}`;
  }

  const monthLabel = cursor.toLocaleDateString(lang === "gr" ? "el-GR" : "en-GB", { month: "long", year: "numeric" });
  const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = new Date(firstOfMonth);
  const lead = (firstOfMonth.getDay() + 6) % 7;
  gridStart.setDate(1 - lead);
  const cells = Array.from({ length: 42 }, (_, i) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + i);
    return date;
  });
  const weekdayLabels = lang === "gr"
    ? ["Δε", "Τρ", "Τε", "Πε", "Πα", "Σα", "Κυ"]
    : ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
  const draftEnd = addDays(draft, 6);
  const sameWeek = draft === weekStartISO;

  return (
    <div ref={rootRef} style={{ position: "relative", flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          height: 34,
          padding: "0 12px",
          borderRadius: 10,
          border: open ? "1.5px solid var(--green)" : "1.5px solid var(--border)",
          background: open ? "var(--green-light)" : "var(--surface2)",
          fontSize: 13,
          fontWeight: 800,
          color: "var(--text-primary)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>{rangeLabel(weekStartISO)}</span>
        <span style={{ fontSize: 10, color: "var(--text-muted)" }} aria-hidden>▾</span>
      </button>
      {open ? (
        <div
          style={{
            position: "absolute",
            top: 42,
            left: 0,
            zIndex: 220,
            width: 292,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: 14,
            boxShadow: "0 16px 40px rgba(0,0,0,0.14)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <button
              type="button"
              aria-label={lang === "gr" ? "Προηγούμενος μήνας" : "Previous month"}
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              style={{ width: 32, height: 32, borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface2)", cursor: "pointer", fontWeight: 800, color: "var(--text-primary)" }}
            >
              ‹
            </button>
            <div style={{ fontSize: 14, fontWeight: 800, textTransform: "capitalize", color: "var(--text-primary)" }}>{monthLabel}</div>
            <button
              type="button"
              aria-label={lang === "gr" ? "Επόμενος μήνας" : "Next month"}
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              style={{ width: 32, height: 32, borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface2)", cursor: "pointer", fontWeight: 800, color: "var(--text-primary)" }}
            >
              ›
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 6 }}>
            {weekdayLabels.map((name) => (
              <div key={name} style={{ textAlign: "center", fontSize: 10, fontWeight: 800, letterSpacing: "0.04em", color: "var(--text-muted)", padding: "4px 0" }}>{name}</div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {cells.map((date) => {
              const iso = toISO(date);
              const weekMonday = mondayOf(iso);
              const inDraft = iso >= draft && iso <= draftEnd;
              const beforeGoLive = weekMonday < goLiveWeek;
              const inMonth = date.getMonth() === cursor.getMonth();
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={beforeGoLive}
                  onClick={() => setDraft(weekMonday)}
                  style={{
                    height: 32,
                    border: "none",
                    borderRadius: inDraft ? 8 : 8,
                    background: inDraft ? "var(--green)" : "transparent",
                    color: inDraft ? "#fff" : inMonth ? "var(--text-primary)" : "var(--text-muted)",
                    fontSize: 12,
                    fontWeight: inDraft ? 800 : 600,
                    cursor: beforeGoLive ? "not-allowed" : "pointer",
                    opacity: beforeGoLive ? 0.35 : inMonth ? 1 : 0.45,
                  }}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)" }}>{rangeLabel(draft)}</div>
            <button
              type="button"
              disabled={sameWeek}
              onClick={() => {
                onCommit(draft);
                setOpen(false);
              }}
              style={{
                border: "none",
                borderRadius: 8,
                padding: "7px 12px",
                background: "var(--green)",
                color: "#fff",
                fontSize: 12,
                fontWeight: 800,
                cursor: sameWeek ? "default" : "pointer",
                opacity: sameWeek ? 0.45 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {lang === "gr" ? "Εμφάνιση" : "Show week"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function ScheduleBuilderPage({ user, lang, allUsers }) {
  const SCHEDULE_GO_LIVE_WEEK = "2026-05-25";
  const { storeNames, storeRecords, loaded: storesLoaded } = usePgStores();
  const defaultPhysicalStore = defaultBuilderStore(storeNames, storeRecords);
  const mountSearch = useRef(
    typeof window !== "undefined" ? window.location.search : ""
  ).current;
  const initialBuilderQuery = useRef(builderQueryFromSearch(mountSearch)).current;
  const [focusDay, setFocusDay] = useState(null);
  const [focusPanelReady, setFocusPanelReady] = useState(false);
  const [vsOptimalActive, setVsOptimalActive] = useState(() => initialBuilderQuery.view === "optimal");
  const ganttNeedsRefreshRef = useRef(false);
  const [ganttRefreshKey, setGanttRefreshKey] = useState(0);
  const [timelineActive, setTimelineActive] = useState(
    () => BUILDER_TIMELINE_ENABLED && initialBuilderQuery.view === "timeline"
  );
  const [gridSource, setGridSource] = useState("internal");
  const currentView = useMemo(
    () => (
      !SHIFTIQ_ENABLED
        ? "schedule"
        : (timelineActive ? "timeline" : vsOptimalActive ? "vsOptimal" : "schedule")
    ),
    [timelineActive, vsOptimalActive]
  );

  useEffect(() => {
    if (!SHIFTIQ_ENABLED) {
      setVsOptimalActive(false);
      setTimelineActive(false);
    } else if (!BUILDER_TIMELINE_ENABLED) {
      setTimelineActive(false);
    }
  }, []);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const saved = localStorage.getItem("pg_sidebar_width");
      const parsed = saved ? parseInt(saved, 10) : null;
      return parsed && parsed >= 300 && parsed <= window.innerWidth * 0.7 ? parsed : 360;
    } catch { return 360; }
  });
  const [nameColWidth, setNameColWidth] = useState(scheduleBuilderNameColWidthFromStorage);
  const [sidebarFullscreen, setSidebarFullscreen] = useState(() => {
    try { return localStorage.getItem("pg_sidebar_fullscreen") === "true"; }
    catch { return false; }
  });
  const [hoveredDayDate, setHoveredDayDate] = useState(null);
  const [dayOffOffer, setDayOffOffer] = useState(null);
  const [dayOffNotices, setDayOffNotices] = useState([]);
  const [dayOffNoticeTick, setDayOffNoticeTick] = useState(0);
  const [ganttScrollTarget, setGanttScrollTarget] = useState(null);
  const [ladderWeek, setLadderWeek] = useState(null);
  const [ladderRefreshKey, setLadderRefreshKey] = useState(0);
  const [ladderSimpleConfirm, setLadderSimpleConfirm] = useState(null);
  const [ladderRejectOpen, setLadderRejectOpen] = useState(false);
  const [ladderRejectComment, setLadderRejectComment] = useState("");
  const [ladderActionBusy, setLadderActionBusy] = useState(false);
  const [needsHrRenotify, setNeedsHrRenotify] = useState(false);
  const pendingLadderActionRef = useRef(null);
  const pendingLadderCommentRef = useRef(null);
  const ladderWeekRef = useRef(null);
  const ladderFetchScopeRef = useRef({ store: "", weekStartISO: "", unifiedLocationId: null });
  const canBuild = canBuildSchedule(user);
  const canFoxlinePush = canPushFoxline(user);
  /** T1–T3 / HR / admin: confirm instead of hard-block on contracted days / people-cost / hours; Gantt gap-fill + wider presets. */
  const adminScheduleOverride = ["admin", "store_manager", "brand_manager", "ops_manager", "hr", "manager"].includes(
    String(user?.role || "").trim().toLowerCase(),
  );
  const canMarkRestRequested = ["store_manager", "admin", "brand_manager", "ops_manager"].includes(user?.role);
  const ladderStatus = ladderWeek?.status || "draft";
  const softoneLadderReady = ladderAllowsSoftonePush(ladderStatus);
  // Always derive from the current session user — cached week.can_edit goes stale after
  // start/stop impersonation until the next approval refetch.
  const ladderAllowsEdit = canEditLadderWeek(user, ladderStatus);

  function applyLadderWeek(week, options = {}) {
    if (!week || typeof week !== "object") return;
    const force = Boolean(options.force);
    const incomingUpdated = String(week.updated_at || "");
    const currentUpdated = String(ladderWeekRef.current?.updated_at || "");
    // Ignore stale approval GETs that finish after a newer ladder POST/apply.
    if (!force && incomingUpdated && currentUpdated && incomingUpdated < currentUpdated) {
      return;
    }
    const status = week.status || "draft";
    const next = {
      ...week,
      can_edit: canEditLadderWeek(user, status),
    };
    ladderWeekRef.current = next;
    setLadderWeek(next);
    if (status !== "pending_hr" && status !== "post_pending_hr") {
      setNeedsHrRenotify(false);
    }
  }

  function refreshLadderWeek() {
    const { store: scopeStore, weekStartISO: scopeWeek, unifiedLocationId: scopeLoc } =
      ladderFetchScopeRef.current;
    if (!scopeStore || scopeStore === "All Stores" || !scopeWeek) {
      return Promise.resolve(null);
    }
    const scope = locationScopeQuery(scopeLoc);
    const qs = [
      scope,
      `week_start=${encodeURIComponent(scopeWeek)}`,
      `store_id=${encodeURIComponent(scopeStore)}`,
    ]
      .filter(Boolean)
      .join("&");
    return fetch(`${API_BASE}/schedule/weeks/approval?${qs}`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (body && typeof body === "object") {
          applyLadderWeek(body, { force: true });
        }
        return body;
      })
      .catch(() => null);
  }

  function applyLadderActionResult(action, weekFromServer) {
    const prev = ladderWeekRef.current || {};
    const prevStatus = prev.status || ladderStatus || "draft";
    if (weekFromServer && typeof weekFromServer === "object") {
      applyLadderWeek(weekFromServer, { force: true });
    } else {
      // Optimistic chrome update — keep prior updated_at so the follow-up GET wins.
      const nextStatus = ladderStatusAfterAction(action, prevStatus);
      applyLadderWeek(
        {
          ...prev,
          status: nextStatus,
          last_notify_kind: String(action || ""),
        },
        { force: true },
      );
    }
    setLadderRefreshKey((k) => k + 1);
    // Confirm from server so status pill / edit lock / SoftOne gate match DB.
    refreshLadderWeek();
  }

  const [store, setStore] = useState(() => {
    const allowed = allowedStoresForUser(user);
    if (Array.isArray(allowed) && allowed.length === 1) return allowed[0];
    if (Array.isArray(allowed) && allowed.length > 1) {
      if (initialBuilderQuery.store && allowed.includes(initialBuilderQuery.store)) {
        return initialBuilderQuery.store;
      }
      return allowed[0];
    }
    if (isEmployeeRole(user) && user?.store && user.store !== "All Stores") return user.store;
    const shared = resolveSharedStoreName({
      search: typeof window !== "undefined" ? window.location.search : "",
      storeRecords,
      storeNames,
    });
    if (shared) return shared;
    if (initialBuilderQuery.store) return initialBuilderQuery.store;
    return user.store === "All Stores" || ["admin", "brand_manager", "ops_manager", "hr"].includes(user?.role)
      ? defaultPhysicalStore
      : user.store;
  });
  const builderPhysicalStores = useMemo(() => {
    const allowed = allowedStoresForUser(user);
    if (Array.isArray(allowed) && allowed.length > 0) return allowed;
    return storeNames;
  }, [user?.role, user?.store, user?.stores, storeNames]);

  const appliedSharedStore = useRef(false);
  useEffect(() => {
    if (!storesLoaded) return;
    const allowed = allowedStoresForUser(user);
    if (Array.isArray(allowed) && allowed.length === 1) {
      const resolved = storeRecordForLabel(allowed[0], storeRecords);
      setStore(resolved?.location_name ? String(resolved.location_name).trim() : allowed[0]);
      return;
    }
    if (Array.isArray(allowed) && allowed.length > 1) {
      setStore((prev) => (allowed.includes(prev) ? prev : allowed[0]));
      return;
    }
    if (storeNames.length === 1) {
      setStore(storeNames[0]);
      return;
    }
    if (appliedSharedStore.current || !storeNames.length) return;
    appliedSharedStore.current = true;
    const sharedName = resolveSharedStoreName({
      search: typeof window !== "undefined" ? window.location.search : "",
      storeRecords,
      storeNames,
    });
    const next = sharedName || defaultBuilderStore(storeNames, storeRecords);
    if (next) setStore(next);
  }, [storesLoaded, user?.role, user?.store, storeNames, storeRecords]);

  const [weekOffset, setWeekOffset] = useState(() => {
    const fromUrl = weekOffsetForMondayISO(initialBuilderQuery.week);
    if (fromUrl != null) return fromUrl;
    return getScheduleBuilderInitialWeekOffset(SCHEDULE_GO_LIVE_WEEK);
  });
  const currentMonday = getMonday(weekOffset);
  function toLocalISO(d) {
    const yr  = d.getFullYear();
    const mo  = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${yr}-${mo}-${day}`;
  }
  const weekStartISO = toLocalISO(currentMonday);
  const builderView = timelineActive ? "timeline" : vsOptimalActive ? "optimal" : "schedule";
  const wroteBuilderUrl = useRef(false);
  useEffect(() => {
    if (!storesLoaded || !store || store === "All Stores") return undefined;
    const locationId = locationIdForStore(store, storeRecords);
    if (locationId == null) return undefined;
    const search = searchForBuilder({ locationId, week: weekStartISO, view: builderView });
    const next = `/builder${search}`;
    const current = `${window.location.pathname}${window.location.search}`;
    if (current === next) {
      wroteBuilderUrl.current = true;
      noteBuilderHref(next);
      document.title = historyTitle("/builder", search);
      return undefined;
    }
    rememberHistoryEntry("/builder", search, { push: wroteBuilderUrl.current });
    noteBuilderHref(next);
    wroteBuilderUrl.current = true;
    return undefined;
  }, [storesLoaded, store, storeRecords, weekStartISO, builderView]);

  useEffect(() => {
    function onPop() {
      if (pageFromPathname(window.location.pathname) !== "builder") return;
      const query = builderQueryFromSearch(window.location.search);
      wroteBuilderUrl.current = true;
      const name = query.locationId != null
        ? storeNameForLocationId(query.locationId, storeRecords)
        : query.store;
      if (name) setStore(name);
      const offset = weekOffsetForMondayISO(query.week);
      if (offset != null) setWeekOffset(offset);
      setTimelineActive(BUILDER_TIMELINE_ENABLED && query.view === "timeline");
      setVsOptimalActive(query.view === "optimal");
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [storeRecords]);

  const currentMondayISO = toLocalISO(getMonday(0));
  const isPastWeek = weekStartISO < currentMondayISO;
  const gridEditable = canBuild && ladderAllowsEdit && !isPastWeek;

  const notifyLadderEditLocked = useCallback(() => {
    const hint = ladderLockedContactHint(user, ladderStatus, lang);
    if (!hint) return false;
    setToast({ message: hint, durationMs: 4500 });
    return true;
  }, [user, ladderStatus, lang]);

  useEffect(() => {
    if (gridEditable) return;
    setActiveCell(null);
    setMobileActiveEmp(null);
    setDayHeaderMenuDate(null);
    setScheduleCellContextMenu(null);
    setPasteReplaceConfirm(null);
  }, [gridEditable]);

  useEffect(() => {
    setDayColumnValidationStatus({});
    setServerViolations([]);
    setServerValidationModalOpen(false);
    setServerOverrideReasons({});
    setExpandedScheduleAlertDays({});
    setDraftSavedAt(null);
  }, [store, weekStartISO, weekOffset]);

  const physicalStore = storeSupportsShiftIq(store, storeRecords) ? store : "";
  const shiftIqStore = SHIFTIQ_ENABLED ? physicalStore : "";
  const storeRecord = storeRecordForLabel(store, storeRecords);
  const unifiedLocationId = storeRecord?.unified_location_id ?? null;
  const unifiedBrandId = storeRecord?.brand_id ?? null;
  const storeQueryReady = storesLoaded && Boolean(store && store !== "All Stores");
  const fetchScopeKey = storeQueryReady && unifiedLocationId != null
    ? `${store}\n${weekStartISO}\n${unifiedLocationId}\n${unifiedBrandId ?? ""}`
    : "";
  const settledFetchKey = useSettledValue(fetchScopeKey, 100);
  const settledFetch = settledFetchKey
    ? {
        store: settledFetchKey.split("\n")[0],
        weekStartISO: settledFetchKey.split("\n")[1],
        unifiedLocationId: Number(settledFetchKey.split("\n")[2]),
        unifiedBrandId: settledFetchKey.split("\n")[3] === "" ? null : Number(settledFetchKey.split("\n")[3]),
      }
    : null;
  const fetchStore = settledFetch?.store || "";
  const fetchWeek = settledFetch?.weekStartISO || "";
  const fetchLocationId = settledFetch?.unifiedLocationId ?? null;
  const fetchBrandId = settledFetch?.unifiedBrandId ?? null;
  ladderFetchScopeRef.current = {
    store: store || "",
    weekStartISO: weekStartISO || "",
    unifiedLocationId: fetchLocationId,
  };
  useEffect(() => {
    if (!["store_manager", "admin", "brand_manager"].includes(user.role) || !fetchWeek || fetchLocationId == null) {
      setDayOffNotices([]);
      return undefined;
    }
    const controller = new AbortController();
    const params = new URLSearchParams();
    params.set("week_start", fetchWeek);
    params.set("unified_location_id", String(fetchLocationId));
    if (fetchStore) params.set("store_id", fetchStore);
    fetch(`${API_BASE}/schedule/day-off-offers/summary?${params}`, {
      headers: getAuthHeaders(),
      signal: controller.signal,
    })
      .then((res) => (res.ok ? res.json() : { notices: [] }))
      .then((body) => {
        if (!controller.signal.aborted) setDayOffNotices(body?.notices || []);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setDayOffNotices([]);
      });
    return () => controller.abort();
  }, [user.role, fetchWeek, fetchLocationId, fetchStore, dayOffNoticeTick]);
  const { demandIntensity, hourlyTargets, weeklyTargets, weeklyTargetsGeneratedAt, shiftiqLoadError, loading: shiftiqLoading } = useShiftIQ(settledFetch && shiftIqStore ? fetchStore : "", fetchWeek, fetchLocationId);
  const { data: headcountPlanningData } = useHeadcountPlanning(settledFetch && shiftIqStore ? fetchStore : "", fetchLocationId, fetchBrandId);
  const { staff: liveStaff, loading: staffLoading, error: staffError } = useStaff(fetchStore, { unifiedLocationId: fetchLocationId });
  const storeUnresolved = storesLoaded && Boolean(store && store !== "All Stores") && unifiedLocationId == null;
  const [scheduleByWeek, setScheduleByWeek] = useState(() => {
    return {};  // still empty — seed is applied in useEffect below
  });
  const [activeCell, setActiveCell] = useState(null);
  const [, setSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [weekSavedOnce, setWeekSavedOnce] = useState(false);
  const [, setHrNotifiedThisSession] = useState(false);
  const [postSaveAckOpen, setPostSaveAckOpen] = useState(false);
  const [postSaveAckItems, setPostSaveAckItems] = useState([]);
  const [saveState, setSaveState] = useState("idle");
  const [publishState, setPublishState] = useState("idle");
  const [validationIntent, setValidationIntent] = useState("save");
  const [, setDraftSavedAt] = useState(null);
  const [persistedCellStatusByWeek, setPersistedCellStatusByWeek] = useState({});
  const [scheduleConflictCellsByWeek, setScheduleConflictCellsByWeek] = useState({});
  const markScheduleUnsaved = useCallback(() => {
    hasUnsavedChangesRef.current = true;
    setHasUnsavedChanges(true);
  }, []);
  const markScheduleSaved = useCallback(() => {
    hasUnsavedChangesRef.current = false;
    setHasUnsavedChanges(false);
    setScheduleConflictCellsByWeek((prev) => ({ ...prev, [weekOffset]: {} }));
  }, [weekOffset]);
  const syncUnsavedAgainstBaseline = useCallback(
    (weekSched) => {
      const cacheKey = `${store}:${weekStartISO}`;
      const baseline = lastPersistedScheduleRef.current[cacheKey] || {};
      const dirty = isScheduleWeekDirtyAgainstBaseline(weekSched, baseline);
      hasUnsavedChangesRef.current = dirty;
      setHasUnsavedChanges(dirty);
      if (!dirty) setSaved(true);
      else setSaved(false);
    },
    [store, weekStartISO],
  );
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [expandedScheduleAlertDays, setExpandedScheduleAlertDays] = useState({});
  const [warningsExpanded, setWarningsExpanded] = useState(false);
  const [targetsExpanded, setTargetsExpanded] = useState(false);
  const [coverageExpanded, setCoverageExpanded] = useState(false);
  const [weekActionsMenuOpen, setWeekActionsMenuOpen] = useState(false);
  const [resetDayConfirmOpen, setResetDayConfirmOpen] = useState(false);
  const [resetDayTarget, setResetDayTarget] = useState(null);
  const [resetWeekModalOpen, setResetWeekModalOpen] = useState(false);
  const [resetWeekConfirmStep2, setResetWeekConfirmStep2] = useState(false);
  const [resetWeekStoreInput, setResetWeekStoreInput] = useState("");
  const [resetAutoFillModalOpen, setResetAutoFillModalOpen] = useState(false);
  const [resetAutoFillConfirmStep2, setResetAutoFillConfirmStep2] = useState(false);
  const [manualResetLoading, setManualResetLoading] = useState(false);
  const [dayHeaderMenuDate, setDayHeaderMenuDate] = useState(null);
  const [assignmentLimitWarning, setAssignmentLimitWarning] = useState(null);
  const [copiedShift, setCopiedShift] = useState(null);
  const [scheduleCellContextMenu, setScheduleCellContextMenu] = useState(null);
  const [slotHistoryTarget, setSlotHistoryTarget] = useState(null);
  const [pasteReplaceConfirm, setPasteReplaceConfirm] = useState(null);
  const [hoveredPasteCell, setHoveredPasteCell] = useState(null);
  const [scheduleCellFocus, setScheduleCellFocus] = useState(null);
  const [toast, setToast] = useState(null);
  const [draftSavedToast, setDraftSavedToast] = useState(null);
  const [scheduleLegendOpen, setScheduleLegendOpen] = useState(false);
  const [loanModalOpen, setLoanModalOpen] = useState(false);
  const [loanModalContext, setLoanModalContext] = useState(null);
  const [loanRequestModalOpen, setLoanRequestModalOpen] = useState(false);
  const [loanRequestContext, setLoanRequestContext] = useState(null);
  const [loanRequestSubmitting, setLoanRequestSubmitting] = useState(false);
  const [loanSubmitting, setLoanSubmitting] = useState(false);
  const [weekLoans, setWeekLoans] = useState([]);
  const [weekLeaveRequests, setWeekLeaveRequests] = useState([]);
  /** Optimistic manager-marked rest days: "employee_code|YYYY-MM-DD" — survives ShiftPicker remount. */
  const [pendingLocalRestRequests, setPendingLocalRestRequests] = useState(() => new Set());
  const [markRestRequestedLoading, setMarkRestRequestedLoading] = useState(false);
  const [removeRestRequestedLoading, setRemoveRestRequestedLoading] = useState(false);
  const [scheduleGuestEmployees, setScheduleGuestEmployees] = useState([]);
  const [scheduleLoanShiftMeta, setScheduleLoanShiftMeta] = useState({});
  /** Admin: confirm before applying a grid shift that breaches people-cost target or opening hours. */
  const [adminCapShiftConfirm, setAdminCapShiftConfirm] = useState(null);
  const [customShifts8, setCustomShifts8] = useState([]);
  const [customShifts6, setCustomShifts6] = useState([]);
  const [mobileDay, setMobileDay] = useState(0);
  const [mobileActiveEmp, setMobileActiveEmp] = useState(null);
  const [focusViewportWidth, setFocusViewportWidth] = useState(
    () => (typeof window !== "undefined" ? window.innerWidth : 1440)
  );
  const [sidebarRoleFilter, setSidebarRoleFilter] = useState("all");
  const [isWideFocusLayout, setIsWideFocusLayout] = useState(
    () => (typeof window !== "undefined" ? window.innerWidth > 1200 : true)
  );
  const [sharedGanttData, setSharedGanttData] = useState(null);
  const [sharedGanttLoading, setSharedGanttLoading] = useState(false);
  const [sharedGanttError, setSharedGanttError] = useState("");
  const [sharedGanttRefreshTick, setSharedGanttRefreshTick] = useState(0);
  const [loadingAction, setLoadingAction] = useState(null);
  const preGanttScrollY = useRef(0);
  const weekActionsRef = useRef(null);
  const lastPersistedScheduleRef = useRef({});
  const scheduleFetchAbortRef = useRef(null);
  const loansFetchAbortRef = useRef(null);
  const leaveFetchAbortRef = useRef(null);
  const hasUnsavedChangesRef = useRef(false);
  const [changesPanelOpen, setChangesPanelOpen] = useState(false);
  const [hrToast, setHrToast] = useState(null);
  const [peopleCostRefreshTick, setPeopleCostRefreshTick] = useState(0);
  const { data: peopleCost, loading: pcLoading } = usePeopleCost(settledFetch && physicalStore ? fetchStore : "", fetchWeek, peopleCostRefreshTick, fetchLocationId, fetchBrandId);
  const [saveOverrideOpen, setSaveOverrideOpen] = useState(false);
  const [saveOverrideAck, setSaveOverrideAck] = useState(false);
  const [serverValidationModalOpen, setServerValidationModalOpen] = useState(false);
  const [serverViolations, setServerViolations] = useState([]);
  const [serverOverrideReasons, setServerOverrideReasons] = useState({});
  const [serverOverrideAuthors, setServerOverrideAuthors] = useState({});
  const seededOverrideReasonsRef = useRef({});
  const seededOverrideAuthorsRef = useRef({});
  const [dayColumnValidationStatus, setDayColumnValidationStatus] = useState({});
  const assignmentLimitWarningTimeoutRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const proactiveValidationTimerRef = useRef(null);
  const proactiveValidationRequestRef = useRef(0);
  const scheduleProactiveValidationRef = useRef(() => {});
  const [isScrolled, setIsScrolled] = useState(false);
  const [unsavedNavConfirm, setUnsavedNavConfirm] = useState(null);
  const storeSwitcherRef = useRef(null);
  const storeSwitcherOpenRef = useRef(false);
  const closeStoreSwitcher = useCallback(() => {
    storeSwitcherRef.current?.close();
  }, []);

  useEffect(() => {
    if (!activeCell) setAdminCapShiftConfirm(null);
  }, [activeCell]);

  const canManualResetSchedule = user.role === "admin" || user.role === "brand_manager" || user.role === "store_manager" || user.role === "ops_manager";

  const defaultHours = STORE_DEFAULT_HOURS[store] || {
    Mon:"10:00-22:00", Tue:"10:00-22:00",
    Wed:"10:00-22:00", Thu:"10:00-22:00",
    Fri:"10:00-22:00", Sat:"10:00-22:00",
    Sun:"11:00-20:00"
  };
  const [openingHours, setOpeningHours] = useState(defaultHours);
  const [staffOpenOffsetMinutes, setStaffOpenOffsetMinutes] = useState(60);
  const [staffCloseOffsetMinutes, setStaffCloseOffsetMinutes] = useState(60);

  if (!canBuild) return (
    <div className="empty-state"><Icon name="pencil" size={40} /><p>{lang === "gr" ? "Δεν έχετε πρόσβαση" : "No access to schedule builder"}</p></div>
  );

  useEffect(() => {
    markScheduleSaved();
    setWeekSavedOnce(false);
    setHrNotifiedThisSession(false);
    setPostSaveAckOpen(false);
    setSaved(false);
    setResetWeekModalOpen(false);
    setResetWeekConfirmStep2(false);
    setResetWeekStoreInput("");
    setResetDayConfirmOpen(false);
    setResetDayTarget(null);
    setSaveState("idle");
    setScheduleGuestEmployees([]);
    setScheduleLoanShiftMeta({});
    setScheduleConflictCellsByWeek({});
    setUnsavedNavConfirm(null);
  }, [store, weekStartISO]);

  useEffect(() => {
    if (!fetchLocationId || !fetchWeek) return undefined;
    const controller = new AbortController();
    const storeQs = locationScopeQuery(fetchLocationId);
    fetch(`${SHIFTIQ_API}/opening-hours?${storeQs}`, { headers: getAuthHeaders(), signal: controller.signal })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (controller.signal.aborted) return;
        setOpeningHours(data?.hours ?? STORE_DEFAULT_HOURS[store] ?? {});
        setStaffOpenOffsetMinutes(Number(data?.staff_open_offset_minutes) || 60);
        setStaffCloseOffsetMinutes(Number(data?.staff_close_offset_minutes) || 60);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setOpeningHours(STORE_DEFAULT_HOURS[store] ?? {});
        setStaffOpenOffsetMinutes(60);
        setStaffCloseOffsetMinutes(60);
      });
    return () => controller.abort();
  }, [fetchLocationId, store]);

  const isMobile = useIsMobile();
  const builderTopWide = !isMobile && focusViewportWidth > 1100;
  const scheduleGridWideBleed = !isMobile && focusViewportWidth > 1200;
  /** Matches `.page-content` horizontal padding on desktop (see CSS). */
  const builderPagePadX = 28;
  const dayNames   = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const dayNamesGr = ["Δευ","Τρί","Τετ","Πέμ","Παρ","Σάβ","Κυρ"];

  // ── Week dates ──────────────────────────────────────────────────────────────
  function getMonday(offset) {
    const base = new Date();
    const day = base.getDay();
    const monday = new Date(base);
    monday.setDate(base.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
    monday.setHours(0,0,0,0);
    return monday;
  }
  function getOffsetForDate(targetMonday) {
    const base = new Date();
    const day = base.getDay();
    const currentMonday = new Date(base);
    currentMonday.setDate(base.getDate() - (day === 0 ? 6 : day - 1));
    currentMonday.setHours(0,0,0,0);
    const target = new Date(targetMonday);
    const diffMs = target - currentMonday;
    return Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));
  }

  useEffect(() => {
    if (weekStartISO < SCHEDULE_GO_LIVE_WEEK) {
      setWeekOffset(getOffsetForDate(parseISODateLocal(SCHEDULE_GO_LIVE_WEEK)));
    }
  }, [weekStartISO]);

  const seedOffset = getOffsetForDate("2026-04-13");
  const weekDates = useMemo(() => {
    const monday = getMonday(weekOffset);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  }, [weekOffset]);
  const weekDayDates = useMemo(() => weekDates.map((d) => toLocalISO(d)), [weekDates]);
  function fmtDate(d) { return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }); }
  function dayKey(d, i) { return dayNames[i] + " " + fmtDate(d); }
  function dayKeyToDateISO(dk) {
    const idx = weekDates.findIndex((d, i) => dayKey(d, i) === dk);
    if (idx < 0) return null;
    return toLocalISO(weekDates[idx]);
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────
  function timeToMins(t) {
    if (!t || typeof t !== "string") return null;
    const parts = t.split(":");
    if (parts.length < 2) return null;
    const h = parseInt(parts[0]), m = parseInt(parts[1]);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  }
  function parseShiftMins(val) {
    if (!val || SPECIAL_STATUSES.find(s => val === s.value)) return null;
    if (val === "OFF") return null;
    const parts = val.split("-");
    if (parts.length !== 2) return null;
    const start = timeToMins(parts[0]);
    let end = timeToMins(parts[1]);
    if (start === null || end === null) return null;
    if (end < start) end += 24 * 60; // past midnight
    return { start, end };
  }

  // ── Employees ───────────────────────────────────────────────────────────────
  const employees = liveStaff ?? [];
  const storeEmployees = useMemo(() => {
    const home = employees
      .filter((u) => employeeMatchesManagerStore(u, store, storeRecords))
      .map((u) => ({ ...u, group: schedulingGroupFor(u) }));
    const homeKeys = new Set(home.map((e) => rosterScheduleKey(e)));
    const guests = (scheduleGuestEmployees || [])
      .filter((g) => !homeKeys.has(rosterScheduleKey(g)))
      .map((g) => ({ ...g, group: schedulingGroupFor(g) }));
    return [...home, ...guests].map((u) => ({
      ...u,
      group: u.group || schedulingGroupFor(u),
    }));
  }, [employees, store, scheduleGuestEmployees, storeRecords]);
  const NAME_ALIASES = {
    // Kitchen — latin name mismatches
    "Md Khirul Alam":     "ALAM",
    "Todorka Hristidu":   "ΔΩΡΑ ΧΡΙΣΤΙΔΟΥ",
    "Md Bodrul Miah":     "BODRUL MIAH MD",
    "Jalloh Hassan":      "Jalloh Hassan",
    "Georgiana Stanciu":  "Georgiana Stanciu",
    "Παναγιωτα Καλογηρου": "ΠΑΝΑΓΙΩΤΑ ΚΑΛΟΓΗΡΟΥ",
    "Γεωργιος Χατζης":    "ΧΑΤΖΗΣ ΓΕΩΡΓΙΟΣ",

    // Service — full legal name vs rota nickname
    "Αντωνια Ιωαννα Ονιεκε Λακουμεντα": "ΤΖΑΝΙΝΑ ΛΑΚΟΥΜΕΝΤΑ",
    "Μαρια Μελινα Αυγη Χατζηχρηστου Κελη": "ΧΑΤΖΗΧΡΗΣΤΟΥ ΜΕΛΙΝΑ",

    // PG Omirou aliases (add when confirmed)
    // PG Tsamadou aliases (add when confirmed)
  };
  function buildSeedFromRota(store, employees) {
    const storeRota = SEED_SCHEDULE[store];
    if (!storeRota) return {};

    function tokenize(name) {
      return name
        .toUpperCase()
        .trim()
        .split(/\s+/)
        .map(t => t.replace(/[^A-Z0-9Α-ΩΑ-Ω]/gi, ""))
        .filter(t => t.length > 1);
    }

    function matchScore(rotaName, empName) {
      const rotaTokens = tokenize(rotaName);
      const empTokens  = tokenize(empName);
      let matches = 0;

      // Last token of each (likely surname) — weight x2
      const rotaLast = rotaTokens[rotaTokens.length - 1];
      const empLast  = empTokens[empTokens.length - 1];
      if (rotaLast && empLast && (
        rotaLast === empLast ||
        rotaLast.startsWith(empLast) ||
        empLast.startsWith(rotaLast)
      )) {
        matches += 2;
      }

      for (const rt of rotaTokens) {
        for (const et of empTokens) {
          if (rt === et || rt.startsWith(et) || et.startsWith(rt)) {
            matches++;
          }
        }
      }
      return matches;
    }

    const result = {};
    employees.forEach(emp => {
      const empName = (emp.name || "").trim();
      if (!empName) return;

      // Check alias map first
      const aliasKey = Object.keys(NAME_ALIASES).find(k =>
        k.toUpperCase() === empName.toUpperCase()
      );
      if (aliasKey) {
        const rotaKey = NAME_ALIASES[aliasKey];
        if (storeRota[rotaKey]) {
          result[rosterScheduleKey(emp)] = storeRota[rotaKey];
          return; // skip fuzzy matching for this employee
        }
      }

      // Fall through to fuzzy matching
      let bestKey = null;
      let bestScore = 0;

      Object.keys(storeRota).forEach(rotaKey => {
        const score = matchScore(rotaKey, empName);
        if (score > bestScore) {
          bestScore = score;
          bestKey = rotaKey;
        }
      });

      // Only accept match if at least one token matched
      if (bestKey && bestScore >= 1) {
        result[rosterScheduleKey(emp)] = storeRota[bestKey];
      }
    });

    return result;
  }
  useEffect(() => {
    // Only seed from live Foxline staff — never from DEMO_USERS
    if (!liveStaff || liveStaff.length === 0) return;
    const liveStoreEmps = liveStaff.filter((e) => employeeMatchesManagerStore(e, store, storeRecords));
    if (liveStoreEmps.length === 0) return;

    setScheduleByWeek(prev => {
      // Only skip if already seeded from LIVE data
      // Check by seeing if any seeded employee id exists in liveStaff
      const existing = prev[seedOffset] || {};
      const liveIds = new Set(liveStoreEmps.map(e => String(e.id)));
      const alreadySeededFromLive = Object.keys(existing).some(id =>
        liveIds.has(String(id))
      );
      if (alreadySeededFromLive) return prev;

      const seeded = buildSeedFromRota(store, liveStoreEmps);
      if (Object.keys(seeded).length === 0) return prev;
      return { ...prev, [seedOffset]: seeded };
    });
  }, [store, liveStaff, seedOffset, storeRecords]);

  const fetchWeeklyScheduleGrid = useCallback(() => {
    if (!fetchLocationId || !fetchWeek) return;
    if (!liveStaff || liveStaff.length === 0) return;
    scheduleFetchAbortRef.current?.abort();
    const controller = new AbortController();
    scheduleFetchAbortRef.current = controller;
    setScheduleLoading(true);
    fetch(`${SHIFTIQ_API}/schedule?${locationScopeQuery(fetchLocationId)}&week_start=${fetchWeek}`, { headers: getAuthHeaders(), signal: controller.signal })
      .then(r => r.ok ? r.json() : [])
      .then(rows => {
        if (controller.signal.aborted) return;
        // Convert API rows to scheduleByWeek format (empty array = clear week in UI)
        // rows: [{ employee_name, rota_date, shift_start,
        //          shift_end, is_off, status }]
        const liveStoreEmps = liveStaff.filter((e) => employeeMatchesManagerStore(e, store, storeRecords));
        const weekSched = {};
        const weekStatus = {};
        const weekLoanMeta = {};
        const guests = [];
        const guestCodes = new Set();

        (rows || []).forEach(row => {
          // Match persisted rota by AFM (canonical or alias). Never by display name.
          const rowCode = String(row.employee_code || "").trim();
          let emp = findStaffForRotaRow(liveStoreEmps, row);
          if (!emp && rowCode) {
            // SoftOne experiment AFM is home staff only in SHIFTIQ_TESTING — never a guest row.
            if (isOpsTestEmployeeCode(rowCode)) return;
            const globalEmp = foxlineStaffRecordForCode(
              [...(allUsers || []), ...(liveStaff || [])],
              rowCode
            );
            const guestKey = globalEmp
              ? `id:${globalEmp.id}`
              : `code:${rowCode}`;
            if (!guestCodes.has(guestKey)) {
              guestCodes.add(guestKey);
              guests.push(guestRecordForRotaCode(globalEmp, rowCode, row));
            }
            emp = guests.find((g) => staffRecordMatchesCode(g, rowCode));
          }
          if (!emp) return;

          // Build day key from rota_date
          const d = new Date(row.rota_date + "T12:00:00");
          const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
          const dn = dayNames[d.getDay()];
          const dd = d.toLocaleDateString("en-GB",
            { day:"2-digit", month:"short" });
          const dk = dn + " " + dd;
          const empKey = rosterScheduleKey(emp);

          // Build shift value
          let shiftVal = "";
          if (row.status === "leave") shiftVal = "ΑΔΕΙΑ";
          else if (row.status === "sick") shiftVal = "ΑΝΑΡΡΩΤΙΚΗ";
          else if (row.status === "other_store") shiftVal = "OTHER_STORE";
          else if (row.status === "no_work") shiftVal = "NO_WORK";
          else if (row.is_off) shiftVal = "OFF";
          else if (row.shift_start && row.shift_end)
            shiftVal = row.shift_start.slice(0,5) + "-" +
                       row.shift_end.slice(0,5);

          if (!weekSched[empKey]) weekSched[empKey] = {};
          weekSched[empKey][dk] = shiftVal;
          if (!weekStatus[empKey]) weekStatus[empKey] = {};
          const rowStatus = String(row.status || "draft").toLowerCase();
          weekStatus[empKey][dk] = rowStatus;
          if (row.status === "other_store" && row.shift_type) {
            if (!weekLoanMeta[empKey]) weekLoanMeta[empKey] = {};
            weekLoanMeta[empKey][dk] = String(row.shift_type);
          }
        });

        const cacheKey = `${store}:${weekStartISO}`;
        const oldBaseline = lastPersistedScheduleRef.current[cacheKey]
          ? JSON.parse(JSON.stringify(lastPersistedScheduleRef.current[cacheKey]))
          : {};

        if (Object.keys(weekSched).length > 0) {
          lastPersistedScheduleRef.current[cacheKey] = JSON.parse(JSON.stringify(weekSched));
        } else {
          delete lastPersistedScheduleRef.current[cacheKey];
        }

        let mergeResult = null;
        const shouldMergeDirtyLocal = hasUnsavedChangesRef.current;
        setScheduleByWeek((prev) => {
          if (!shouldMergeDirtyLocal) {
            return { ...prev, [weekOffset]: weekSched };
          }
          const oldLocal = prev[weekOffset] || {};
          mergeResult = mergeFetchedSchedulePreservingDirtyLocal(weekSched, oldLocal, oldBaseline);
          return { ...prev, [weekOffset]: mergeResult.merged };
        });

        const dirtyKeys = mergeResult?.dirtyKeys || new Set();
        const mergeConflicts = mergeResult?.conflicts || [];
        if (shouldMergeDirtyLocal && mergeConflicts.length > 0) {
          const nameByEmpId = new Map();
          liveStoreEmps.forEach((e) => nameByEmpId.set(rosterScheduleKey(e), e.name || rosterScheduleKey(e)));
          guests.forEach((g) => nameByEmpId.set(rosterScheduleKey(g), g.name || rosterScheduleKey(g)));
          const conflictToast = formatScheduleMergeConflictToast(mergeConflicts, nameByEmpId, lang);
          if (conflictToast) {
            setToast({ message: conflictToast, durationMs: SCHEDULE_CONFLICT_TOAST_MS });
          }
          setScheduleConflictCellsByWeek((prev) => ({
            ...prev,
            [weekOffset]: mergeScheduleConflictCellMaps(prev[weekOffset], mergeConflicts),
          }));
        } else if (!shouldMergeDirtyLocal) {
          setScheduleConflictCellsByWeek((prev) => ({ ...prev, [weekOffset]: {} }));
        }
        setScheduleGuestEmployees(guests);
        setScheduleLoanShiftMeta((prev) => {
          const mergedMeta = shouldMergeDirtyLocal
            ? mergeAuxMapForDirtyCells(weekLoanMeta, prev[weekOffset] || {}, dirtyKeys, true)
            : weekLoanMeta;
          return { ...prev, [weekOffset]: mergedMeta };
        });
        setPersistedCellStatusByWeek((prev) => {
          const mergedStatus = shouldMergeDirtyLocal
            ? mergeAuxMapForDirtyCells(weekStatus, prev[weekOffset] || {}, dirtyKeys, false)
            : weekStatus;
          return { ...prev, [weekOffset]: mergedStatus };
        });
        if ((rows || []).length > 0) {
          setWeekSavedOnce(true);
        }
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
      })
      .finally(() => {
        if (!controller.signal.aborted) setScheduleLoading(false);
      });
  }, [fetchLocationId, fetchWeek, weekOffset, liveStaff, allUsers, lang, storeRecords]);

  const clearScheduleCellConflict = useCallback((empId, dk) => {
    setScheduleConflictCellsByWeek((prev) => {
      const week = prev[weekOffset];
      if (!week?.[empId]?.[dk]) return prev;
      const nextWeek = { ...week };
      const nextEmp = { ...nextWeek[empId] };
      delete nextEmp[dk];
      if (Object.keys(nextEmp).length === 0) delete nextWeek[empId];
      else nextWeek[empId] = nextEmp;
      return { ...prev, [weekOffset]: nextWeek };
    });
  }, [weekOffset]);

  const fetchWeekLoans = useCallback(() => {
    if (!fetchLocationId || !fetchWeek) return;
    loansFetchAbortRef.current?.abort();
    const controller = new AbortController();
    loansFetchAbortRef.current = controller;
    fetch(
      `${API_BASE}/staff/loans?${locationScopeQuery(fetchLocationId)}&week_start=${encodeURIComponent(fetchWeek)}`,
      { headers: getAuthHeaders(), signal: controller.signal }
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (!controller.signal.aborted) setWeekLoans(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setWeekLoans([]);
      });
  }, [fetchLocationId, fetchWeek]);

  const fetchWeekLeaveRequests = useCallback(() => {
    if (!fetchLocationId || !fetchWeek) return;
    leaveFetchAbortRef.current?.abort();
    const controller = new AbortController();
    leaveFetchAbortRef.current = controller;
    fetch(
      `${SHIFTIQ_API}/schedule/leave-requests?${locationScopeQuery(fetchLocationId)}&week_start=${encodeURIComponent(fetchWeek)}`,
      { headers: getAuthHeaders(), signal: controller.signal }
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        setWeekLeaveRequests(list);
        // Drop optimistic keys once the server payload covers them.
        setPendingLocalRestRequests((prev) => {
          if (!prev || prev.size === 0) return prev;
          const next = new Set();
          prev.forEach((key) => {
            const sep = key.lastIndexOf("|");
            if (sep < 0) return;
            const idPart = key.slice(0, sep);
            const date = key.slice(sep + 1);
            const stub =
              idPart.startsWith("name:")
                ? { name: idPart.slice(5), employee_code: "" }
                : { name: "", employee_code: idPart };
            if (!hasRequestedRestDay(list, stub, date)) next.add(key);
          });
          return next.size === prev.size ? prev : next;
        });
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setWeekLeaveRequests([]);
      });
  }, [fetchLocationId, fetchWeek, store]);

  useEffect(() => {
    fetchWeeklyScheduleGrid();
    return () => scheduleFetchAbortRef.current?.abort();
  }, [fetchWeeklyScheduleGrid]);

  useEffect(() => {
    fetchWeekLoans();
    return () => loansFetchAbortRef.current?.abort();
  }, [fetchWeekLoans]);

  useEffect(() => {
    fetchWeekLeaveRequests();
    return () => leaveFetchAbortRef.current?.abort();
  }, [fetchWeekLeaveRequests]);

  useEffect(() => {
    if (!SHIFTIQ_ENABLED || !physicalStore || !fetchWeek || fetchLocationId == null) {
      setSharedGanttData(null);
      setSharedGanttLoading(false);
      setSharedGanttError("");
      return undefined;
    }
    const controller = new AbortController();
    setSharedGanttLoading(true);
    setSharedGanttError("");
    fetch(`${SHIFTIQ_API}/optimal-schedule?${locationScopeQuery(fetchLocationId)}&week_start=${fetchWeek}`, { headers: getAuthHeaders(), signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
      .then((data) => {
        if (!controller.signal.aborted) setSharedGanttData(data);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setSharedGanttData(null);
        setSharedGanttError("Failed to load optimal schedule.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setSharedGanttLoading(false);
      });
    return () => controller.abort();
  }, [physicalStore, fetchWeek, fetchLocationId, sharedGanttRefreshTick, ganttRefreshKey]);

  useEffect(() => {
    if (!sharedGanttLoading && loadingAction) {
      setLoadingAction(null);
    }
  }, [sharedGanttLoading]);

  useEffect(() => {
    const onRotaUpdated = (event) => {
      const detail = event?.detail || {};
      if (detail.store_id && detail.store_id !== store) return;
      if (detail.week_start && detail.week_start !== weekStartISO) return;
        if (!detail.skipDirty) {
        setSaved(false);
        markScheduleUnsaved();
      }
      fetchWeeklyScheduleGrid();
      fetchWeekLoans();
      fetchWeekLeaveRequests();
      setPeopleCostRefreshTick((t) => t + 1);
      if (detail.refreshGantt !== false) {
        if (currentView !== "vsOptimal" && currentView !== "gantt") {
          ganttNeedsRefreshRef.current = true;
        }
        setSharedGanttRefreshTick((t) => t + 1);
      }
    };
    window.addEventListener("rota-updated", onRotaUpdated);
    return () => window.removeEventListener("rota-updated", onRotaUpdated);
  }, [fetchWeeklyScheduleGrid, fetchWeekLoans, fetchWeekLeaveRequests, store, weekStartISO, currentView]);

  const closeResetWeekModal = useCallback(() => {
    setResetWeekModalOpen(false);
    setResetWeekConfirmStep2(false);
    setResetWeekStoreInput("");
  }, []);

  const closeResetAutoFillModal = useCallback(() => {
    setResetAutoFillModalOpen(false);
    setResetAutoFillConfirmStep2(false);
  }, []);

  const closeResetDayModal = useCallback(() => {
    setResetDayConfirmOpen(false);
    setResetDayTarget(null);
  }, []);

  const resetDayModalLabel = useMemo(() => {
    if (!resetDayTarget) return { dayName: "", dateLabel: "" };
    const d = new Date(`${resetDayTarget}T12:00:00`);
    const idx = (d.getDay() + 6) % 7;
    return {
      dayName: lang === "gr" ? dayNamesGr[idx] : dayNames[idx],
      dateLabel: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
    };
  }, [resetDayTarget, lang]);

  const resetWeekStoreNameOk =
    resetWeekStoreInput.trim().toLowerCase() === String(store || "").trim().toLowerCase();

  const openResetDayConfirmFor = (dayDate) => {
    if (!dayDate) return;
    setResetDayTarget(dayDate);
    setResetDayConfirmOpen(true);
    setDayHeaderMenuDate(null);
  };

  const runManualResetDay = async () => {
    const date = resetDayTarget;
    if (!date || !store || manualResetLoading) return;
    setManualResetLoading(true);
    setLoadingAction("Resetting day...");
    try {
      const res = await fetch(`${SHIFTIQ_API}/optimal-schedule/reset`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          store_id: store,
          week_start: weekStartISO,
          date,
          force: true,
        }),
      });
      if (!res.ok) throw new Error("failed");
      fetchWeeklyScheduleGrid();
      window.dispatchEvent(
        new CustomEvent("rota-updated", {
          detail: { store_id: store, week_start: weekStartISO, refreshGantt: true, skipDirty: true },
        })
      );
      setSharedGanttRefreshTick((t) => t + 1);
      markScheduleSaved();
      setFocusDay(null);
      closeResetDayModal();
      setToast(lang === "gr" ? "Η ημέρα επαναφέρθηκε" : "Day reset successfully");
    } catch {
      setToast(lang === "gr" ? "Η επαναφορά απέτυχε" : "Reset failed — try again");
    } finally {
      setManualResetLoading(false);
      setLoadingAction(null);
    }
  };

  const runManualResetWeek = async () => {
    if (!store || manualResetLoading || !resetWeekStoreNameOk) return;
    setManualResetLoading(true);
    try {
      const res = await fetch(`${SHIFTIQ_API}/optimal-schedule/reset`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          store_id: store,
          week_start: weekStartISO,
          force: true,
        }),
      });
      if (!res.ok) throw new Error("failed");
      fetchWeeklyScheduleGrid();
      window.dispatchEvent(
        new CustomEvent("rota-updated", {
          detail: { store_id: store, week_start: weekStartISO, refreshGantt: true, skipDirty: true },
        })
      );
      setSharedGanttRefreshTick((t) => t + 1);
      markScheduleSaved();
      closeResetWeekModal();
      setToast(lang === "gr" ? "Η εβδομάδα επαναφέρθηκε" : "Week reset successfully");
    } catch {
      setToast(lang === "gr" ? "Η επαναφορά απέτυχε" : "Reset failed — try again");
    } finally {
      setManualResetLoading(false);
    }
  };

  const runManualResetAutoFillsWeek = async () => {
    if (!store || manualResetLoading) return;
    setManualResetLoading(true);
    try {
      const res = await fetch(`${SHIFTIQ_API}/optimal-schedule/reset`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          store_id: store,
          week_start: weekStartISO,
          force: false,
        }),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      const n = Number(data?.removed ?? 0);
      fetchWeeklyScheduleGrid();
      window.dispatchEvent(
        new CustomEvent("rota-updated", {
          detail: { store_id: store, week_start: weekStartISO, refreshGantt: true, skipDirty: true },
        })
      );
      setSharedGanttRefreshTick((t) => t + 1);
      markScheduleSaved();
      closeResetAutoFillModal();
      setToast(
        n > 0
          ? lang === "gr"
            ? `Αφαιρέθηκαν ${n} αυτόματες βάρδιες`
            : `Removed ${n} auto-filled slot${n === 1 ? "" : "s"}`
          : lang === "gr"
            ? "Δεν υπάρχουν αυτόματες βάρδιες προς αφαίρεση"
            : "No auto-filled slots to remove",
      );
    } catch {
      setToast(lang === "gr" ? "Η επαναφορά απέτυχε" : "Reset failed — try again");
    } finally {
      setManualResetLoading(false);
    }
  };

  const incomingLoans = useMemo(
    () => (weekLoans || []).filter((l) => l.direction === "incoming" && l.status !== "cancelled"),
    [weekLoans]
  );

  const grouped = GROUPS.map(g => ({
    ...g,
    members: storeEmployees
      .filter(u => u.group === g.key)
      .slice()
      .sort((a, b) => Number(Boolean(b.shared_hub)) - Number(Boolean(a.shared_hub))),
  })).filter(g => g.members.length > 0);

  // Current week's schedule (auto-seed previous week on first access)
  function getWeekSched(offset) {
    if (scheduleByWeek[offset]) return scheduleByWeek[offset];
    if (offset === -1) {
      // build seed for previous week
      const prevMonday = getMonday(offset);
      const prevDates = Array.from({ length: 7 }, (_, i) => { const d = new Date(prevMonday); d.setDate(prevMonday.getDate()+i); return d; });
      return buildSeedSchedule(prevDates, dayNames, storeEmployees, fmtDate);
    }
    return {};
  }
  const schedule = getWeekSched(weekOffset);
  const [foxlinePopupKey, setFoxlinePopupKey] = useState(null);
  const foxlineStaffPool = useMemo(
    () => [...(liveStaff || []), ...(scheduleGuestEmployees || []), ...(allUsers || [])],
    [liveStaff, scheduleGuestEmployees, allUsers]
  );
  const applySoftoneInternalClears = useCallback(
    (cleared) => {
      if (!Array.isArray(cleared) || cleared.length === 0) return;
      const isoToDk = {};
      weekDates.forEach((d, i) => {
        isoToDk[toLocalISO(d)] = dayKey(d, i);
      });
      const targets = cleared
        .map((row) => ({
          dig: employeeCodeDigits(row.employee_code),
          code: String(row.employee_code || "").trim(),
          dk: isoToDk[String(row.date || "").slice(0, 10)] || "",
        }))
        .filter((t) => t.dk && (t.dig || t.code));
      if (!targets.length) return;

      const clearInWeek = (weekSched) => {
        const next = { ...(weekSched || {}) };
        let changed = false;
        for (const t of targets) {
          for (const empId of Object.keys(next)) {
            const dig = employeeCodeDigits(empId);
            if (t.dig && dig !== t.dig && empId !== t.code) continue;
            if (!t.dig && empId !== t.code) continue;
            if (!next[empId]?.[t.dk]) continue;
            next[empId] = { ...next[empId], [t.dk]: "" };
            changed = true;
          }
        }
        return changed ? next : weekSched;
      };

      setScheduleByWeek((prev) => {
        const cur = prev[weekOffset] || {};
        const nextWeek = clearInWeek(cur);
        if (nextWeek === cur) return prev;
        const cacheKey = `${store}:${weekStartISO}`;
        const baseline = clearInWeek(lastPersistedScheduleRef.current[cacheKey] || {});
        lastPersistedScheduleRef.current[cacheKey] = baseline || {};
        queueMicrotask(() => syncUnsavedAgainstBaseline(nextWeek));
        return { ...prev, [weekOffset]: nextWeek };
      });
    },
    [weekDates, weekOffset, store, weekStartISO, syncUnsavedAgainstBaseline],
  );

  const foxlineSync = useFoxlineSlotSync({
    store: fetchStore,
    weekStartISO: fetchWeek,
    weekDates,
    dayKeyFn: dayKey,
    localSchedule: schedule,
    staffPool: foxlineStaffPool,
    storeRecords,
    user,
    enabled: Boolean(fetchLocationId && fetchWeek),
    softoneWritesAllowed: softoneLadderReady,
    onWriteComplete: (result) => {
      const payload = result?.data && typeof result.data === "object" ? result.data : result;
      const week = payload?.week || result?.week || null;
      if (week && typeof week === "object" && week.status) {
        applyLadderWeek(week);
      }
      const notice = formatSoftoneWriteNotice(result, lang);
      if (notice?.message) {
        setToast({
          message: notice.message,
          durationMs: notice.tone === "danger" ? 9000 : 4000,
          tone: notice.tone === "danger" ? "danger" : "info",
        });
      }
      // SoftOne may mark Ergani final (or otherwise advance the week) — refresh ladder chrome.
      setLadderRefreshKey((k) => k + 1);
    },
    onInternalCleared: applySoftoneInternalClears,
    lang,
  });
  const erganiSync = useErganiSlots({
    store: fetchStore,
    weekStartISO: fetchWeek,
    unifiedLocationId: fetchLocationId,
    enabled: Boolean(fetchLocationId && fetchWeek),
  });
  const [erganiHistory, setErganiHistory] = useState([]);
  const foxlineOpenSlot = (() => {
    if (!foxlinePopupKey) return null;
    if (foxlineSync.slotByKey[foxlinePopupKey]) return foxlineSync.slotByKey[foxlinePopupKey];
    const sep = String(foxlinePopupKey).lastIndexOf("::");
    if (sep < 0) return null;
    return lookupSlotByKey(
      foxlineSync.slotByKey,
      foxlinePopupKey.slice(0, sep),
      foxlinePopupKey.slice(sep + 2),
    );
  })();
  useEffect(() => {
    if (!foxlineOpenSlot?.employee_code || !foxlineOpenSlot?.date) {
      setErganiHistory([]);
      return undefined;
    }
    let cancelled = false;
    erganiSync.loadHistory(foxlineOpenSlot.employee_code, foxlineOpenSlot.date)
      .then((rows) => { if (!cancelled) setErganiHistory(rows); })
      .catch(() => { if (!cancelled) setErganiHistory([]); });
    return () => { cancelled = true; };
  }, [foxlineOpenSlot?.employee_code, foxlineOpenSlot?.date, erganiSync.loadHistory]);
  const scheduleChangeRows = foxlineSync.changeRows || [];
  const scheduleChangesLoading = foxlineSync.loading;
  const unseenChanges = useScheduleUnseenChanges({
    user,
    store,
    weekStartISO,
    weekDates: weekDayDates,
    changeRows: scheduleChangeRows,
    loading: foxlineSync.loading,
  });
  const getLoanShiftType = (empId, dk) =>
    scheduleLoanShiftMeta[weekOffset]?.[empId]?.[dk] || "";
  const getContractedWorkDays = (emp) => Number(emp?.work_days ?? emp?.days_per_week ?? emp?.workDays ?? 5);
  const countsTowardContractDay = (val) => {
    if (!val || val === "OFF" || val === "ΑΔΕΙΑ") return false;
    if (SPECIAL_STATUSES.find((s) => s.value === val)) return false;
    return Boolean(parseShiftMins(val));
  };
  const foxSlotForEmployeeDay = (empId, dk) => {
    const idx = weekDates.findIndex((d, i) => dayKey(d, i) === dk);
    if (idx < 0) return null;
    return lookupSlotByKey(foxlineSync.slotByKey, empId, toLocalISO(weekDates[idx]));
  };
  const isSoftoneChangeLockedCell = (empId, dk) =>
    slotSoftoneChangeLocked(foxSlotForEmployeeDay(empId, dk));
  const openSoftoneLockPopup = (empId, dk) => {
    const fox = foxSlotForEmployeeDay(empId, dk);
    const idx = weekDates.findIndex((d, i) => dayKey(d, i) === dk);
    const iso = idx >= 0 ? toLocalISO(weekDates[idx]) : "";
    setFoxlinePopupKey(fox?.key || (iso ? slotKey(empId, iso) : null));
    setActiveCell(null);
  };
  const notifySoftoneChangeLocked = () => {
    setToast(
      lang === "gr"
        ? softoneChangeLockLabel("gr")
        : softoneChangeLockLabel("en")
    );
  };
  /** Live grid rules: Internal when set, otherwise the SoftOne value. Copied/hypothetical maps stay internal-only. */
  const scheduleRuleValue = (empId, dk, weekSchedule = schedule) => {
    const stored = weekSchedule?.[empId]?.[dk] || "";
    if (weekSchedule !== schedule) return stored;
    return displayedScheduleValue(stored, foxSlotForEmployeeDay(empId, dk));
  };
  const cellCountsAsWorkingDay = (empId, dk, weekSchedule = schedule) => {
    const val = scheduleRuleValue(empId, dk, weekSchedule);
    const persistedStatus = String(getPersistedCellStatus(empId, dk) || "").toLowerCase();
    const loanType = String(getLoanShiftType(empId, dk) || "").toLowerCase();
    if (persistedStatus === "leave" || val === "ΑΔΕΙΑ") return false;
    if (val === "OFF" || persistedStatus === "off") return false;
    if (loanType === "loan_away") return false;
    return Boolean(parseShiftMins(val));
  };
  const countScheduledShiftDays = (empId, weekSchedule = schedule) => {
    return weekDates.reduce((acc, d, i) => {
      const dk = dayKey(d, i);
      return cellCountsAsWorkingDay(empId, dk, weekSchedule) ? acc + 1 : acc;
    }, 0);
  };
  const contractedDaysLimitForCell = (empId, dk, weekSchedule = schedule) => {
    const emp = storeEmployees.find((e) => String(rosterScheduleKey(e)) === String(empId));
    const contractedDays = getContractedWorkDays(emp);
    const currentEmpSched = weekSchedule?.[empId] || {};
    const hasShiftInTargetCell = countsTowardContractDay(currentEmpSched[dk] || "");
    const currentScheduledDays = countScheduledShiftDays(empId, weekSchedule);
    return {
      emp,
      contractedDays,
      currentScheduledDays,
      blocked: !hasShiftInTargetCell && currentScheduledDays >= contractedDays,
    };
  };
  const showContractedDaysToast = (emp, contractedDays, currentScheduledDays) => {
    const firstName = String(emp?.name || "").split(" ")[0] || "Employee";
    setToast(
      `${firstName} is contracted ${contractedDays} days/week and is already scheduled ${currentScheduledDays} days. Remove a shift first.`
    );
  };
  const openLoanModal = (emp, dk) => {
    const dateISO = dayKeyToDateISO(dk);
    if (!dateISO) return;
    setLoanModalContext({ emp, dk, dateISO, dateLabel: dk });
    setLoanModalOpen(true);
    setActiveCell(null);
    setMobileActiveEmp(null);
  };
  const openLoanRequestModal = (emp, dk) => {
    const dateISO = dayKeyToDateISO(dk);
    if (!dateISO) return;
    setLoanRequestContext({ emp, dk, dateISO, dateLabel: dk });
    setLoanRequestModalOpen(true);
    setActiveCell(null);
    setMobileActiveEmp(null);
  };
  const submitLoanRequest = async (reason) => {
    if (!loanRequestContext?.emp) return;
    const emp = loanRequestContext.emp;
    const { dk, dateISO } = loanRequestContext;
    setLoanRequestSubmitting(true);
    try {
      const r = await fetch(`${SHIFTIQ_API}/loans/request`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          requesting_store_id: store,
          employee_code: rosterScheduleKey(emp),
          loan_date: dateISO,
          reason,
        }),
      });
      if (!r.ok) {
        let detail = lang === "gr" ? "Σφάλμα αιτήματος δανεισμού" : "Loan request failed";
        try {
          const errBody = await r.json();
          const raw = errBody?.detail ?? errBody?.message;
          if (typeof raw === "string" && raw.trim()) detail = raw;
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      const empKey = rosterScheduleKey(emp);
      setCell(empKey, dk, "OTHER_STORE");
      setScheduleLoanShiftMeta((prev) => ({
        ...prev,
        [weekOffset]: {
          ...(prev[weekOffset] || {}),
          [empKey]: {
            ...(prev[weekOffset]?.[empKey] || {}),
            [dk]: "loan_pending",
          },
        },
      }));
      setLoanRequestModalOpen(false);
      setLoanRequestContext(null);
      fetchWeeklyScheduleGrid();
      setToast(
        lang === "gr"
          ? "Αίτημα δανεισμού στάλθηκε — εκκρεμεί έγκριση"
          : "Loan request sent — pending approval"
      );
    } catch (err) {
      setToast(err?.message || (lang === "gr" ? "Σφάλμα αιτήματος δανεισμού" : "Loan request failed"));
    } finally {
      setLoanRequestSubmitting(false);
    }
  };
  const handleLoanSubmit = async (payload) => {
    if (!loanModalContext?.emp) return;
    if (!isValidShiftRange(payload.shift_start, payload.shift_end)) {
      setToast(
        lang === "gr" ? "Η ώρα λήξης πρέπει να είναι μετά την έναρξη." : "End time must be after start time."
      );
      return;
    }
    const emp = loanModalContext.emp;
    const { dk, dateISO } = loanModalContext;
    setLoanSubmitting(true);
    try {
      const body = {
        employee_code: rosterScheduleKey(emp),
        employee_name: emp.name,
        home_store: store,
        host_store: payload.host_store,
        loan_date: dateISO,
        shift_start: parseShiftEndValue(payload.shift_start).time,
        shift_end: parseShiftEndValue(payload.shift_end).time,
        reason: payload.reason,
        created_by_name: user?.name || user?.email || null,
        created_by_role: user?.role || null,
      };
      const r = await fetch(`${API_BASE}/staff/loans`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        let detail = lang === "gr" ? "Σφάλμα δανεισμού" : "Loan failed";
        try {
          const errBody = await r.json();
          const raw = errBody?.detail ?? errBody?.message;
          if (typeof raw === "string" && raw.trim()) detail = raw;
          else if (Array.isArray(raw) && raw[0]?.msg) detail = raw[0].msg;
        } catch {
          if (r.status === 404) {
            detail =
              lang === "gr"
                ? "Το API δανεισμού δεν είναι διαθέσιμο — αναπτύξτε το stiqos-api"
                : "Loan API not available — deploy latest stiqos-api";
          }
        }
        throw new Error(detail);
      }
      await r.json();
      setCell(rosterScheduleKey(emp), dk, "OTHER_STORE");
      setLoanModalOpen(false);
      setLoanModalContext(null);
      fetchWeekLoans();
      fetchWeeklyScheduleGrid();
      const name = String(emp.name || "").split(" ")[0] || emp.name;
      setToast(
        lang === "gr"
          ? `Ο/Η ${name} έχει δανειστεί στο ${payload.host_store}`
          : `${name} loaned to ${payload.host_store}`
      );
    } catch (err) {
      const msg = err?.message || (lang === "gr" ? "Σφάλμα δανεισμού" : "Loan failed");
      setToast(msg);
    } finally {
      setLoanSubmitting(false);
    }
  };
  const clearOtherStoreShift = async (emp, dk, loanIdHint = null) => {
    const empId = rosterScheduleKey(emp);
    const dateISO = dayKeyToDateISO(dk);
    if (!dateISO || !store || store === "All Stores") return;

    const existingLoan = findStaffLoanForCell(weekLoans, {
      employeeCode: empId,
      employeeName: emp.name,
      dateISO,
      store,
    });
    const loanId = loanIdHint || existingLoan?.id;

    try {
      if (loanId) {
        const r = await fetch(`${API_BASE}/staff/loans/${loanId}`, {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify({ status: "cancelled" }),
        });
        if (!r.ok) throw new Error("cancel loan failed");
      } else {
        const r = await fetch(`${SHIFTIQ_API}/schedule`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify([
            {
              store_id: emp.rota_store_id || store,
              rota_week_start: weekStartISO,
              rota_date: dateISO,
              employee_code: empId,
              role: emp.group || "service",
              shift_value: "",
            },
          ]),
        });
        if (!r.ok) throw new Error("delete rota failed");
      }
      clearCell(empId, dk);
      fetchWeeklyScheduleGrid();
      fetchWeekLoans();
      foxlineSync.reload();
      window.dispatchEvent(
        new CustomEvent("rota-updated", {
          detail: { store_id: store, week_start: weekStartISO, refreshGantt: false },
        })
      );
      setToast(
        lang === "gr" ? "Ο δανεισμός αφαιρέθηκε από το πρόγραμμα" : "Loan shift removed from schedule"
      );
    } catch {
      setToast(
        lang === "gr" ? "Σφάλμα αφαίρεσης δανεισμού — δοκιμάστε ξανά" : "Failed to clear loan — try again"
      );
    }
  };
  const openCellEditor = (emp, dk) => {
    if (isPastWeek) return;
    if (!gridEditable) {
      notifyLadderEditLocked();
      return;
    }
    const empId = rosterScheduleKey(emp);
    if (isSoftoneChangeLockedCell(empId, dk)) {
      openSoftoneLockPopup(empId, dk);
      return;
    }
    const isActive = activeCell?.empId === empId && activeCell?.dk === dk;
    if (isActive) {
      setActiveCell(null);
      return;
    }
    const cellValNow = getCell(empId, dk);
    const canRequestLoan = ["store_manager", "admin", "manager"].includes(user?.role);
    if (!cellValNow && isCrossBranchEmployee(emp, store) && canRequestLoan) {
      openLoanRequestModal(emp, dk);
      return;
    }
    // Allow opening when at contracted-day max so Rest Day (OFF) can still be set.
    // Working shifts stay blocked in setCell / the picker when limit.blocked.
    setActiveCell({
      empId,
      dk,
      shiftHours: emp.shiftHours ?? emp.shift_hours ?? 8,
      empName: emp.name,
      dayLabel: dk,
    });
  };
  const scheduledByDayIndex = weekDates.map((d, i) => {
    const dk = dayKey(d, i);
    return storeEmployees.reduce((count, emp) => {
      const shift = parseShiftMins(scheduleRuleValue(rosterScheduleKey(emp), dk));
      return shift ? count + 1 : count;
    }, 0);
  });

  const rawAvgHourly = headcountPlanningData?.stores?.[store]?.current_capacity?.avg_hourly_rate;
  const avgRate = Number.isFinite(Number(rawAvgHourly)) && Number(rawAvgHourly) > 0 ? Number(rawAvgHourly) : 6.7;
  const PEOPLE_COST_EFKA = 1.2415;
  const builderPeopleCostPctByDay = useMemo(() => weekDates.map((d, i) => {
    const iso = toLocalISO(d);
    const breakdown = peopleCost?.daily_breakdown?.[iso];
    if (!breakdown) return null;
    const dk = dayKey(d, i);
    let softoneOnlyHours = 0;
    for (const emp of storeEmployees) {
      const empKey = rosterScheduleKey(emp);
      const internal = schedule[empKey]?.[dk] || "";
      const foxSlot = lookupSlotByKey(foxlineSync.slotByKey, empKey, iso);
      const shown = displayedLabourHours(internal, foxSlot);
      if (shown.source === "softone") {
        const share = Number(emp.cost_share);
        const weight = emp.shared_hub && Number.isFinite(share) && share > 0 && share < 1 ? share : 1;
        softoneOnlyHours += shown.hours * weight;
      }
    }
    return peopleCostPctWithDisplayedSlots({
      apiLabourCostEur: breakdown.labour_cost_eur ?? breakdown.cost,
      apiPeopleCostPct: breakdown.people_cost_pct,
      revenue: breakdown.revenue_amount ?? breakdown.forecasted_revenue,
      softoneOnlyHours,
      avgHourlyRate: avgRate,
      efka: PEOPLE_COST_EFKA,
    });
  }), [weekDates, peopleCost, schedule, storeEmployees, foxlineSync?.slotByKey, avgRate]);
  const builderPeopleCostRevenueByDay = useMemo(() => weekDates.map((d) => {
    const iso = toLocalISO(d);
    const day = peopleCost?.daily_breakdown?.[iso];
    return {
      basis: day?.revenue_basis ?? peopleCost?.revenue_basis ?? null,
      amount: day?.revenue_amount ?? day?.forecasted_revenue ?? null,
    };
  }), [weekDates, peopleCost]);

  function totalScheduledHoursForDay(schedMap, dayIndex) {
    const d = weekDates[dayIndex];
    if (!d) return 0;
    const dk = dayKey(d, dayIndex);
    let hours = 0;
    for (const emp of storeEmployees) {
      const sh = parseShiftMins(schedMap[rosterScheduleKey(emp)]?.[dk] || "");
      if (sh) hours += (sh.end - sh.start) / 60;
    }
    return hours;
  }
  function scheduledCodesOnDay(dayIndex) {
    const d = weekDates[dayIndex];
    if (!d) return [];
    const dk = dayKey(d, dayIndex);
    const iso = toLocalISO(d);
    const codes = [];
    const seen = new Set();
    for (const emp of storeEmployees) {
      const empKey = rosterScheduleKey(emp);
      const internal = schedule[empKey]?.[dk] || "";
      const foxSlot = lookupSlotByKey(foxlineSync.slotByKey, empKey, iso);
      const shown = displayedLabourHours(internal, foxSlot);
      if (!shown.hours) continue;
      const code = String(emp.employee_code || "").trim();
      if (!code || seen.has(code)) continue;
      seen.add(code);
      codes.push(code);
    }
    return codes;
  }
  function dayOffCostSnapshot(dayIndex) {
    const d = weekDates[dayIndex];
    if (!d) return { revenue: null, labourCostEur: null, shifts: [], thresholdPct: 22 };
    const dk = dayKey(d, dayIndex);
    const iso = toLocalISO(d);
    const breakdown = peopleCost?.daily_breakdown?.[iso];
    const revenue = Number(breakdown?.revenue_amount ?? breakdown?.forecasted_revenue);
    const apiLabour = Number(breakdown?.labour_cost_eur ?? breakdown?.cost);
    const baseCost = Number.isFinite(apiLabour) && apiLabour > 0 ? apiLabour : 0;
    const shifts = [];
    let softoneHours = 0;
    for (const emp of storeEmployees) {
      const empKey = rosterScheduleKey(emp);
      const internal = schedule[empKey]?.[dk] || "";
      const foxSlot = lookupSlotByKey(foxlineSync.slotByKey, empKey, iso);
      const shown = displayedLabourHours(internal, foxSlot);
      if (!shown.hours) continue;
      const code = String(emp.employee_code || "").trim();
      if (!code) continue;
      const share = Number(emp.cost_share);
      const weight = emp.shared_hub && Number.isFinite(share) && share > 0 && share < 1 ? share : 1;
      const costEur = shown.hours * weight * avgRate * PEOPLE_COST_EFKA;
      if (shown.source === "softone") softoneHours += shown.hours * weight;
      const existing = shifts.find((shift) => shift.code === code);
      if (existing) existing.costEur += costEur;
      else shifts.push({ code, costEur });
    }
    return {
      revenue: Number.isFinite(revenue) ? revenue : null,
      labourCostEur: baseCost + softoneHours * avgRate * PEOPLE_COST_EFKA,
      shifts,
      thresholdPct: typeof peopleCost?.threshold_pct === "number" ? peopleCost.threshold_pct : 22,
    };
  }
  function peopleCostPctForDayWithSchedule(schedMap, dayIndex) {
    const d = weekDates[dayIndex];
    if (!d) return null;
    const iso = toLocalISO(d);
    const dk = dayKey(d, dayIndex);
    let hours = 0;
    for (const emp of storeEmployees) {
      const sh = parseShiftMins(schedMap[rosterScheduleKey(emp)]?.[dk] || "");
      if (sh) hours += (sh.end - sh.start) / 60;
    }
    const revRaw = peopleCost?.daily_breakdown?.[iso]?.forecasted_revenue;
    const rev = revRaw != null ? Number(revRaw) : NaN;
    if (!Number.isFinite(rev) || rev <= 0) return null;
    return ((hours * avgRate * PEOPLE_COST_EFKA) / rev) * 100;
  }
  function getAdminShiftBeyondCapFlags(empId, dk, val) {
    if (!adminScheduleOverride) return [];
    const dayIdx = weekDates.findIndex((d, i) => dayKey(d, i) === dk);
    if (dayIdx < 0) return [];
    const flags = [];
    const dn = dayNames[dayIdx];
    const hyp = { ...schedule, [empId]: { ...(schedule[empId] || {}), [dk]: val } };

    if (countsTowardContractDay(val)) {
      const emp = storeEmployees.find((e) => String(rosterScheduleKey(e)) === String(empId));
      if (emp) {
        const contracted = getContractedWorkDays(emp);
        const prevDays = countScheduledShiftDays(empId, schedule);
        const hypDays = countScheduledShiftDays(empId, hyp);
        if (hypDays > contracted && (hypDays > prevDays || prevDays <= contracted)) {
          flags.push({
            type: "contractDays",
            firstName: String(emp.name || "").trim().split(/\s+/)[0] || "Employee",
            hypDays,
            contracted,
          });
        }
      }
    }

    const sh = parseShiftMins(val);
    if (!sh) return flags;

    const ohRaw = openingHours[dn];
    if (ohRaw && ohRaw !== "CLOSED") {
      const ohParts = String(ohRaw).split("-");
      if (ohParts.length === 2) {
        const openMins = timeToMins(ohParts[0].trim());
        let closeMins = timeToMins(ohParts[1].trim());
        if (openMins != null && closeMins != null) {
          if (closeMins < openMins) closeMins += 24 * 60;
          if (sh.start < openMins || sh.end > closeMins) {
            flags.push({ type: "opening", oh: ohRaw, shiftLabel: val });
          }
        }
      }
    }
    const prevH = totalScheduledHoursForDay(schedule, dayIdx);
    const hypH = totalScheduledHoursForDay(hyp, dayIdx);
    const hypPct = peopleCostPctForDayWithSchedule(hyp, dayIdx);
    const THRESHOLD = typeof peopleCost?.threshold_pct === "number" ? peopleCost.threshold_pct : 22;
    if (hypPct != null && hypPct > THRESHOLD && hypH > prevH + 1 / 60) {
      flags.push({ type: "people", hypPct, threshold: THRESHOLD, dayShort: dn });
    }
    return flags;
  }

  function getCell(empId, dk) { return schedule[empId]?.[dk] || ""; }
  function isScheduleCellDirty(empId, dk) {
    const cacheKey = `${store}:${weekStartISO}`;
    const persisted = lastPersistedScheduleRef.current[cacheKey]?.[empId]?.[dk];
    return isScheduleCellDirtyAgainstBaseline(schedule[empId]?.[dk], persisted);
  }
  function isScheduleCellConflicted(empId, dk) {
    return Boolean(scheduleConflictCellsByWeek[weekOffset]?.[empId]?.[dk]);
  }
  function getScheduleCellConflictDetail(empId, dk) {
    return scheduleConflictCellsByWeek[weekOffset]?.[empId]?.[dk] || null;
  }
  function getPersistedCellStatus(empId, dk) {
    return persistedCellStatusByWeek[weekOffset]?.[empId]?.[dk] || null;
  }
  function setCell(empId, dk, val) {
    if (isPastWeek || !gridEditable) return;
    if (isSoftoneChangeLockedCell(empId, dk)) {
      openSoftoneLockPopup(empId, dk);
      notifySoftoneChangeLocked();
      return;
    }
    const nextCountsTowardContract = countsTowardContractDay(val);
    if (nextCountsTowardContract) {
      const limit = contractedDaysLimitForCell(empId, dk, schedule);
      if (!adminScheduleOverride && limit.blocked) {
        setAssignmentLimitWarning({
          empId,
          dk,
          message: `${String(limit.emp?.name || "").split(" ")[0] || "Employee"} is contracted for ${limit.contractedDays} days/week — already scheduled ${limit.currentScheduledDays} days. Remove a day first.`,
        });
        showContractedDaysToast(limit.emp, limit.contractedDays, limit.currentScheduledDays);
        if (assignmentLimitWarningTimeoutRef.current) clearTimeout(assignmentLimitWarningTimeoutRef.current);
        assignmentLimitWarningTimeoutRef.current = setTimeout(() => setAssignmentLimitWarning(null), 4000);
        return;
      }
    }
    setScheduleByWeek((prev) => {
      const cur = prev[weekOffset] || {};
      const nextWeek = { ...cur, [empId]: { ...(cur[empId] || {}), [dk]: val } };
      queueMicrotask(() => syncUnsavedAgainstBaseline(nextWeek));
      return { ...prev, [weekOffset]: nextWeek };
    });
    clearScheduleCellConflict(empId, dk);
    setAssignmentLimitWarning(null);
    setActiveCell(null);
    scheduleProactiveValidationRef.current(1500);
  }
  function clearCell(empId, dk) {
    if (isPastWeek || !gridEditable) return;
    if (isSoftoneChangeLockedCell(empId, dk)) {
      openSoftoneLockPopup(empId, dk);
      notifySoftoneChangeLocked();
      return;
    }
    setScheduleByWeek((prev) => {
      const cur = prev[weekOffset] || {};
      const nextWeek = { ...cur, [empId]: { ...(cur[empId] || {}), [dk]: "" } };
      queueMicrotask(() => syncUnsavedAgainstBaseline(nextWeek));
      return { ...prev, [weekOffset]: nextWeek };
    });
    clearScheduleCellConflict(empId, dk);
    scheduleProactiveValidationRef.current(1500);
  }

  const isScheduleShiftValue = (val) => Boolean(parseShiftMins(val));

  const shiftStringFromCopied = (copied) => {
    if (!copied?.shift_start || !copied?.shift_end) return "";
    return `${copied.shift_start}-${copied.shift_end}`;
  };

  const shiftCopiedFromCellVal = (val, role) => {
    if (!isScheduleShiftValue(val)) return null;
    const parts = String(val).split("-");
    if (parts.length !== 2) return null;
    return {
      shift_start: parts[0].trim(),
      shift_end: parts[1].trim(),
      role: String(role || "service").toLowerCase(),
    };
  };

  const isCellPasteBlocked = (empId, dk, val = getCell(empId, dk)) => {
    if (isPastWeek) return true;
    if (isSoftoneChangeLockedCell(empId, dk)) return true;
    const persistedStatus = String(getPersistedCellStatus(empId, dk) || "").toLowerCase();
    const loanType = String(getLoanShiftType(empId, dk) || "").toLowerCase();
    if (persistedStatus === "leave" || val === "ΑΔΕΙΑ") return true;
    if (val === "OFF" || persistedStatus === "off") return true;
    if (loanType === "loan_away") return true;
    if (val === "OTHER_STORE") return true;
    if (SPECIAL_STATUSES.find((s) => s.value === val)) return true;
    return false;
  };

  const isCellEmptyForPaste = (empId, dk, val = getCell(empId, dk)) => {
    if (isCellPasteBlocked(empId, dk, val)) return false;
    return !isScheduleShiftValue(val);
  };

  const closeScheduleCellContextMenu = () => setScheduleCellContextMenu(null);

  const clearCopiedShiftMode = () => {
    setCopiedShift(null);
    setPasteReplaceConfirm(null);
    setHoveredPasteCell(null);
    closeScheduleCellContextMenu();
  };

  const tryApplyShiftValueToCell = (empId, dk, val) => {
    if (countsTowardContractDay(val)) {
      const limit = contractedDaysLimitForCell(empId, dk, schedule);
      if (!adminScheduleOverride && limit.blocked) {
        setAssignmentLimitWarning({
          empId,
          dk,
          message: `${String(limit.emp?.name || "").split(" ")[0] || "Employee"} is contracted for ${limit.contractedDays} days/week — already scheduled ${limit.currentScheduledDays} days. Remove a day first.`,
        });
        showContractedDaysToast(limit.emp, limit.contractedDays, limit.currentScheduledDays);
        if (assignmentLimitWarningTimeoutRef.current) clearTimeout(assignmentLimitWarningTimeoutRef.current);
        assignmentLimitWarningTimeoutRef.current = setTimeout(() => setAssignmentLimitWarning(null), 4000);
        return false;
      }
    }
    const capFlags = getAdminShiftBeyondCapFlags(empId, dk, val);
    if (capFlags.length > 0) {
      setAdminCapShiftConfirm({ empId, dk, val, capFlags });
      return false;
    }
    setCell(empId, dk, val);
    return true;
  };

  const applyCopiedShiftToCell = (empId, dk) => {
    if (!copiedShift || isPastWeek) return false;
    const val = shiftStringFromCopied(copiedShift);
    if (!val) return false;
    if (isCellPasteBlocked(empId, dk)) return false;
    return tryApplyShiftValueToCell(empId, dk, val);
  };

  const copyShiftFromCell = (empId, dk, val, role) => {
    const payload = shiftCopiedFromCellVal(val, role);
    if (!payload) return;
    setCopiedShift({ ...payload, sourceEmpId: empId, sourceDk: dk });
    setPasteReplaceConfirm(null);
    closeScheduleCellContextMenu();
  };

  const pasteCopiedShiftToRemainingWeek = (empId) => {
    if (!copiedShift || isPastWeek) return;
    weekDates.forEach((d, i) => {
      const dk = dayKey(d, i);
      if (dk === copiedShift.sourceDk) return;
      const cellVal = getCell(empId, dk);
      if (!isCellEmptyForPaste(empId, dk, cellVal)) return;
      applyCopiedShiftToCell(empId, dk);
    });
    closeScheduleCellContextMenu();
  };

  const handleScheduleCellContextMenu = (e, emp, dk) => {
    e.preventDefault();
    e.stopPropagation();
    const empId = rosterScheduleKey(emp);
    if (isSoftoneChangeLockedCell(empId, dk)) {
      openSoftoneLockPopup(empId, dk);
      return;
    }
    if (!gridEditable) {
      notifyLadderEditLocked();
      return;
    }
    setScheduleCellContextMenu({
      x: e.clientX,
      y: e.clientY,
      emp,
      dk,
      empId,
      val: getCell(empId, dk),
      dateISO: dayKeyToDateISO(dk),
    });
  };

  const openSlotHistory = (emp, dk, val) => {
    const empId = rosterScheduleKey(emp);
    setSlotHistoryTarget({
      emp,
      empId,
      dk,
      dateISO: dayKeyToDateISO(dk),
      val,
    });
    closeScheduleCellContextMenu();
  };

  const handlePasteShiftClick = (emp, dk) => {
    if (!copiedShift || isPastWeek) return;
    if (!gridEditable) {
      notifyLadderEditLocked();
      return;
    }
    const empId = rosterScheduleKey(emp);
    const val = getCell(empId, dk);
    if (isCellPasteBlocked(empId, dk, val)) return;
    if (isScheduleShiftValue(val)) {
      setPasteReplaceConfirm({ empId, dk, emp });
      return;
    }
    if (isCellEmptyForPaste(empId, dk, val)) {
      setPasteReplaceConfirm(null);
      applyCopiedShiftToCell(empId, dk);
    }
  };

  const handleScheduleCellClick = (emp, dk) => {
    if (isPastWeek) return;
    const empId = rosterScheduleKey(emp);
    setScheduleCellFocus({ empId, dk });
    if (isSoftoneChangeLockedCell(empId, dk)) {
      openSoftoneLockPopup(empId, dk);
      return;
    }
    if (!gridEditable) {
      notifyLadderEditLocked();
      return;
    }
    if (copiedShift) {
      handlePasteShiftClick(emp, dk);
      return;
    }
    openCellEditor(emp, dk);
  };

  function copyPreviousWeek() {
    if (!gridEditable) {
      notifyLadderEditLocked();
      return;
    }
    const prevOffset = weekOffset - 1;
    const prevSched = getWeekSched(prevOffset);
    if (!prevSched || Object.keys(prevSched).length === 0) return;
    // Re-map day keys from previous week to current week
    const newSched = {};
    storeEmployees.forEach(emp => {
      newSched[rosterScheduleKey(emp)] = {};
      weekDates.forEach((d, i) => {
        const curDk  = dayKey(d, i);
        const prevD  = new Date(getMonday(prevOffset));
        prevD.setDate(getMonday(prevOffset).getDate() + i);
        const prevDk = dayNames[i] + " " + fmtDate(prevD);
        newSched[rosterScheduleKey(emp)][curDk] = prevSched[rosterScheduleKey(emp)]?.[prevDk] || "";
      });
    });
    setScheduleByWeek(prev => ({ ...prev, [weekOffset]: newSched }));
    syncUnsavedAgainstBaseline(newSched);
    const exceededEmployees = storeEmployees.filter((emp) => {
      const scheduledDays = countScheduledShiftDays(rosterScheduleKey(emp), newSched);
      return scheduledDays > getContractedWorkDays(emp);
    });
    if (exceededEmployees.length > 0) {
      setToast(`${exceededEmployees.length} employees exceed their contracted days after copy. Review before saving.`);
    }
  }
  const contractedHoursInRoster = useMemo(() => {
    const hours = new Set(storeEmployees.map((emp) => normalizeContractedShiftHours(emp)));
    return Array.from(hours).sort((a, b) => a - b);
  }, [storeEmployees]);
  const adminShiftPresetUnion = useMemo(() => {
    const merged = [];
    contractedHoursInRoster.forEach((hours) => {
      merged.push(...shiftPresetsForHours(hours, customShifts6, customShifts8));
    });
    return Array.from(new Set(merged));
  }, [contractedHoursInRoster, customShifts6, customShifts8]);

  // ── Alert engine ────────────────────────────────────────────────────────────
  const alertEngine = useMemo(() => {
  const shiftsAssigned = storeEmployees.some(emp =>
    weekDates.some((d, i) => {
      const val = scheduleRuleValue(rosterScheduleKey(emp), dayKey(d, i));
      return val && val !== "" && val !== "OFF" &&
             !SPECIAL_STATUSES.find(s => s.value === val);
    })
  );
  const alerts = [];

  storeEmployees.forEach(emp => {
    const dayCells = weekDates.map((d, i) => ({ dk: dayKey(d, i), dayIdx: i, val: scheduleRuleValue(rosterScheduleKey(emp), dayKey(d, i)), dayName: dayNames[i] }));

    // 1. Missing shifts: 5-day employee should have exactly workDays filled shifts
    const workedDays = dayCells.filter(c => {
      const v = c.val;
      if (!v || v === "OFF") return false;
      if (v === "ΑΔΕΙΑ") return true; // Treat leave as a valid fulfilled day for contract-day checks
      return !SPECIAL_STATUSES.find(s => v === s.value);
    }).length;
    const emptyDays = dayCells.filter(c => !c.val).length;
    if (emptyDays > 0) {
      alerts.push({ type: "empty", emp: emp.name, msg: `${emp.name.split(" ")[0]} has ${emptyDays} day(s) not filled in.`, msgGr: `${emp.name.split(" ")[0]}: ${emptyDays} μέρα(ες) χωρίς καταχώρηση.` });
    }
    const employeeWorkDays = Number(emp.workDays ?? emp.days_per_week ?? 5);
    if (workedDays > 0 && workedDays !== employeeWorkDays) {
      const diff = employeeWorkDays - workedDays;
      if (diff > 0) {
        alerts.push({ type: "shifts", emp: emp.name, msg: `${emp.name.split(" ")[0]} is on a ${employeeWorkDays}-day contract but only has ${workedDays} work shifts this week (missing ${diff}).`, msgGr: `${emp.name.split(" ")[0]}: ${employeeWorkDays} μέρες/εβδ. αλλά μόνο ${workedDays} βάρδιες (λείπουν ${diff}).` });
      }
    }

    const offDays = dayCells.filter((c) => c.val === "OFF").length;
    const noWorkDays = dayCells.filter((c) => c.val === "NO_WORK").length;
    const firstName = emp.name.split(" ")[0];
    if (employeeWorkDays === 4 && emptyDays === 0) {
      if (offDays !== 2 || noWorkDays !== 1) {
        alerts.push({
          type: "no_work",
          emp: emp.name,
          msg: `${firstName}: 4-day contract needs 2 rest days (OFF) and 1 No Work day (has ${offDays} OFF, ${noWorkDays} No Work).`,
          msgGr: `${firstName}: συμβόλαιο 4 ημερών χρειάζεται 2 ρεπό (OFF) και 1 Μη εργασία (έχει ${offDays} OFF, ${noWorkDays} Μη εργασία).`,
        });
      }
    } else if (employeeWorkDays !== 4 && noWorkDays > 0) {
      alerts.push({
        type: "no_work",
        emp: emp.name,
        msg: `${firstName}: No Work is normally only for 4-day contracts (override with a reason if intentional).`,
        msgGr: `${firstName}: Η Μη εργασία ισχύει κανονικά μόνο για συμβόλαια 4 ημερών (override με λόγο αν είναι σκόπιμο).`,
      });
    }

    // 2. Rest rule: <11h between end of one shift and start of next
    for (let i = 0; i < dayCells.length - 1; i++) {
      const todayShift = parseShiftMins(dayCells[i].val);
      const tomorrowShift = parseShiftMins(dayCells[i+1].val);
      if (!todayShift || !tomorrowShift) continue;
      // Minutes from today's end to tomorrow's start (add 24h for tomorrow)
      const restMins = (tomorrowShift.start + 24 * 60) - todayShift.end;
      if (restMins < 11 * 60) {
        const restH = (restMins / 60).toFixed(1);
        alerts.push({ type: "rest", emp: emp.name, msg: `${emp.name.split(" ")[0]}: Only ${restH}h rest between ${dayNames[i]} (${dayCells[i].val}) and ${dayNames[i+1]} (${dayCells[i+1].val}). Min 11h required.`, msgGr: `${emp.name.split(" ")[0]}: Μόνο ${restH}ω ανάπαυση μεταξύ ${dayNames[i]} και ${dayNames[i+1]}. Απαιτούνται 11ω.` });
      }
    }
  });

  if (shiftsAssigned) {
    // 3. Coverage: check each day's opening hours are covered
    dayNames.forEach((dn, di) => {
      const oh = openingHours[dn];
      if (!oh || oh === "CLOSED") return; // skip closed days
      const ohParts = oh.split("-");
      if (ohParts.length !== 2) return;
      const openMins  = timeToMins(ohParts[0]);
      const closeMins = timeToMins(ohParts[1]) < timeToMins(ohParts[0]) ? timeToMins(ohParts[1]) + 24*60 : timeToMins(ohParts[1]);
      if (openMins === null || closeMins === null) return;

      const dk = dayKey(weekDates[di], di);
      // Collect all shifts working that day
      const shiftsToday = storeEmployees
        .map(emp => parseShiftMins(scheduleRuleValue(rosterScheduleKey(emp), dk)))
        .filter(Boolean);

      if (shiftsToday.length === 0) {
        alerts.push({ type: "coverage", day: dn, msg: `${dn}: No shifts assigned — store opens ${oh}.`, msgGr: `${dn}: Δεν υπάρχουν βάρδιες — το κατάστημα είναι ανοιχτό ${oh}.` });
        return;
      }

      const earliestStart = Math.min(...shiftsToday.map(s => s.start));
      const latestEnd     = Math.max(...shiftsToday.map(s => s.end));

      if (earliestStart > openMins) {
        const gap = Math.round((earliestStart - openMins));
        alerts.push({ type: "coverage", day: dn, msg: `${dn}: Opening gap — store opens at ${oh.split("-")[0]} but first shift starts ${Math.round(gap/60)}h${gap%60 ? gap%60+"m" : ""} later.`, msgGr: `${dn}: Κενό ανοίγματος — το κατάστημα ανοίγει ${oh.split("-")[0]} αλλά η πρώτη βάρδια ξεκινά αργότερα.` });
      }
      if (latestEnd < closeMins) {
        alerts.push({ type: "coverage", day: dn, msg: `${dn}: Closing gap — last shift ends before store closes at ${oh.split("-")[1]}.`, msgGr: `${dn}: Κενό κλεισίματος — η τελευταία βάρδια τελειώνει πριν κλείσει το κατάστημα.` });
      }
    });

    // ── Rule: at least 1 kitchen/cleaning + 1 service crew at opening & closing ──
    dayNames.forEach((dn, di) => {
      const oh = openingHours[dn];
      if (!oh || oh === "CLOSED") return; // skip closed days
      const ohParts = oh.split("-");
      if (ohParts.length !== 2) return;
      const openMins  = timeToMins(ohParts[0]);
      const closeMins = timeToMins(ohParts[1]) < timeToMins(ohParts[0]) ? timeToMins(ohParts[1]) + 24*60 : timeToMins(ohParts[1]);
      if (openMins === null || closeMins === null) return;

      const dk = dayKey(weekDates[di], di);

      const empShifts = storeEmployees.map(emp => ({
        emp,
        shift: parseShiftMins(scheduleRuleValue(rosterScheduleKey(emp), dk)),
        group: emp.group || "service",
      })).filter(e => e.shift);

      const coversOpen  = e => e.shift.start <= openMins + 30;
      const coversClose = e => e.shift.end   >= closeMins - 30;
      const isKitchenOrCleaning = e => e.group === "kitchen" || e.group === "cleaning";
      const isService            = e => e.group === "service" || e.group === "supervisor";

      const openingEmps  = empShifts.filter(coversOpen);
      const closingEmps  = empShifts.filter(coversClose);

      if (openingEmps.length > 0) {
        if (!openingEmps.some(isKitchenOrCleaning))
          alerts.push({ type: "role", day: dn, msg: `${dn}: No kitchen/cleaning crew scheduled at opening (${ohParts[0]}).`, msgGr: `${dn}: Δεν υπάρχει κουζίνα/καθαριότητα στο άνοιγμα (${ohParts[0]}).` });
        if (!openingEmps.some(isService))
          alerts.push({ type: "role", day: dn, msg: `${dn}: No service crew scheduled at opening (${ohParts[0]}).`, msgGr: `${dn}: Δεν υπάρχει service crew στο άνοιγμα (${ohParts[0]}).` });
      }
      if (closingEmps.length > 0) {
        if (!closingEmps.some(isKitchenOrCleaning))
          alerts.push({ type: "role", day: dn, msg: `${dn}: No kitchen/cleaning crew scheduled at closing (${ohParts[1]}).`, msgGr: `${dn}: Δεν υπάρχει κουζίνα/καθαριότητα στο κλείσιμο (${ohParts[1]}).` });
        if (!closingEmps.some(isService))
          alerts.push({ type: "role", day: dn, msg: `${dn}: No service crew scheduled at closing (${ohParts[1]}).`, msgGr: `${dn}: Δεν υπάρχει service crew στο κλείσιμο (${ohParts[1]}).` });
      }
    });
  }

  const criticalAlerts = alerts.filter(a =>
    a.type === "rest" || a.type === "coverage" || a.type === "role" || a.type === "no_work"
  );
  const warningAlerts = alerts.filter(a =>
    a.type === "empty" || a.type === "shifts"
  );
  return { alerts, criticalAlerts, warningAlerts, anyShiftAssigned: shiftsAssigned };
  }, [storeEmployees, schedule, weekDates, openingHours, weekOffset]);
  const alerts = alertEngine.alerts;
  const criticalAlerts = alertEngine.criticalAlerts;
  const warningAlerts = alertEngine.warningAlerts;
  const anyShiftAssigned = alertEngine.anyShiftAssigned;
  const isScheduleDayFilled = (empId, dk, val) => {
    const persistedStatus = String(getPersistedCellStatus(empId, dk) || "").toLowerCase();
    const loanType = String(getLoanShiftType(empId, dk) || "").toLowerCase();
    if (persistedStatus === "leave" || val === "ΑΔΕΙΑ") return true;
    if (val === "OFF" || persistedStatus === "off") return true;
    if (loanType === "loan_away") return true;
    if (val && SPECIAL_STATUSES.find((s) => s.value === val)) return true;
    return Boolean(parseShiftMins(val));
  };
  const incompleteShiftEmployeeCount = storeEmployees.filter((emp) => {
    const empId = rosterScheduleKey(emp);
    return weekDates.some((d, i) => {
      const dk = dayKey(d, i);
      const val = scheduleRuleValue(empId, dk);
      return !isScheduleDayFilled(empId, dk, val);
    });
  }).length;
  const showWarnings = anyShiftAssigned;

  const cellVal  = activeCell ? getCell(activeCell.empId, activeCell.dk) : "";
  const activeEmp = activeCell
    ? storeEmployees.find((e) => rosterScheduleKey(e) === activeCell.empId)
    : null;
  const activeCellDateISO = (() => {
    if (!activeCell?.dk) return "";
    const idx = weekDates.findIndex((d, i) => dayKey(d, i) === activeCell.dk);
    if (idx < 0) return "";
    return toLocalISO(weekDates[idx]);
  })();
  const activeRestDayAlreadyRequested = Boolean(
    activeEmp &&
      activeCellDateISO &&
      isRestDayMarkedRequested(
        weekLeaveRequests,
        pendingLocalRestRequests,
        activeEmp,
        activeCellDateISO
      )
  );

  const markRestDayAsRequested = async () => {
    if (!canMarkRestRequested || !activeCell || !activeEmp || !store || markRestRequestedLoading) return false;
    if (cellVal !== "OFF") return false;
    if (!activeCellDateISO) return false;
    const token = getOpsToken();
    if (!token) {
      setToast(
        lang === "gr"
          ? "Απαιτείται σύνδεση με email/κωδικό (η γρήγορη σύνδεση χωρίς token δεν επαρκεί)"
          : "Sign in with email/password first (session has no API token)"
      );
      return false;
    }
    if (
      isRestDayMarkedRequested(
        weekLeaveRequests,
        pendingLocalRestRequests,
        activeEmp,
        activeCellDateISO
      )
    ) {
      return true;
    }

    const empCode = String(rosterScheduleKey(activeEmp) || "").trim();
    const fromUsers = (allUsers || []).find(
      (u) => String(u?.employee_code ?? "").trim() === empCode
    );
    const empEmail =
      String(activeEmp.email || fromUsers?.email || "").trim() || null;
    const pendingKey = restDayPendingKey(activeEmp, activeCellDateISO);

    setMarkRestRequestedLoading(true);
    try {
      const res = await fetch(`${SHIFTIQ_API}/leave-requests`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          store_id: store,
          employee_name: activeEmp.name,
          employee_email: empEmail,
          employee_code: empCode || null,
          type: "rest_day",
          date: activeCellDateISO,
          status: "approved",
          reason: "Καταχωρήθηκε από manager",
        }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const detail = payload?.detail;
        const message =
          typeof detail === "string"
            ? detail
            : detail?.message ||
              (lang === "gr"
                ? "Αποτυχία καταχώρησης αίτησης ρεπό"
                : "Failed to mark rest day as requested");
        setToast(message);
        return false;
      }
      // Parent-level optimistic key first — survives ShiftPicker remount on re-render.
      if (pendingKey) {
        setPendingLocalRestRequests((prev) => {
          const next = new Set(prev);
          next.add(pendingKey);
          return next;
        });
      }
      const newRow = {
        id: payload?.id ?? Date.now(),
        store_id: store,
        employee_name: activeEmp.name,
        employee_email: empEmail,
        employee_code: empCode,
        leave_type: "rest_day",
        type: "rest_day",
        leave_date: activeCellDateISO,
        date: activeCellDateISO,
        status: payload?.status || "approved",
      };
      setWeekLeaveRequests((prev) => {
        const list = prev || [];
        const already = hasRequestedRestDay(list, activeEmp, activeCellDateISO);
        return already ? list : [...list, newRow];
      });
      return true;
    } catch {
      setToast(
        lang === "gr"
          ? "Αποτυχία καταχώρησης αίτησης ρεπό"
          : "Failed to mark rest day as requested"
      );
      return false;
    } finally {
      setMarkRestRequestedLoading(false);
    }
  };

  const removeRestDayRequested = async () => {
    if (!canMarkRestRequested || !activeCell || !activeEmp || removeRestRequestedLoading) return false;
    if (!activeCellDateISO) return false;

    const matched = findRequestedRestDayRow(weekLeaveRequests, activeEmp, activeCellDateISO);
    const requestId = matched?.id;
    if (requestId == null) {
      setToast(
        lang === "gr"
          ? "Δεν βρέθηκε η αίτηση ρεπό"
          : "Rest-day request not found"
      );
      return false;
    }

    const token = getOpsToken();
    if (!token) {
      setToast(
        lang === "gr"
          ? "Απαιτείται σύνδεση με email/κωδικό (η γρήγορη σύνδεση χωρίς token δεν επαρκεί)"
          : "Sign in with email/password first (session has no API token)"
      );
      return false;
    }

    const pendingKey = restDayPendingKey(activeEmp, activeCellDateISO);
    setRemoveRestRequestedLoading(true);
    try {
      const res = await fetch(`${SHIFTIQ_API}/leave-requests/${requestId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!res.ok && res.status !== 204) {
        const payload = await res.json().catch(() => null);
        const detail = payload?.detail;
        const message =
          typeof detail === "string"
            ? detail
            : detail?.message ||
              (lang === "gr"
                ? "Αποτυχία αφαίρεσης αίτησης ρεπό"
                : "Failed to remove rest-day request");
        setToast(message);
        return false;
      }
      if (pendingKey) {
        setPendingLocalRestRequests((prev) => {
          if (!prev || !prev.has(pendingKey)) return prev;
          const next = new Set(prev);
          next.delete(pendingKey);
          return next;
        });
      }
      setWeekLeaveRequests((prev) =>
        (prev || []).filter((row) => String(row?.id) !== String(requestId))
      );
      return true;
    } catch {
      setToast(
        lang === "gr"
          ? "Αποτυχία αφαίρεσης αίτησης ρεπό"
          : "Failed to remove rest-day request"
      );
      return false;
    } finally {
      setRemoveRestRequestedLoading(false);
    }
  };

  const activeEmpShiftHours = normalizeContractedShiftHours(
    activeEmp ?? activeCell?.shiftHours ?? 8
  );
  const shiftOptions = activeEmp
    ? shiftPresetsForHours(activeEmpShiftHours, customShifts6, customShifts8)
    : shiftPresetsForHours(8, customShifts6, customShifts8);
  const mobileDk = dayKey(weekDates[mobileDay], mobileDay);
  const focusedDateObj = focusDay ? new Date(`${focusDay}T12:00:00`) : null;
  const focusedDayIndex = focusedDateObj ? (focusedDateObj.getDay() + 6) % 7 : -1;
  const focusedDayName = focusedDayIndex >= 0 ? (lang === "gr" ? dayNamesGr[focusedDayIndex] : dayNames[focusedDayIndex]) : "";
  const focusedDateLabel = focusedDateObj
    ? focusedDateObj.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "";
  const deferredSchedule = useDeferredValue(schedule);
  const displayGanttData = useMemo(
    () =>
      enrichGanttDataWithDisplayedSchedule(sharedGanttData, {
        storeEmployees,
        weekSchedule: deferredSchedule,
        weekDates,
        dayKeyFn: dayKey,
        toLocalISOFn: toLocalISO,
        foxlineSlotByKey: foxlineSync?.slotByKey,
      }),
    [sharedGanttData, storeEmployees, deferredSchedule, weekDates, weekOffset, foxlineSync?.slotByKey],
  );
  const focusedGanttDayData = focusDay && displayGanttData?.days
    ? displayGanttData.days.find((d) => d.date === focusDay)
    : null;
  const sidebarOpenRange = (openingHours?.[focusedDayIndex >= 0 ? dayNames[focusedDayIndex] : ""] || "08:00-22:00").split("-");
  const sidebarOpenTime = sidebarOpenRange[0] || "08:00";
  const sidebarCloseTime = sidebarOpenRange[1] || "22:00";
  const toHour = (t) => {
    if (!t || typeof t !== "string") return 0;
    const [h, m] = t.split(":").map(Number);
    return Number(h || 0) + Number(m || 0) / 60;
  };
  const staffOpenOffset = (staffOpenOffsetMinutes || 60) / 60;
  const staffCloseOffset = (staffCloseOffsetMinutes || 60) / 60;
  const sidebarTStart = toHour(sidebarOpenTime || "08:00") - staffOpenOffset;
  const sidebarTEnd = toHour(sidebarCloseTime || "22:00") + staffCloseOffset;
  const sidebarTW = Math.max(1, sidebarTEnd - sidebarTStart);
  const sidebarOpenLinePct = `${((toHour(sidebarOpenTime) - sidebarTStart) / sidebarTW) * 100}%`;
  const sidebarCloseLinePct = `${((toHour(sidebarCloseTime) - sidebarTStart) / sidebarTW) * 100}%`;
  const sidebarLeftPct = (slot) =>
    timelineBarGeometry(slot?.start, slot?.end, sidebarTStart, sidebarTEnd).leftPct;
  const sidebarWidthPct = (slot) =>
    timelineBarGeometry(slot?.start, slot?.end, sidebarTStart, sidebarTEnd).widthPct;
  const sidebarRoleMeta = {
    supervisor: { fill: "var(--green)", tint: "var(--green-light)", label: "Sup" },
    service: { fill: "#3B82F6", tint: "#EFF6FF", label: "Srv" },
    boh: { fill: "#F59E0B", tint: "#FFFBEB", label: "BOH" },
  };
  const sidebarRoles = ["supervisor", "service", "boh"];
  const sidebarDayData = displayGanttData?.days?.find((d) => d.date === focusDay) || null;
  const focusedDayKey = useMemo(() => {
    if (!focusDay) return null;
    const idx = weekDates.findIndex((d) => toLocalISO(d) === focusDay);
    return idx >= 0 ? dayKey(weekDates[idx], idx) : null;
  }, [focusDay, weekDates, weekOffset]);
  const focusedScheduleDraftSlots = useMemo(
    () => {
      if (!focusPanelReady) return [];
      return draftSlotsFromDisplayedDay(
        storeEmployees,
        schedule,
        focusedDayKey,
        foxlineSync?.slotByKey,
        focusDay,
      );
    },
    [focusPanelReady, storeEmployees, schedule, focusedDayKey, focusDay, weekOffset, hasUnsavedChanges, foxlineSync?.slotByKey],
  );
  const sidebarDisplayDayData = useMemo(() => {
    if (!focusDay || !focusPanelReady) return null;
    const ganttDraft = (sidebarDayData?.draft_slots || []).filter((s) => !isDraftSlotPlaceholder(s));
    const mergedDraft = mergeSidebarDraftSlots(focusedScheduleDraftSlots, ganttDraft);
    const hasScheduleAssignments = focusedScheduleDraftSlots.length > 0;
    if (mergedDraft.length === 0 && !sidebarDayData && sharedGanttLoading) return null;
    if (sidebarDayData && hasScheduleAssignments) {
      return sidebarDayData;
    }
    return {
      ...(sidebarDayData || { date: focusDay }),
      draft_slots: mergedDraft,
      gap_hours: hasScheduleAssignments || !SHIFTIQ_ENABLED ? 0 : Number(sidebarDayData?.gap_hours || 0),
      missing_slots: SHIFTIQ_ENABLED && !hasScheduleAssignments ? (sidebarDayData?.missing_slots || []) : [],
      optimal_slots: SHIFTIQ_ENABLED ? (sidebarDayData?.optimal_slots || []) : [],
    };
  }, [focusDay, focusPanelReady, sidebarDayData, focusedScheduleDraftSlots, sharedGanttLoading]);
  const focusDays = displayGanttData?.days || [];
  const focusedDayListIndex = focusDay ? focusDays.findIndex((d) => d.date === focusDay) : -1;
  const canFocusPrevDay = focusedDayListIndex > 0;
  const canFocusNextDay = focusedDayListIndex >= 0 && focusedDayListIndex < focusDays.length - 1;
  const focusedDayNameKey = focusedDayIndex >= 0 ? dayNames[focusedDayIndex] : "";
  const focusedDayIsClosed = Boolean(focusedDayNameKey && openingHours?.[focusedDayNameKey] === "CLOSED");
  const focusedDraftSlots = focusedGanttDayData?.draft_slots || [];
  const focusedOptimalSlots = focusedGanttDayData?.optimal_slots || [];
  const focusedCoveredOptimalCount = !focusPanelReady ? 0 : focusedOptimalSlots.reduce((acc, opt) => {
    const optStart = timeToMins(opt?.start);
    let optEnd = timeToMins(opt?.end);
    if (optStart == null || optEnd == null) return acc;
    if (optEnd <= optStart) optEnd += 24 * 60;
    const optDuration = optEnd - optStart;
    if (!optDuration || optDuration <= 0) return acc;
    const matches = focusedDraftSlots.some((drf) => {
      if (ganttDisplayRoleKey(drf?.role) !== ganttDisplayRoleKey(opt?.role)) return false;
      const drfStart = timeToMins(drf?.start);
      let drfEnd = timeToMins(drf?.end);
      if (drfStart == null || drfEnd == null) return false;
      if (drfEnd <= drfStart) drfEnd += 24 * 60;
      const overlap = Math.max(0, Math.min(drfEnd, optEnd) - Math.max(drfStart, optStart));
      return overlap / optDuration >= 0.5;
    });
    return matches ? acc + 1 : acc;
  }, 0);
  const mobileScheduled = storeEmployees.filter(emp => {
    const v = scheduleRuleValue(rosterScheduleKey(emp), mobileDk);
    return v && v !== "OFF" && !SPECIAL_STATUSES.find(s => s.value === v);
  }).length;
  const mobileUnassigned = storeEmployees.filter(emp => !scheduleRuleValue(rosterScheduleKey(emp), mobileDk)).length;

  const belowTargetHours = (() => {
    if (!hourlyTargets) return 0;
    let count = 0;
    const groupKeys = ["supervisor", "service", "kitchen", "cleaning"];
    weekDates.forEach((d, i) => {
      const dateStr = toLocalISO(d);
      const dk = dayKey(d, i);
      const openRange = openingHours[dayNames[i]];
      let openH = 0;
      let closeH = 24;
      if (openRange && openRange !== "CLOSED" && openRange.includes("-")) {
        const [openS, closeS] = openRange.split("-");
        openH = parseInt((openS || "0").split(":")[0], 10);
        closeH = parseInt((closeS || "24").split(":")[0], 10);
        if (closeH <= openH) closeH += 24;
      }
      Object.keys(hourlyTargets).forEach((hourKey) => {
        const hour = parseInt(hourKey, 10);
        if (Number.isNaN(hour)) return;
        const hourAdj = hour < openH ? hour + 24 : hour;
        if (hourAdj < openH || hourAdj >= closeH) return;
        const target = hourlyTargets?.[hourKey]?.[dateStr] || {};
        const hasAnyTarget = groupKeys.some((g) => Number(target[g] ?? 0) > 0);
        if (!hasAnyTarget) return;
        let under = false;
        for (const g of groupKeys) {
          const recommended = Number(target[g] ?? 0);
          if (recommended <= 0) continue;
          const scheduled = storeEmployees.filter((emp) => {
            if ((emp.group || "service") !== g) return false;
            const shift = parseShiftMins(scheduleRuleValue(rosterScheduleKey(emp), dk));
            if (!shift) return false;
            return shift.start <= hour * 60 && hour * 60 < shift.end;
          }).length;
          if (scheduled < recommended) {
            under = true;
            break;
          }
        }
        if (under) count += 1;
      });
    });
    return count;
  })();
  const scheduleAlertDigest = useMemo(
    () =>
      buildScheduleAlertDigest({
        weekDates,
        dayNames,
        dayNamesGr,
        toLocalISO,
        fmtDate,
        criticalAlerts,
        warningAlerts,
        serverViolations,
        showWarnings,
        hasShifts: anyShiftAssigned,
        lang,
        incompleteShiftEmployeeCount,
      }),
    [weekDates, dayNames, dayNamesGr, criticalAlerts, warningAlerts, serverViolations, showWarnings, anyShiftAssigned, lang, weekOffset, incompleteShiftEmployeeCount]
  );
  const criticalCount = scheduleAlertDigest.criticalCount;
  const warningCount = scheduleAlertDigest.warningCount;
  const totalIssueCount = scheduleAlertDigest.totalIssueCount;
  const blockingIssueCount = criticalAlerts.length + warningAlerts.length;
  const hasBlockingIssues = blockingIssueCount > 0;
  const canOverrideSaveBlock = ["admin", "brand_manager", "ops_manager", "store_manager", "manager"].includes(user.role);

  const toggleScheduleAlertDay = useCallback((dateIso) => {
    setExpandedScheduleAlertDays((prev) => ({ ...prev, [dateIso]: !prev[dateIso] }));
  }, []);

  function serverViolationOverrideKey(v) {
    return `${v.rule_code}|${v.date}`;
  }

  const NON_OVERRIDABLE_RULE_CODES = new Set(["REST_VIOLATION", "WEEKLY_MAX_48H"]);

  function normalizeScheduleViolations(violations) {
    return (violations || []).map((v) => {
      const ruleCode = String(v?.rule_code || "");
      const raw = v?.can_override;
      let can_override = true;
      if (raw === false || raw === "false" || raw === 0) can_override = false;
      else if (raw === true || raw === "true") can_override = true;
      if (NON_OVERRIDABLE_RULE_CODES.has(ruleCode)) can_override = false;
      return {
        ...v,
        severity: String(v?.severity || "").toLowerCase(),
        can_override,
      };
    });
  }

  function isScheduleViolationLegal(v) {
    if (String(v?.severity || "").toLowerCase() !== "blocking") return false;
    if (v?.can_override === false) return true;
    return NON_OVERRIDABLE_RULE_CODES.has(String(v?.rule_code || ""));
  }

  function isScheduleViolationOverridableBlocking(v) {
    return String(v?.severity || "").toLowerCase() === "blocking" && !isScheduleViolationLegal(v);
  }

  function isClientScheduleViolation(v) {
    return String(v?.source || "") === "client" || String(v?.rule_code || "").startsWith("CLIENT_");
  }

  function buildClientAlertViolations() {
    const items = [];
    criticalAlerts.forEach((al, idx) => {
      const di = dayNames.indexOf(al.day);
      const dateIso = di >= 0 && weekDates[di] ? toLocalISO(weekDates[di]) : "";
      items.push({
        rule_code: `CLIENT_${al.type || "alert"}_${idx}`,
        date: dateIso,
        description: lang === "gr" ? al.msgGr || al.msg : al.msg,
        severity: "blocking",
        can_override: true,
        source: "client",
      });
    });
    if (showWarnings) {
      warningAlerts.forEach((al, idx) => {
        const di = dayNames.indexOf(al.day);
        const dateIso = di >= 0 && weekDates[di] ? toLocalISO(weekDates[di]) : "";
        items.push({
          rule_code: `CLIENT_WARN_${al.type || "warn"}_${idx}`,
          date: dateIso,
          description: lang === "gr" ? al.msgGr || al.msg : al.msg,
          severity: "warning",
          can_override: true,
          source: "client",
        });
      });
    }
    return normalizeScheduleViolations(items);
  }

  function ladderPriorAlertNotes() {
    const week = ladderWeek || {};
    const rows = [];
    if (week.t1_alert_note) {
      rows.push({
        tier: "T1",
        by: week.submitted_by_name,
        text: week.t1_alert_note,
      });
    }
    if (week.t2_alert_note) {
      rows.push({
        tier: "T2",
        by: week.t2_approved_by_name,
        text: week.t2_alert_note,
      });
    }
    if (week.t3_alert_note) {
      rows.push({
        tier: "T3",
        by: week.t3_approved_by_name,
        text: week.t3_alert_note,
      });
    }
    return rows;
  }

  function fetchWeekRuleOverrides() {
    if (!store || store === "All Stores" || !weekStartISO) {
      return Promise.resolve([]);
    }
    return fetch(
      `${SHIFTIQ_API}/schedule/overrides?store_id=${encodeURIComponent(store)}&week_start=${encodeURIComponent(weekStartISO)}`,
      { headers: getAuthHeaders() }
    )
      .then((r) => (r.ok ? r.json() : { overrides: [] }))
      .then((body) => (Array.isArray(body?.overrides) ? body.overrides : []))
      .catch(() => []);
  }

  function buildLadderAlertComment(violations, reasons) {
    const lines = [];
    (violations || []).forEach((v) => {
      const key = serverViolationOverrideKey(v);
      const reason = String(reasons[key] || "").trim();
      if (!reason) return;
      const when = v.date ? `${v.date}: ` : "";
      lines.push(`${when}${v.description}\n→ ${reason}`);
    });
    return lines.join("\n\n").trim();
  }

  /** Prefill reason inputs + who originally set that reason (unchanged T2 keeps T1 attribution). */
  function seedOverrideReasons(violations, overrides) {
    const byKey = {};
    const byDate = {};
    (overrides || []).forEach((o) => {
      const rule = String(o?.rule_code || "").trim();
      const dateIso = String(o?.date || "").trim();
      const reason = String(o?.override_reason || "").trim();
      const by = String(o?.overridden_by || "").trim();
      if (!rule || !reason) return;
      const key = `${rule}|${dateIso}`;
      byKey[key] = { reason, by };
      if (dateIso) {
        if (!byDate[dateIso]) byDate[dateIso] = [];
        byDate[dateIso].push({ reason, by });
      }
    });
    const seeded = {};
    const authors = {};
    (violations || []).forEach((v) => {
      if (!isScheduleViolationOverridableBlocking(v)) return;
      const key = serverViolationOverrideKey(v);
      if (byKey[key]) {
        seeded[key] = byKey[key].reason;
        if (byKey[key].by) authors[key] = byKey[key].by;
        return;
      }
      const dateIso = String(v?.date || "").trim();
      const dateHits = dateIso ? byDate[dateIso] || [] : [];
      if (dateHits.length > 0) {
        seeded[key] = dateHits[0].reason;
        if (dateHits[0].by) authors[key] = dateHits[0].by;
      }
    });
    return { reasons: seeded, authors };
  }

  function computeDayColumnValidationStatus(violations) {
    const byDate = {};
    (violations || []).forEach((v) => {
      const d = v.date;
      if (!d) return;
      if (v.severity === "blocking") byDate[d] = "blocking";
      else if (v.severity === "warning" && byDate[d] !== "blocking") byDate[d] = "warning";
    });
    return byDate;
  }

  function serverViolationsToAckItems(violations) {
    return (violations || []).map((v, idx) => ({
      key: `server-warn-${v.rule_code}-${v.date}-${idx}`,
      text: `${v.date}: ${v.description}`,
    }));
  }

  const runProactiveValidation = useCallback(() => {
    if (!store || store === "All Stores") return;
    const currentSched = scheduleByWeek[weekOffset] || {};
    const hasAnyShifts = Object.values(currentSched).some((empSched) =>
      Object.values(empSched || {}).some(
        (val) => val && val !== "" && val !== "OFF"
      )
    );
    if (!hasAnyShifts) {
      setServerViolations([]);
      setDayColumnValidationStatus({});
      return;
    }
    const requestId = proactiveValidationRequestRef.current + 1;
    proactiveValidationRequestRef.current = requestId;
    fetch(`${SHIFTIQ_API}/schedule/validate`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        store_id: store,
        week_start: weekStartISO,
      }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (requestId !== proactiveValidationRequestRef.current) return;
        const violations = Array.isArray(data?.violations) ? data.violations : [];
        setServerViolations(normalizeScheduleViolations(violations));
        setDayColumnValidationStatus(computeDayColumnValidationStatus(violations));
      })
      .catch(() => {});
  }, [store, weekStartISO, scheduleByWeek, weekOffset]);

  const scheduleProactiveValidation = useCallback(
    (debounceMs = 0) => {
      if (proactiveValidationTimerRef.current) {
        clearTimeout(proactiveValidationTimerRef.current);
        proactiveValidationTimerRef.current = null;
      }
      if (debounceMs > 0) {
        proactiveValidationTimerRef.current = setTimeout(() => {
          proactiveValidationTimerRef.current = null;
          runProactiveValidation();
        }, debounceMs);
        return;
      }
      runProactiveValidation();
    },
    [runProactiveValidation]
  );

  scheduleProactiveValidationRef.current = scheduleProactiveValidation;

  useEffect(() => {
    if (scheduleLoading || !store || store === "All Stores" || !liveStaff?.length) return;
    scheduleProactiveValidation();
  }, [scheduleLoading, store, weekStartISO, weekOffset, liveStaff, scheduleProactiveValidation]);

  function buildPostSaveAcknowledgementItems(extraItems = []) {
    const items = [...(extraItems || [])];
    criticalAlerts.forEach((al, idx) => {
      items.push({ key: `crit-${idx}`, text: lang === "gr" ? al.msgGr : al.msg });
    });
    if (showWarnings) {
      warningAlerts.forEach((al, idx) => {
        items.push({ key: `warn-${idx}`, text: lang === "gr" ? al.msgGr : al.msg });
      });
    }
    (sharedGanttData?.days || []).forEach((day) => {
      const missing = day.missing_slots || [];
      if (missing.length > 0) {
        items.push({
          key: `gantt-missing-${day.date}`,
          text:
            lang === "gr"
              ? `${day.day_name || day.date}: ${missing.length} ακάλυπτες θέσεις`
              : `${day.day_name || day.date}: ${missing.length} uncovered slot(s)`,
        });
      }
      const gapHours = Number(day.gap_hours || 0);
      if (gapHours > 0) {
        items.push({
          key: `gantt-gap-${day.date}`,
          text:
            lang === "gr"
              ? `${day.day_name || day.date}: ${gapHours}h κενό κάλυψης`
              : `${day.day_name || day.date}: ${gapHours}h coverage gap`,
        });
      }
    });
    incomingLoans.forEach((loan) => {
      if (loanShowsShiftAtHost(loan)) return;
      const name = resolveEmployeeName(loan.employee_name || loan.employee_code, liveStaff || []);
      const dateLabel = String(loan.loan_date || "").slice(0, 10);
      items.push({
        key: `loan-${loan.id}`,
        text:
          lang === "gr"
            ? `Δανεισμός εκκρεμεί έγκριση: ${name} από ${loan.home_store} (${dateLabel})`
            : `Pending cross-store loan: ${name} from ${loan.home_store} (${dateLabel})`,
      });
    });
    return items;
  }

  function completePostSaveAcknowledgement() {
    setPostSaveAckOpen(false);
    setPostSaveAckItems([]);
  }

  function triggerPostSaveGuidedFlow(extraAckItems = []) {
    const items = buildPostSaveAcknowledgementItems(extraAckItems);
    if (items.length > 0) {
      setPostSaveAckItems(items);
      setPostSaveAckOpen(true);
    }
  }

  const weekRangeLabel = `${fmtDate(weekDates[0])} – ${fmtDate(weekDates[6])}`;

  const applyStoreSwitch = useCallback((targetStore) => {
    setStore(targetStore);
    rememberStoreByName(targetStore, storeRecords);
    closeStoreSwitcher();
  }, [closeStoreSwitcher, storeRecords]);

  const applyWeekOffsetChange = useCallback((nextOffset) => {
    setWeekOffset(nextOffset);
  }, []);

  const requestStoreSwitch = useCallback(
    (targetStore) => {
      if (targetStore === store) {
        closeStoreSwitcher();
        return;
      }
      if (hasUnsavedChanges) {
        setUnsavedNavConfirm({ kind: "store", targetStore });
        return;
      }
      applyStoreSwitch(targetStore);
    },
    [store, hasUnsavedChanges, applyStoreSwitch, closeStoreSwitcher],
  );

  const requestWeekOffsetChange = useCallback(
    (delta) => {
      if (delta < 0 && weekStartISO <= SCHEDULE_GO_LIVE_WEEK) return;
      const nextOffset = weekOffset + delta;
      if (hasUnsavedChanges) {
        setUnsavedNavConfirm({ kind: "week", nextOffset });
        return;
      }
      applyWeekOffsetChange(nextOffset);
    },
    [weekOffset, weekStartISO, hasUnsavedChanges, applyWeekOffsetChange],
  );

  const dismissUnsavedNavConfirm = useCallback(() => {
    const kind = unsavedNavConfirm?.kind;
    setUnsavedNavConfirm(null);
    if (kind === "store") closeStoreSwitcher();
  }, [unsavedNavConfirm, closeStoreSwitcher]);

  const confirmUnsavedNavDiscard = useCallback(() => {
    const pending = unsavedNavConfirm;
    if (!pending) return;
    setUnsavedNavConfirm(null);
    markScheduleSaved();
    if (pending.kind === "store") applyStoreSwitch(pending.targetStore);
    else if (pending.kind === "week") applyWeekOffsetChange(pending.nextOffset);
    else if (pending.kind === "leave" && typeof pending.proceed === "function") pending.proceed();
  }, [unsavedNavConfirm, applyStoreSwitch, applyWeekOffsetChange, markScheduleSaved]);

  useEffect(() => {
    return setLeaveGuard((proceed, onBlocked) => {
      if (!hasUnsavedChangesRef.current) {
        proceed();
        return;
      }
      if (typeof onBlocked === "function") onBlocked();
      setUnsavedNavConfirm({ kind: "leave", proceed });
    });
  }, []);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges]);

  const validPeopleCostPcts = builderPeopleCostPctByDay.filter((v) => v != null && Number.isFinite(v));
  const weekAvgPeopleCostPct = validPeopleCostPcts.length
    ? validPeopleCostPcts.reduce((a, b) => a + b, 0) / validPeopleCostPcts.length
    : null;
  const storeSwitcherButtonDot =
    criticalCount > 0
      ? "#EF4444"
      : weekAvgPeopleCostPct == null || !Number.isFinite(weekAvgPeopleCostPct)
        ? "var(--text-muted)"
        : weekAvgPeopleCostPct > 30
          ? "#EF4444"
          : weekAvgPeopleCostPct >= 22
            ? "#F59E0B"
            : "#16A34A";

  const openWarningsPanelFromBadge = (e) => {
    e.stopPropagation();
    setShowAlerts((v) => !v);
  };
  const handleSidebarDragStart = (e) => {
    if (sidebarFullscreen) return;
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = sidebarWidth;
    const onMove = (moveEvent) => {
      const delta = startX - moveEvent.clientX;
      const newWidth = Math.min(Math.max(startWidth + delta, 300), window.innerWidth * 0.7);
      setSidebarWidth(newWidth);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleNameColDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = nameColWidth;
    const onMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.min(
        Math.max(startWidth + delta, SCHEDULE_NAME_COL_MIN),
        SCHEDULE_NAME_COL_MAX,
      );
      setNameColWidth(newWidth);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const scheduleTableMinWidth = nameColWidth + weekDates.length * 110;

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (dayHeaderMenuDate && !e.target.closest(".day-header-menu")) {
        setDayHeaderMenuDate(null);
      }
      if (!e.target.closest(".week-actions-menu")) {
        setWeekActionsMenuOpen(false);
      }
      if (scheduleCellContextMenu && !e.target.closest(".schedule-cell-context-menu")) {
        closeScheduleCellContextMenu();
      }
      if (pasteReplaceConfirm && !e.target.closest(".schedule-paste-replace-confirm")) {
        setPasteReplaceConfirm(null);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [dayHeaderMenuDate, scheduleCellContextMenu, pasteReplaceConfirm]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (isPastWeek) return;
      const tag = String(e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;

      if (e.key === "Escape") {
        if (copiedShift) {
          e.preventDefault();
          clearCopiedShiftMode();
        }
        return;
      }

      if (!scheduleCellFocus) return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const { empId, dk } = scheduleCellFocus;
      const focusEmp = storeEmployees.find((emp) => rosterScheduleKey(emp) === empId);
      const cellVal = getCell(empId, dk);

      if (e.key === "c" || e.key === "C") {
        if (!isScheduleShiftValue(cellVal) || !focusEmp) return;
        e.preventDefault();
        copyShiftFromCell(empId, dk, cellVal, focusEmp.group || "service");
      } else if (e.key === "v" || e.key === "V") {
        if (!copiedShift || !focusEmp) return;
        e.preventDefault();
        handlePasteShiftClick(focusEmp, dk);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    isPastWeek,
    copiedShift,
    scheduleCellFocus,
    storeEmployees,
    schedule,
    weekOffset,
    weekDates,
    adminScheduleOverride,
  ]);

  useEffect(() => {
    const onResize = () => {
      setFocusViewportWidth(window.innerWidth);
      setIsWideFocusLayout(window.innerWidth > 1200);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onEsc = (e) => {
      if ((e.key === "d" || e.key === "D") && !e.ctrlKey && !e.metaKey) {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag === "input" || tag === "textarea" || tag === "select") return;
        if (focusDay) return;
        const targetDay = hoveredDayDate || weekDayDates[0];
        if (targetDay) {
          setFocusPanelReady(false);
          setFocusDay(targetDay);
        }
        return;
      }
      if (e.key !== "Escape") return;
      if (unsavedNavConfirm) {
        dismissUnsavedNavConfirm();
        return;
      }
      if (storeSwitcherOpenRef.current) {
        closeStoreSwitcher();
        return;
      }
      if (sidebarFullscreen) {
        setSidebarFullscreen(false);
        return;
      }
      setFocusDay(null);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [sidebarFullscreen, focusDay, hoveredDayDate, weekDayDates, closeStoreSwitcher, unsavedNavConfirm, dismissUnsavedNavConfirm]);

  useEffect(() => {
    if (!focusDay) return undefined;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setFocusPanelReady(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [focusDay]);

  useEffect(() => {
    if (!focusDay) return undefined;
    const onArrowNav = (e) => {
      if (storeSwitcherOpenRef.current) return;
      if (e.key === "ArrowLeft" && canFocusPrevDay) {
        e.preventDefault();
        setFocusPanelReady(false);
        setFocusDay(focusDays[focusedDayListIndex - 1]?.date || null);
      } else if (e.key === "ArrowRight" && canFocusNextDay) {
        e.preventDefault();
        setFocusPanelReady(false);
        setFocusDay(focusDays[focusedDayListIndex + 1]?.date || null);
      }
    };
    window.addEventListener("keydown", onArrowNav);
    return () => window.removeEventListener("keydown", onArrowNav);
  }, [focusDay, canFocusPrevDay, canFocusNextDay, focusDays, focusedDayListIndex]);

  useEffect(() => {
    if (vsOptimalActive) setFocusDay(null);
  }, [vsOptimalActive]);

  useEffect(() => {
    if (!focusDay) return;
    setSidebarRoleFilter("all");
  }, [focusDay]);

  useEffect(() => {
    if (!focusDay && sidebarFullscreen) {
      setSidebarFullscreen(false);
    }
  }, [focusDay, sidebarFullscreen]);

  useEffect(() => {
    try { localStorage.setItem("pg_sidebar_width", String(sidebarWidth)); }
    catch {}
  }, [sidebarWidth]);

  useEffect(() => {
    try { localStorage.setItem("pg_schedule_name_col_width", String(nameColWidth)); }
    catch {}
  }, [nameColWidth]);

  useEffect(() => {
    try { localStorage.setItem("pg_sidebar_fullscreen", String(sidebarFullscreen)); }
    catch {}
  }, [sidebarFullscreen]);

  useEffect(() => {
    if (timelineActive) setFocusDay(null);
  }, [timelineActive]);

  useEffect(() => {
    if (timelineActive) window.scrollTo({ top: 0, behavior: "instant" });
  }, [timelineActive]);

  useEffect(() => {
    return () => {
      if (assignmentLimitWarningTimeoutRef.current) {
        clearTimeout(assignmentLimitWarningTimeoutRef.current);
      }
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
      if (proactiveValidationTimerRef.current) {
        clearTimeout(proactiveValidationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => setToast(null), scheduleToastDurationMs(toast));
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, [toast]);

  useEffect(() => {
    if (!draftSavedToast) return undefined;
    const t = setTimeout(() => setDraftSavedToast(null), 3000);
    return () => clearTimeout(t);
  }, [draftSavedToast]);

  useEffect(() => {
    if (!hrToast) return undefined;
    const t = setTimeout(() => setHrToast(null), 3500);
    return () => clearTimeout(t);
  }, [hrToast]);

  // Re-fetch week approval when session identity changes (impersonation start/stop).
  const ladderIdentityKey = `${user?.id || ""}|${user?.role || ""}|${user?.impersonated ? 1 : 0}|${user?.is_super_admin ? 1 : 0}|${user?.can_impersonate ? 1 : 0}`;
  const ladderIdentityRef = useRef(ladderIdentityKey);
  useEffect(() => {
    if (ladderIdentityRef.current === ladderIdentityKey) return;
    ladderIdentityRef.current = ladderIdentityKey;
    setNeedsHrRenotify(false);
    setLadderRefreshKey((k) => k + 1);
  }, [ladderIdentityKey]);

  function persistScheduleCore(extraAckItems = [], options = {}) {
    if (!ladderAllowsEdit) return Promise.resolve({ ok: false, reason: "locked" });
    const { chainPublish = false, skipPostSaveFlow = false } = options;
    const persistEmployees = storeEmployees || [];
    const currentSched = scheduleByWeek[weekOffset] || {};
    const rows = [];

    persistEmployees.forEach((emp) => {
      const empSched = currentSched[rosterScheduleKey(emp)] || {};
      weekDates.forEach((d, i) => {
        const dk = dayKey(d, i);
        const val = empSched[dk];
        if (val === undefined || val === null) return;
        rows.push({
          store_id: emp.rota_store_id || store,
          rota_week_start: weekStartISO,
          rota_date: toLocalISO(d),
          employee_code: rosterScheduleKey(emp),
          role: emp.group || "service",
          shift_value: val || "",
          status: "draft",
        });
      });
    });

    const weekStartStr = weekStartISO;
    const changeItems = [];
    persistEmployees.forEach((emp) => {
      weekDates.forEach((d, i) => {
        const dk = dayKey(d, i);
        const rawOld = lastPersistedScheduleRef.current[`${store}:${weekStartISO}`]?.[rosterScheduleKey(emp)]?.[dk];
        const rawNew = currentSched[rosterScheduleKey(emp)]?.[dk];
        const normOld = rawOld == null || rawOld === "" ? null : String(rawOld);
        const normNew = rawNew == null || rawNew === "" ? null : String(rawNew);
        if (normOld === normNew) return;
        let changeType = "modified";
        if (!normOld && normNew) changeType = "assigned";
        else if (normOld && !normNew) changeType = "removed";
        const dkApi = String(dayNames[i] || "mon").toLowerCase().slice(0, 3);
        changeItems.push({
          store_name: store,
          week_start: weekStartStr,
          employee_id: emp.id,
          employee_code: rosterScheduleKey(emp),
          employee_name: emp.name,
          day_key: dkApi,
          change_type: changeType,
          old_value: normOld,
          new_value: normNew,
          changed_by_name: user.name,
          changed_by_role: user.role,
          source: "local",
          rota_date: toLocalISO(d),
        });
      });
    });

    const finishDraftSave = () => {
      setSaveState("saved");
      setSaved(true);
      markScheduleSaved();
      setWeekSavedOnce(true);
      setSaveOverrideOpen(false);
      setSaveOverrideAck(false);
      setServerValidationModalOpen(false);
      setServerOverrideReasons({});
      if (
        changeItems.length > 0
        && (ladderStatus === "pending_hr" || ladderStatus === "post_pending_hr")
      ) {
        setNeedsHrRenotify(true);
      }
      setTimeout(() => setSaveState("idle"), 2500);

      setTimeout(() => {
        changeItems.forEach((item) => scheduleLogChangeSilent(item));
      }, 150);
      foxlineSync.recordLocalEdits(changeItems);
      lastPersistedScheduleRef.current[`${store}:${weekStartISO}`] = JSON.parse(JSON.stringify(currentSched));
      setPersistedCellStatusByWeek((prev) => {
        const base = { ...(prev[weekOffset] || {}) };
        Object.keys(currentSched).forEach((empKey) => {
          if (!base[empKey]) base[empKey] = {};
          Object.keys(currentSched[empKey] || {}).forEach((dk) => {
            base[empKey][dk] = "draft";
          });
        });
        return { ...prev, [weekOffset]: base };
      });
      const savedAt = new Date();
      setDraftSavedAt(savedAt);
      setDraftSavedToast(
        lang === "gr"
          ? `Προσχέδιο αποθηκεύτηκε ${savedAt.toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" })}`
          : `Draft saved ${savedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
      );
      foxlineSync.reload();
      window.dispatchEvent(
        new CustomEvent("rota-updated", {
          detail: { store_id: store, week_start: weekStartStr, refreshGantt: true, skipDirty: true },
        })
      );
      scheduleProactiveValidation();
      if (chainPublish) {
        publishScheduleCore();
      } else if (!skipPostSaveFlow) {
        triggerPostSaveGuidedFlow(extraAckItems);
      }
    };

    if (rows.length === 0) {
      if (changeItems.length === 0) return Promise.resolve({ ok: true, empty: true });
      setSaveState("saving");
      finishDraftSave();
      return Promise.resolve({ ok: true });
    }

    setSaveState("saving");
    return fetch(`${SHIFTIQ_API}/schedule`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(rows),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (data?.saved >= 0) {
          finishDraftSave();
          return { ok: true };
        }
        setSaveState("error");
        setTimeout(() => setSaveState("idle"), 3000);
        return { ok: false };
      })
      .catch(() => {
        setSaveState("error");
        setTimeout(() => setSaveState("idle"), 3000);
        setToast(
          lang === "gr"
            ? "Αποτυχία αποθήκευσης — δοκιμάστε ξανά"
            : "Save failed — try again"
        );
        return { ok: false };
      });
  }

  /** SoftOne writes: Internal must be saved first (checks → save → SoftOne). */
  async function runSoftoneAfterInternalSaved(action) {
    if (!canFoxlinePush || foxlineSync.readOnly) return;
    if (!softoneLadderReady) {
      setToast(
        lang === "gr"
          ? "Η αποστολή στο SoftOne είναι διαθέσιμη μόνο μετά την έγκριση T3 (Αναμονή ανεβάσματος HR)"
          : "SoftOne upload is only available after T3 approval (Pending HR upload)"
      );
      return;
    }
    if (hasUnsavedChanges || !weekSavedOnce) {
      if (!ladderAllowsEdit) {
        setToast(
          lang === "gr"
            ? "Αποθηκεύστε πρώτα το Εσωτερικό πρόγραμμα πριν την αποστολή στο SoftOne"
            : "Save the Internal schedule first before SoftOne upload"
        );
        return;
      }
      const saved = await persistScheduleCore([], { skipPostSaveFlow: true });
      if (!saved?.ok) return;
      // Let SoftOne classifier pick up the saved Internal grid.
      await new Promise((r) => setTimeout(r, 50));
    }
    try {
      await action();
    } catch {
      /* SoftOne hook surfaces sync.error */
    }
  }

  function publishScheduleCore() {
    if (!store || store === "All Stores") return;
    const pendingAction = pendingLadderActionRef.current;
    const ladderAdvance = Boolean(pendingAction && LADDER_ENDPOINTS[pendingAction]);
    setPublishState("publishing");
    const url = ladderAdvance
      ? `${SHIFTIQ_API}${LADDER_ENDPOINTS[pendingAction]}`
      : `${SHIFTIQ_API}/schedule/publish`;
    fetch(url, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        store_id: store,
        week_start: weekStartISO,
        unified_location_id: fetchLocationId,
        ...(ladderAdvance && pendingLadderCommentRef.current
          ? { comment: pendingLadderCommentRef.current }
          : {}),
      }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        const nested = data?.detail && typeof data.detail === "object" ? data.detail : data;
        if (r.status === 403 || r.status === 400) {
          const legal = Array.isArray(nested?.legal_violations) ? nested.legal_violations : [];
          const violations = Array.isArray(nested?.violations) ? nested.violations : legal;
          setPublishState("idle");
          if (legal.length || violations.length) {
            // Keep any override reasons the user already typed — only legal blocks ladder.
            setServerViolations(normalizeScheduleViolations(violations.length ? violations : legal));
            setServerValidationModalOpen(true);
          }
          setToast(
            (typeof data?.detail === "string" ? data.detail : nested?.detail) ||
              (lang === "gr"
                ? "Δεν μπορεί να προχωρήσει — διορθώστε πρώτα τις παραβιάσεις κανόνων"
                : "Cannot advance — fix requirement violations first")
          );
          return null;
        }
        if (!r.ok) {
          return Promise.reject(r.status);
        }
        return data;
      })
      .then((data) => {
        if (!data) return;
        const violations = Array.isArray(data?.violations) ? data.violations : [];
        const blocking = violations.filter((v) => v.severity === "blocking");
        if (blocking.length > 0) {
          setPublishState("idle");
          setServerViolations(normalizeScheduleViolations(violations));
          setServerOverrideReasons({});
          setServerValidationModalOpen(true);
          return;
        }
        setPublishState("idle");
        setDraftSavedAt(null);
        setServerValidationModalOpen(false);
        setServerOverrideReasons({});
        const publishedSched = scheduleByWeek[weekOffset] || {};
        setPersistedCellStatusByWeek((prev) => {
          const base = { ...(prev[weekOffset] || {}) };
          Object.keys(publishedSched).forEach((empKey) => {
            if (!base[empKey]) base[empKey] = {};
            Object.keys(publishedSched[empKey] || {}).forEach((dk) => {
              base[empKey][dk] = "published";
            });
          });
          return { ...prev, [weekOffset]: base };
        });
        fetchWeeklyScheduleGrid();
        scheduleProactiveValidation();
        foxlineSync.reload();
        if (ladderAdvance) {
          pendingLadderActionRef.current = null;
          pendingLadderCommentRef.current = null;
          if (pendingAction === "renotify_hr" || pendingAction === "approve_t3") {
            setNeedsHrRenotify(false);
          }
          applyLadderActionResult(pendingAction, data?.week);
          const sent = Boolean(data?.notify?.sent);
          const label = ladderActionLabel(pendingAction, lang);
          setToast(
            lang === "gr"
              ? sent
                ? `Ολοκληρώθηκε: ${label}`
                : `Ολοκληρώθηκε: ${label} — η ειδοποίηση email δεν στάλθηκε`
              : sent
                ? `Done: ${label}`
                : `Done: ${label} — email notify did not send`
          );
          return;
        }
        const publishedCount = Number(data?.published_count ?? 0);
        if (publishedCount > 0) {
          setToast(
            lang === "gr"
              ? "Το πρόγραμμα δημοσιεύτηκε εσωτερικά — στείλτε το στο T2 από τη γραμμή κατάστασης"
              : "Schedule published internally — notify T2 from the week status bar"
          );
        } else {
          setToast({
            message:
              lang === "gr"
                ? "Δεν υπάρχουν βάρδιες προς δημοσίευση αυτή την εβδομάδα"
                : "No shifts to publish this week",
            tone: "info",
          });
        }
      })
      .catch(() => {
        setPublishState("idle");
        pendingLadderActionRef.current = null;
        setToast(
          lang === "gr"
            ? "Αποτυχία δημοσίευσης — δοκιμάστε ξανά"
            : "Publish failed — try again"
        );
      });
  }

  function beginServerValidationThenPublish() {
    setValidationIntent("publish");
    setPublishState("validating");
    const ladderAdvance = Boolean(pendingLadderActionRef.current && LADDER_ENDPOINTS[pendingLadderActionRef.current]);
    const overridesPromise = ladderAdvance ? fetchWeekRuleOverrides() : Promise.resolve([]);
    Promise.all([
      fetch(`${SHIFTIQ_API}/schedule/validate`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          store_id: store,
          week_start: weekStartISO,
        }),
      }).then((r) => (r.ok ? r.json() : Promise.reject(r.status))),
      overridesPromise,
    ])
      .then(([data, overrides]) => {
        const violations = Array.isArray(data?.violations) ? data.violations : [];
        setDayColumnValidationStatus(computeDayColumnValidationStatus(violations));
        let combined = normalizeScheduleViolations(violations);
        if (ladderAdvance) {
          combined = [...combined, ...buildClientAlertViolations()];
        }
        const blocking = combined.filter((v) => v.severity === "blocking");
        const warnings = combined.filter((v) => v.severity === "warning");
        const priorNotes = ladderAdvance ? ladderPriorAlertNotes() : [];
        const priorOverrides = Array.isArray(overrides) ? overrides : [];

        // Ladder uses the Schedule Rules modal (with per-item explanations).
        if (blocking.length > 0 || (ladderAdvance && (warnings.length > 0 || priorNotes.length > 0))) {
          setPublishState("idle");
          setServerViolations(combined);
          if (ladderAdvance) {
            const seeded = seedOverrideReasons(combined, priorOverrides);
            seededOverrideReasonsRef.current = { ...seeded.reasons };
            seededOverrideAuthorsRef.current = { ...seeded.authors };
            setServerOverrideReasons(seeded.reasons);
            setServerOverrideAuthors(seeded.authors);
          } else {
            seededOverrideReasonsRef.current = {};
            seededOverrideAuthorsRef.current = {};
            setServerOverrideReasons({});
            setServerOverrideAuthors({});
          }
          setServerValidationModalOpen(true);
          return;
        }

        const warningAckItems = serverViolationsToAckItems(warnings);
        if (hasUnsavedChanges) {
          persistScheduleCore(warningAckItems, { chainPublish: true, skipPostSaveFlow: true });
        } else {
          publishScheduleCore();
        }
      })
      .catch(() => {
        setPublishState("idle");
        setToast(
          lang === "gr"
            ? "Αποτυχία ελέγχου κανόνων — δοκιμάστε ξανά"
            : "Schedule rules check failed — try again"
        );
      });
  }

  function handleSaveWithServerOverrides() {
    const warnings = serverViolations.filter((v) => String(v?.severity || "").toLowerCase() === "warning");
    const hasLegalViolations = serverViolations.some(isScheduleViolationLegal);
    const overridableBlocking = serverViolations.filter(isScheduleViolationOverridableBlocking);
    const hasAnyBlockingViolation = serverViolations.some(
      (v) => String(v?.severity || "").toLowerCase() === "blocking"
    );
    const allOverridableReasonsFilledIn =
      overridableBlocking.length === 0
        ? !hasAnyBlockingViolation
        : overridableBlocking.every((v) => {
            const key = serverViolationOverrideKey(v);
            return String(serverOverrideReasons[key] || "").trim().length > 0;
          });
    if (hasLegalViolations || !allOverridableReasonsFilledIn) {
      return;
    }

    const serverBlocking = overridableBlocking.filter((v) => !isClientScheduleViolation(v));
    const clientBlocking = overridableBlocking.filter((v) => isClientScheduleViolation(v) && v.date);
    const ladderAdvance = Boolean(pendingLadderActionRef.current && LADDER_ENDPOINTS[pendingLadderActionRef.current]);
    if (ladderAdvance) {
      pendingLadderCommentRef.current = buildLadderAlertComment(overridableBlocking, serverOverrideReasons);
    }

    setSaveState("validating");
    const overridePosts = [...serverBlocking, ...(ladderAdvance ? clientBlocking : [])].filter((v) => {
      const key = serverViolationOverrideKey(v);
      const next = String(serverOverrideReasons[key] || "").trim();
      const prev = String(seededOverrideReasonsRef.current[key] || "").trim();
      // Unchanged reason — keep original author; do not insert a duplicate override row.
      return Boolean(next) && next !== prev;
    });
    Promise.all(
      overridePosts.map((v) =>
        fetch(`${SHIFTIQ_API}/schedule/override`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            store_id: store,
            week_start: weekStartISO,
            date: v.date,
            rule_code: v.rule_code,
            overridden_by: user.name,
            override_reason: String(serverOverrideReasons[serverViolationOverrideKey(v)] || "").trim(),
          }),
        }).then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      )
    )
      .then(() => {
        setServerValidationModalOpen(false);
        const warningAckItems = serverViolationsToAckItems(warnings);
        if (hasUnsavedChanges) {
          persistScheduleCore(warningAckItems, { chainPublish: true, skipPostSaveFlow: true });
        } else {
          publishScheduleCore();
        }
      })
      .catch(() => {
        setSaveState("idle");
        setPublishState("idle");
        setToast(
          lang === "gr"
            ? "Αποτυχία καταχώρησης override — δοκιμάστε ξανά"
            : "Failed to record overrides — try again"
        );
      });
  }

  const serverViolationsGroupedByDate = serverViolations.reduce((acc, v) => {
    const d = v.date || "";
    if (!acc[d]) acc[d] = [];
    acc[d].push(v);
    return acc;
  }, {});
  const hasLegalViolations = serverViolations.some(isScheduleViolationLegal);
  const overridableBlockingViolations = serverViolations.filter(isScheduleViolationOverridableBlocking);
  const hasAnyBlockingViolation = serverViolations.some(
    (v) => String(v?.severity || "").toLowerCase() === "blocking"
  );
  const allOverridableReasonsFilledIn =
    overridableBlockingViolations.length === 0
      ? !hasAnyBlockingViolation
      : overridableBlockingViolations.every((v) => {
          const key = serverViolationOverrideKey(v);
          return String(serverOverrideReasons[key] || "").trim().length > 0;
        });
  const saveWithOverridesEnabled = !hasLegalViolations && allOverridableReasonsFilledIn;
  const pendingLadderKind =
    pendingLadderActionRef.current && LADDER_ENDPOINTS[pendingLadderActionRef.current]
      ? pendingLadderActionRef.current
      : null;

  function saveSchedule() {
    if (!store || store === "All Stores" || !ladderAllowsEdit || !hasUnsavedChanges) {
      return;
    }
    persistScheduleCore([], { skipPostSaveFlow: true });
  }

  function requestLadderAdvance(action) {
    const kind = String(action || "");
    if (!store || store === "All Stores" || !ladderAllowsEdit) return;
    if (!LADDER_ENDPOINTS[kind]) return;
    if (ladderActionRequiresValidation(kind)) {
      if (hasUnsavedChanges || !weekSavedOnce) {
        setToast(
          lang === "gr"
            ? "Αποθηκεύστε πρώτα ένα προσχέδιο πριν την αποστολή"
            : "Save a draft first before sending this week on"
        );
        return;
      }
      pendingLadderActionRef.current = kind;
      pendingLadderCommentRef.current = null;
      setSaveOverrideOpen(false);
      // Skip page banner — open Schedule Rules modal (explanations) via validation.
      publishSchedule({ forceOverride: true });
      return;
    }
    setLadderSimpleConfirm(kind);
  }

  async function postLadderAction(kind, comment) {
    const path = LADDER_ENDPOINTS[kind];
    if (!path || !store || store === "All Stores" || ladderActionBusy) return;
    setLadderActionBusy(true);
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          store_id: store,
          unified_location_id: fetchLocationId,
          week_start: weekStartISO,
          comment: comment || undefined,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof body?.detail === "string" ? body.detail : "Request failed");
      }
      applyLadderActionResult(kind, body?.week);
      setLadderSimpleConfirm(null);
      setLadderRejectOpen(false);
      setLadderRejectComment("");
      setToast(
        lang === "gr"
          ? `${ladderActionLabel(kind, lang)} — ολοκληρώθηκε`
          : `${ladderActionLabel(kind, lang)} — done`
      );
    } catch (err) {
      setToast(err?.message || (lang === "gr" ? "Αποτυχία ενέργειας" : "Action failed"));
    } finally {
      setLadderActionBusy(false);
    }
  }

  function publishSchedule(options = {}) {
    const forceOverride = Boolean(options.forceOverride);
    if (!store || store === "All Stores") {
      return;
    }
    if (hasUnsavedChanges || !weekSavedOnce) {
      return;
    }
    // Client alert ack for non-ladder publish. Ladder advances use Schedule Rules modal.
    if (hasBlockingIssues && !forceOverride) {
      setValidationIntent("publish");
      setShowAlerts(true);
      setSaveOverrideOpen(true);
      setSaveOverrideAck(false);
      return;
    }
    beginServerValidationThenPublish();
  }

  function saveScheduleButtonLabel() {
    if (saveState === "saving") return <span className="spinner-dark" />;
    if (saveState === "saved") return lang === "gr" ? "✓ Αποθηκεύτηκε" : "✓ Saved";
    return lang === "gr" ? "Αποθήκευση προσχεδίου" : "Save draft";
  }

  function publishScheduleButtonLabel() {
    if (publishState === "validating") {
      return lang === "gr" ? "Έλεγχος..." : "Checking rules...";
    }
    if (publishState === "publishing") return <span className="spinner" />;
    if (headerLadderAction) return ladderActionLabel(headerLadderAction, lang);
    const action = primaryLadderAction(user, ladderWeek?.status || "draft");
    return action ? ladderActionLabel(action, lang) : lang === "gr" ? "Ειδοποίηση T2" : "Notify T2";
  }

  const savePublishBusy =
    saveState === "validating" || saveState === "saving" || publishState === "validating" || publishState === "publishing";
  const publishGated = hasUnsavedChanges || !weekSavedOnce;
  const headerLadderAction = ladderAllowsEdit ? primaryLadderAction(user, ladderStatus) : null;
  const showHeaderReject = canRejectLadder(user, ladderStatus);
  const headerAdvanceNeedsDraft =
    Boolean(headerLadderAction) && ladderActionRequiresValidation(headerLadderAction);
  const headerRenotifyBlocked =
    headerLadderAction === "renotify_hr" && (!needsHrRenotify || hasUnsavedChanges);
  const headerAdvanceDisabled =
    Boolean(headerLadderAction) &&
    (savePublishBusy ||
      ladderActionBusy ||
      (headerAdvanceNeedsDraft && publishGated) ||
      headerRenotifyBlocked);
  const publishDisabledTitle = headerRenotifyBlocked
    ? hasUnsavedChanges
      ? lang === "gr"
        ? "Αποθηκεύστε τις αλλαγές και μετά ειδοποιήστε ξανά το HR"
        : "Save your changes, then re-notify HR"
      : lang === "gr"
        ? "Το HR έχει ήδη ειδοποιηθεί — αποθηκεύστε νέες αλλαγές για να ειδοποιήσετε ξανά"
        : "HR is already notified — save new changes to re-notify"
    : publishGated
      ? lang === "gr"
        ? "Αποθηκεύστε πρώτα ένα προσχέδιο πριν την αποστολή"
        : "Save a draft first before sending this week on"
      : "";

  const pendingReviewKeySet = useMemo(
    () =>
      new Set(
        collapseScheduleChangeRows(scheduleChangeRows, weekDayDates)
          .filter((slot) => slotNeedsPendingReviewForUser(slot, user))
          .map((slot) => slot.key)
      ),
    [scheduleChangeRows, weekDayDates, user]
  );

  const restorePreGanttScroll = () => {
    setTimeout(() => window.scrollTo({ top: preGanttScrollY.current, behavior: "instant" }), 50);
  };

  const scheduleSourceReady = gridSource === "softone"
    ? Boolean(foxlineSync.monitor?.available) && !foxlineSync.loading
    : gridSource === "ergani"
      ? !erganiSync.loading && !erganiSync.error
      : true;
  const scheduleSourceError = gridSource === "softone" && !foxlineSync.loading && !foxlineSync.monitor?.available
    ? (foxlineSync.error || (lang === "gr" ? "Το SoftOne δεν έχει φορτωθεί" : "SoftOne is not loaded"))
    : gridSource === "ergani" && erganiSync.error
      ? erganiSync.error
      : "";

  if (storeUnresolved || (staffError && !staffLoading)) {
    const message = storeUnresolved
      ? (lang === "gr"
        ? `Το κατάστημα «${store}» δεν ταιριάζει με τη λίστα καταστημάτων.`
        : `Store “${store}” is not in the store list.`)
      : (staffError || (lang === "gr" ? "Αποτυχία φόρτωσης προσωπικού" : "Could not load staff"));
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <div style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 600, maxWidth: 420, margin: "0 auto" }}>
          {message}
        </div>
      </div>
    );
  }

  if (!liveStaff) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <LoadingPulse size={28} />
        <div style={{ fontSize: 13, color: "var(--text-muted)",
          fontWeight: 600, marginTop: 12 }}>
          {lang === "gr" ? "Φόρτωση προσωπικού..." :
           "Loading staff..."}
        </div>
      </div>
    );
  }

  return (
    <div
      className="anim-fade"
      style={{
        marginRight: focusDay && !sidebarFullscreen && focusViewportWidth > 1200 ? sidebarWidth : 0,
        transition: "margin-right 0.25s ease",
      }}
    >
      {hrToast && (
        <div
          role="status"
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 4000,
            maxWidth: "min(420px, 92vw)",
            padding: "12px 18px",
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 600,
            boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
            background: hrToast.kind === "err" ? "#FEE2E2" : "var(--surface)",
            color: hrToast.kind === "err" ? "#991B1B" : "var(--text-primary)",
            border: `1.5px solid ${hrToast.kind === "err" ? "#FECACA" : "var(--border)"}`,
          }}
        >
          {hrToast.message}
        </div>
      )}
      {toast && (
        <div
          role="status"
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            background: scheduleToastTone(toast) === "danger"
              ? "#FEE2E2"
              : scheduleToastTone(toast) === "info"
                ? "var(--surface)"
                : "#1A1A1A",
            color: scheduleToastTone(toast) === "danger"
              ? "#991B1B"
              : scheduleToastTone(toast) === "info"
                ? "var(--text-secondary)"
                : "#fff",
            border: scheduleToastTone(toast) === "danger"
              ? "1px solid #FCA5A5"
              : scheduleToastTone(toast) === "info"
                ? "1px solid var(--border)"
                : "none",
            fontSize: 13,
            fontWeight: scheduleToastTone(toast) === "info" ? 600 : 700,
            padding: "12px 20px",
            borderRadius: 10,
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
            zIndex: 500,
            whiteSpace: "normal",
            textAlign: "center",
            maxWidth: "min(520px, calc(100vw - 32px))",
          }}
        >
          {scheduleToastMessage(toast)}
        </div>
      )}
      {draftSavedToast && (
        <div
          role="status"
          style={{
            position: "fixed",
            bottom: toast ? 68 : 24,
            left: "50%",
            transform: "translateX(-50%)",
            background: "var(--surface)",
            color: "var(--text-primary)",
            fontSize: 13,
            fontWeight: 600,
            padding: "10px 16px",
            borderRadius: 10,
            border: "1.5px solid var(--border)",
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            zIndex: 500,
            whiteSpace: "nowrap",
          }}
        >
          {draftSavedToast}
        </div>
      )}

      <LoanModal
        isOpen={loanModalOpen}
        onClose={() => {
          if (loanSubmitting) return;
          setLoanModalOpen(false);
          setLoanModalContext(null);
        }}
        employee={loanModalContext?.emp}
        date={loanModalContext?.dateISO}
        dateLabel={loanModalContext?.dateLabel}
        currentStore={store}
        allStores={builderPhysicalStores}
        openingHours={openingHours}
        lang={lang}
        onSubmit={handleLoanSubmit}
        submitting={loanSubmitting}
      />

      <LoanRequestModal
        isOpen={loanRequestModalOpen}
        onClose={() => {
          if (loanRequestSubmitting) return;
          setLoanRequestModalOpen(false);
          setLoanRequestContext(null);
        }}
        employee={loanRequestContext?.emp}
        homeStore={loanRequestContext?.emp ? homeStoreForEmployee(loanRequestContext.emp) : ""}
        hostStore={store}
        dateLabel={loanRequestContext?.dateLabel}
        lang={lang}
        onSubmit={submitLoanRequest}
        submitting={loanRequestSubmitting}
      />

      {unsavedNavConfirm && (
        <div
          className="modal-overlay"
          style={{ zIndex: 550 }}
          onClick={(e) => e.target === e.currentTarget && dismissUnsavedNavConfirm()}
        >
          <div className="modal-box" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-handle" />
            <div className="modal-title">
              {lang === "gr" ? "Μη αποθηκευμένες αλλαγές" : "Unsaved changes"}
            </div>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 22 }}>
              {lang === "gr"
                ? `Έχετε μη αποθηκευμένες αλλαγές για ${store} — ${weekRangeLabel}. ${
                    unsavedNavConfirm.kind === "store"
                      ? "Αν αλλάξετε κατάστημα τώρα, οι αλλαγές θα χαθούν."
                      : unsavedNavConfirm.kind === "week"
                        ? "Αν μεταβείτε σε άλλη εβδομάδα τώρα, οι αλλαγές θα χαθούν."
                        : "Αν φύγετε τώρα, οι αλλαγές θα χαθούν."
                  }`
                : `You have unsaved changes for ${store} — ${weekRangeLabel}. ${
                    unsavedNavConfirm.kind === "store"
                      ? "If you switch stores now these changes will be lost."
                      : unsavedNavConfirm.kind === "week"
                        ? "If you change week now these changes will be lost."
                        : "If you leave now these changes will be lost."
                  }`}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button type="button" className="btn-base btn-green" onClick={dismissUnsavedNavConfirm}>
                {lang === "gr" ? "Επιστροφή για αποθήκευση" : "Go back and save"}
              </button>
              <button
                type="button"
                className="btn-base btn-outline"
                onClick={confirmUnsavedNavDiscard}
                style={{ color: "#B91C1C", borderColor: "#FECACA" }}
              >
                {lang === "gr"
                  ? unsavedNavConfirm.kind === "leave"
                    ? "Έξοδος ούτως ή άλλως"
                    : "Αλλαγή ούτως ή άλλως"
                  : unsavedNavConfirm.kind === "leave"
                    ? "Leave anyway"
                    : "Switch anyway"}
              </button>
            </div>
          </div>
        </div>
      )}

      {ladderSimpleConfirm && (
        <div
          className="modal-overlay"
          style={{ zIndex: 4300 }}
          onClick={(e) => e.target === e.currentTarget && !ladderActionBusy && setLadderSimpleConfirm(null)}
        >
          <div className="modal-box" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-handle" />
            <div className="modal-title">{ladderActionLabel(ladderSimpleConfirm, lang)}</div>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 22 }}>
              {lang === "gr" ? "Επιβεβαιώνετε αυτή την ενέργεια;" : "Confirm this action?"}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn-base btn-outline"
                disabled={ladderActionBusy}
                onClick={() => setLadderSimpleConfirm(null)}
              >
                {lang === "gr" ? "Ακύρωση" : "Cancel"}
              </button>
              <button
                type="button"
                className="btn-base btn-green"
                disabled={ladderActionBusy}
                onClick={() => postLadderAction(ladderSimpleConfirm)}
              >
                {ladderActionBusy ? "…" : lang === "gr" ? "Επιβεβαίωση" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {ladderRejectOpen && (
        <div
          className="modal-overlay"
          style={{ zIndex: 4300 }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !ladderActionBusy) {
              setLadderRejectOpen(false);
              setLadderRejectComment("");
            }
          }}
        >
          <div className="modal-box" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-handle" />
            <div className="modal-title">
              {lang === "gr" ? "Απόρριψη εβδομάδας" : "Reject week"}
            </div>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 14 }}>
              {lang === "gr"
                ? `Προσθέστε λόγο απόρριψης. Η εβδομάδα θα επιστρέψει σε ${ladderRejectReturnLabel(ladderStatus, lang)}.`
                : `Add a reject reason. The week will return to ${ladderRejectReturnLabel(ladderStatus, lang)}.`}
            </p>
            <textarea
              value={ladderRejectComment}
              onChange={(e) => setLadderRejectComment(e.target.value)}
              placeholder={lang === "gr" ? "Λόγος απόρριψης" : "Reject reason"}
              rows={4}
              style={{
                width: "100%",
                border: "1px solid var(--border)",
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 13,
                resize: "vertical",
                marginBottom: 18,
                boxSizing: "border-box",
                background: "var(--surface)",
                color: "var(--text-primary)",
              }}
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn-base btn-outline"
                disabled={ladderActionBusy}
                onClick={() => {
                  setLadderRejectOpen(false);
                  setLadderRejectComment("");
                }}
              >
                {lang === "gr" ? "Ακύρωση" : "Cancel"}
              </button>
              <button
                type="button"
                className="btn-base btn-outline"
                disabled={ladderActionBusy || !String(ladderRejectComment || "").trim()}
                onClick={() => postLadderAction("reject", String(ladderRejectComment || "").trim())}
                style={{
                  color: "#fff",
                  background: "#DC2626",
                  borderColor: "#DC2626",
                  opacity: String(ladderRejectComment || "").trim() ? 1 : 0.45,
                  cursor:
                    ladderActionBusy || !String(ladderRejectComment || "").trim()
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {ladderActionBusy
                  ? "…"
                  : lang === "gr"
                    ? "Απόρριψη"
                    : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {serverValidationModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.72)",
            zIndex: 4200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              width: "min(560px, 100%)",
              maxHeight: "min(85vh, 720px)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              background: "var(--surface)",
              borderRadius: 16,
              boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ padding: "20px 22px 12px", borderBottom: "1px solid var(--border)" }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: "var(--text-primary)" }}>
                {pendingLadderKind
                  ? ladderActionLabel(pendingLadderKind, lang)
                  : lang === "gr"
                    ? "Κανόνες Προγραμματισμού"
                    : "Schedule Rules"}
              </div>
              {hasLegalViolations ? (
                <p
                  style={{
                    fontSize: 13,
                    color: "#991B1B",
                    marginTop: 8,
                    lineHeight: 1.45,
                    background: "#FEF2F2",
                    border: "1px solid #FECACA",
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontWeight: 600,
                  }}
                >
                  {lang === "gr"
                    ? "Το πρόγραμμα περιέχει νομικές παραβιάσεις που πρέπει να διορθωθούν πριν τη δημοσίευση. Δεν επιτρέπεται override για αυτά."
                    : "This schedule contains legal requirement violations that must be fixed before publishing. Override is not permitted for these items."}
                </p>
              ) : (
                <p style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 8, lineHeight: 1.45 }}>
                  {lang === "gr"
                    ? "Οι παραβιάσεις απαιτούν λόγο override πριν τη συνέχεια."
                    : "Blocking items need an override reason before continuing."}
                </p>
              )}
            </div>
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "12px 22px",
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              {Object.keys(serverViolationsGroupedByDate)
                .sort()
                .map((dateKey) => (
                  <div key={dateKey || "__nodate"}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 900,
                        color: "var(--text-primary)",
                        marginBottom: 8,
                        letterSpacing: 0.3,
                      }}
                    >
                      {dateKey || (lang === "gr" ? "Χωρίς ημερομηνία" : "No date")}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {serverViolationsGroupedByDate[dateKey].map((v, idx) => {
                        const isBlocking = String(v.severity || "").toLowerCase() === "blocking";
                        const isLegalBlocking = isScheduleViolationLegal(v);
                        const isOverridableBlocking = isScheduleViolationOverridableBlocking(v);
                        const overrideKey = serverViolationOverrideKey(v);
                        return (
                          <div
                            key={`${v.rule_code}-${dateKey}-${idx}`}
                            style={{
                              border: `1px solid ${isBlocking ? "#FECACA" : "#FDE68A"}`,
                              background: isBlocking ? "#FEF2F2" : "#FFFBEB",
                              borderRadius: 10,
                              padding: "10px 12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 10,
                              }}
                            >
                              <span style={{ fontSize: 13, color: "var(--text-primary)", lineHeight: 1.4, flex: 1 }}>
                                {v.description}
                              </span>
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  padding: "3px 8px",
                                  borderRadius: 999,
                                  whiteSpace: "nowrap",
                                  background: isBlocking ? "#FEE2E2" : "#FEF3C7",
                                  color: isBlocking ? "#991B1B" : "#92400E",
                                }}
                              >
                                {v.severity}
                              </span>
                            </div>
                            {isLegalBlocking ? (
                              <div
                                style={{
                                  marginTop: 8,
                                  fontSize: 11,
                                  fontWeight: 800,
                                  color: "#991B1B",
                                  background: "#FEE2E2",
                                  border: "1px solid #FECACA",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                }}
                              >
                                {lang === "gr"
                                  ? "Νομική απαίτηση — δεν μπορεί να παρακαμφθεί"
                                  : "Legal requirement — cannot be overridden"}
                              </div>
                            ) : isOverridableBlocking ? (
                              <div style={{ marginTop: 8 }}>
                                {serverOverrideAuthors[overrideKey] ? (
                                  <div
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: "var(--text-secondary)",
                                      marginBottom: 4,
                                    }}
                                  >
                                    {lang === "gr" ? "Ορίστηκε από" : "Set by"}{" "}
                                    {serverOverrideAuthors[overrideKey]}
                                  </div>
                                ) : null}
                                <input
                                  type="text"
                                  value={serverOverrideReasons[overrideKey] || ""}
                                  onChange={(e) => {
                                    const next = e.target.value;
                                    const original = String(seededOverrideReasonsRef.current[overrideKey] || "");
                                    const originalAuthor = seededOverrideAuthorsRef.current[overrideKey] || "";
                                    setServerOverrideReasons((prev) => ({
                                      ...prev,
                                      [overrideKey]: next,
                                    }));
                                    setServerOverrideAuthors((prev) => {
                                      const nextAuthors = { ...prev };
                                      if (String(next).trim() === String(original).trim() && originalAuthor) {
                                        nextAuthors[overrideKey] = originalAuthor;
                                      } else if (String(next).trim()) {
                                        nextAuthors[overrideKey] = user?.name || user?.email || "";
                                      } else {
                                        delete nextAuthors[overrideKey];
                                      }
                                      return nextAuthors;
                                    });
                                  }}
                                  placeholder={
                                    lang === "gr" ? "Λόγος override (υποχρεωτικό)" : "Reason for override (required)"
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "8px 10px",
                                    fontSize: 13,
                                    border: "1px solid #FCA5A5",
                                    borderRadius: 8,
                                    boxSizing: "border-box",
                                  }}
                                />
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
            <div
              style={{
                padding: "14px 22px 18px",
                borderTop: "1px solid var(--border)",
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                className="btn-base btn-outline"
                onClick={() => {
                  setServerValidationModalOpen(false);
                  setServerOverrideReasons({});
                  setServerOverrideAuthors({});
                  seededOverrideReasonsRef.current = {};
                  seededOverrideAuthorsRef.current = {};
                  pendingLadderActionRef.current = null;
                  pendingLadderCommentRef.current = null;
                  setPublishState("idle");
                }}
              >
                {lang === "gr" ? "Επιστροφή για διόρθωση" : "Go back and fix"}
              </button>
              <button
                type="button"
                className="btn-base btn-green"
                onClick={(e) => {
                  if (!saveWithOverridesEnabled || saveState === "validating" || saveState === "saving") {
                    e.preventDefault();
                    return;
                  }
                  handleSaveWithServerOverrides();
                }}
                disabled={!saveWithOverridesEnabled || saveState === "validating" || saveState === "saving"}
                aria-disabled={!saveWithOverridesEnabled || saveState === "validating" || saveState === "saving"}
                style={
                  !saveWithOverridesEnabled || saveState === "validating" || saveState === "saving"
                    ? { opacity: 0.45, cursor: "not-allowed" }
                    : undefined
                }
              >
                {saveState === "validating" || saveState === "saving" ? (
                  <span className="spinner" />
                ) : pendingLadderKind ? (
                  ladderActionLabel(pendingLadderKind, lang)
                ) : lang === "gr" ? (
                  "Αποθήκευση με overrides"
                ) : (
                  "Save with overrides"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {postSaveAckOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.72)",
            zIndex: 4100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              width: "min(520px, 100%)",
              background: "#1A1A1A",
              color: "#fff",
              borderRadius: 16,
              padding: "22px 24px",
              boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>
              {lang === "gr" ? "Επιβεβαίωση alerts" : "Acknowledge alerts"}
            </div>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.72)", marginBottom: 16, lineHeight: 1.5 }}>
              {lang === "gr"
                ? "Το πρόγραμμα αποθηκεύτηκε. Υπάρχουν ενεργά alerts που απαιτούν αναγνώριση:"
                : "Draft saved. Active alerts still need your acknowledgement:"}
            </p>
            <ul style={{ margin: "0 0 20px", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8, maxHeight: "min(50vh, 320px)", overflowY: "auto" }}>
              {postSaveAckItems.map((item) => (
                <li
                  key={item.key}
                  style={{
                    fontSize: 13,
                    lineHeight: 1.45,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.12)",
                  }}
                >
                  {item.text}
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="btn-base btn-green"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={completePostSaveAcknowledgement}
            >
              {lang === "gr" ? "Αναγνωρίζω" : "I acknowledge"}
            </button>
          </div>
        </div>
      )}

      {resetDayConfirmOpen && resetDayTarget && (
        <div
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && !manualResetLoading && closeResetDayModal()}
        >
          <div className="modal-box" style={{ maxWidth: 440 }}>
            <div className="modal-handle" />
            <div className="modal-title">
              {lang === "gr"
                ? `Επαναφορά ${resetDayModalLabel.dayName}, ${resetDayModalLabel.dateLabel};`
                : `Reset ${resetDayModalLabel.dayName}, ${resetDayModalLabel.dateLabel}?`}
            </div>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 22 }}>
              {lang === "gr"
                ? "Θα διαγραφούν οριστικά όλες οι βάρδιες αυτής της ημέρας. Δεν μπορεί να αναιρεθεί."
                : "This will permanently delete all shifts for this day. This cannot be undone."}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn-base btn-outline"
                disabled={manualResetLoading}
                onClick={() => !manualResetLoading && closeResetDayModal()}
              >
                {lang === "gr" ? "Ακύρωση" : "Cancel"}
              </button>
              <button
                type="button"
                disabled={manualResetLoading}
                onClick={() => !manualResetLoading && runManualResetDay()}
                style={{
                  border: "none",
                  borderRadius: 10,
                  padding: "8px 16px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: manualResetLoading ? "wait" : "pointer",
                  background: "#EF4444",
                  color: "#fff",
                  opacity: manualResetLoading ? 0.7 : 1,
                }}
              >
                {manualResetLoading ? <span className="spinner" /> : (lang === "gr" ? "Επαναφορά ημέρας" : "Reset day")}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetWeekModalOpen && (
        <div
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && !manualResetLoading && closeResetWeekModal()}
        >
          <div className="modal-box" style={{ maxWidth: 440 }}>
            <div className="modal-handle" />
            <div className="modal-title">
              {lang === "gr"
                ? `Επαναφορά εβδομάδας — διαγραφή όλων των βαρδιών (${fmtDate(weekDates[0])} – ${fmtDate(weekDates[6])})`
                : `Reset entire week — clears all shifts (${fmtDate(weekDates[0])} – ${fmtDate(weekDates[6])})`}
            </div>
            {!resetWeekConfirmStep2 ? (
              <>
                <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 22 }}>
                  {lang === "gr"
                    ? "Διαγράφει οριστικά από τη βάση κάθε βάρδια αυτής της εβδομάδας για αυτό το κατάστημα — χειροκίνητες, προσχέδιο και δημοσιευμένες. Οι μη αποθηκευμένες αλλαγές στο πλέγμα χάνονται. Δεν μπορεί να αναιρεθεί."
                    : "Permanently deletes every shift saved for this store and week in the database — manual entries, drafts, and published rows. Unsaved grid edits are discarded. This cannot be undone."}
                </p>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="btn-base btn-outline"
                    disabled={manualResetLoading}
                    onClick={() => !manualResetLoading && closeResetWeekModal()}
                  >
                    {lang === "gr" ? "Ακύρωση" : "Cancel"}
                  </button>
                  <button
                    type="button"
                    disabled={manualResetLoading}
                    onClick={() => {
                      setResetWeekConfirmStep2(true);
                      setResetWeekStoreInput("");
                    }}
                    style={{
                      border: "none",
                      borderRadius: 10,
                      padding: "8px 16px",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                      background: "#EF4444",
                      color: "#fff",
                    }}
                  >
                    {lang === "gr" ? "Συνέχεια" : "Continue"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 12 }}>
                  {lang === "gr"
                    ? `Πληκτρολογήστε το όνομα καταστήματος για επιβεβαίωση διαγραφής όλων των βαρδιών: ${store}`
                    : `Type the store name to confirm deleting all shifts: ${store}`}
                </p>
                <input
                  type="text"
                  value={resetWeekStoreInput}
                  onChange={(e) => setResetWeekStoreInput(e.target.value)}
                  autoFocus
                  placeholder={store}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1.5px solid var(--border)",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 14,
                    marginBottom: 20,
                    background: "var(--surface2)",
                    color: "var(--text-primary)",
                  }}
                />
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="btn-base btn-outline"
                    disabled={manualResetLoading}
                    onClick={() => {
                      if (manualResetLoading) return;
                      setResetWeekConfirmStep2(false);
                      setResetWeekStoreInput("");
                    }}
                  >
                    {lang === "gr" ? "Πίσω" : "Back"}
                  </button>
                  <button
                    type="button"
                    disabled={manualResetLoading || !resetWeekStoreNameOk}
                    onClick={() => !manualResetLoading && resetWeekStoreNameOk && runManualResetWeek()}
                    style={{
                      border: "none",
                      borderRadius: 10,
                      padding: "8px 16px",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: manualResetLoading || !resetWeekStoreNameOk ? "not-allowed" : "pointer",
                      background: "#EF4444",
                      color: "#fff",
                      opacity: manualResetLoading || !resetWeekStoreNameOk ? 0.45 : 1,
                    }}
                  >
                    {manualResetLoading ? <span className="spinner" /> : (lang === "gr" ? "Επιβεβαίωση επαναφοράς" : "Confirm reset")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {resetAutoFillModalOpen && (
        <div
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && !manualResetLoading && closeResetAutoFillModal()}
        >
          <div className="modal-box" style={{ maxWidth: 440 }}>
            <div className="modal-handle" />
            <div className="modal-title">
              {lang === "gr"
                ? `Αναίρεση μόνο αυτόματων πληρώσεων (${fmtDate(weekDates[0])} – ${fmtDate(weekDates[6])})`
                : `Clear auto-fills only (${fmtDate(weekDates[0])} – ${fmtDate(weekDates[6])})`}
            </div>
            {!resetAutoFillConfirmStep2 ? (
              <>
                <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 22 }}>
                  {lang === "gr"
                    ? "Αφαιρεί από τη βάση μόνο τις αυτόματες πληρώσεις ShiftIQ, τα κενά gap και τις σειρές OPTIMAL_*. Οι χειροκίνητες βάρδιες δεν επηρεάζονται."
                    : "Removes only ShiftIQ auto-filled slots, gap placeholders, and OPTIMAL_* rows from the database. Shifts you entered manually are not affected."}
                </p>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="btn-base btn-outline"
                    disabled={manualResetLoading}
                    onClick={() => !manualResetLoading && closeResetAutoFillModal()}
                  >
                    {lang === "gr" ? "Ακύρωση" : "Cancel"}
                  </button>
                  <button
                    type="button"
                    disabled={manualResetLoading}
                    onClick={() => setResetAutoFillConfirmStep2(true)}
                    style={{
                      border: "none",
                      borderRadius: 10,
                      padding: "8px 16px",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                      background: "#EF4444",
                      color: "#fff",
                    }}
                  >
                    {lang === "gr" ? "Συνέχεια" : "Continue"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 22 }}>
                  {lang === "gr"
                    ? "Επιβεβαιώνετε την αφαίρεση των αυτόματων πληρώσεων για ολόκληρη την εβδομάδα;"
                    : "Confirm removal of auto-filled slots for the entire week?"}
                </p>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="btn-base btn-outline"
                    disabled={manualResetLoading}
                    onClick={() => {
                      if (manualResetLoading) return;
                      setResetAutoFillConfirmStep2(false);
                    }}
                  >
                    {lang === "gr" ? "Πίσω" : "Back"}
                  </button>
                  <button
                    type="button"
                    disabled={manualResetLoading}
                    onClick={() => !manualResetLoading && runManualResetAutoFillsWeek()}
                    style={{
                      border: "none",
                      borderRadius: 10,
                      padding: "8px 16px",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: manualResetLoading ? "not-allowed" : "pointer",
                      background: "#EF4444",
                      color: "#fff",
                      opacity: manualResetLoading ? 0.7 : 1,
                    }}
                  >
                    {manualResetLoading ? <span className="spinner" /> : (lang === "gr" ? "Επιβεβαίωση" : "Confirm")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <StoreSwitcherDialog
        ref={storeSwitcherRef}
        openRef={storeSwitcherOpenRef}
        stores={builderPhysicalStores}
        currentStore={store}
        weekStartISO={weekStartISO}
        employees={employees}
        totalIssueCount={totalIssueCount}
        lang={lang}
        onSelect={requestStoreSwitch}
      />

      {/* ── Top controls ── */}
      {/* ── Top controls ── */}
      <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: builderTopWide ? "nowrap" : "wrap",
          ...(isMobile
            ? { padding: "10px 0", marginBottom: 8 }
            : {
                position: "sticky",
                top: 0,
                zIndex: 100,
                background: "var(--surface)",
                borderBottom: "1px solid var(--border)",
                marginLeft: -builderPagePadX,
                marginRight: -builderPagePadX,
                paddingLeft: builderPagePadX,
                paddingRight: builderPagePadX,
                paddingTop: 10,
                paddingBottom: 10,
                marginBottom: 8,
                boxShadow: isScrolled ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
              }),
        }}
      >
        {(["admin", "brand_manager", "store_manager", "ops_manager", "hr"].includes(user.role)) && (
          <button
            type="button"
            onClick={() => storeSwitcherRef.current?.open()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              minWidth: 140,
              padding: "7px 14px",
              borderRadius: 10,
              border: "1.5px solid var(--border)",
              background: "var(--surface2)",
              fontSize: 13,
              fontWeight: 800,
              color: "var(--text-primary)",
              cursor: builderPhysicalStores.length > 1 ? "pointer" : "default",
              flexShrink: 0,
              opacity: builderPhysicalStores.length > 1 ? 1 : 0.9,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: storeSwitcherButtonDot,
                flexShrink: 0,
              }}
              aria-hidden
            />
            <span
              style={{
                flex: 1,
                textAlign: "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {store || (lang === "gr" ? "Επιλέξτε κατάστημα" : "Select store")}
            </span>
            {builderPhysicalStores.length > 1 ? (
              <span style={{ fontSize: 10, color: "var(--text-muted)", lineHeight: 1 }} aria-hidden>
                ▾
              </span>
            ) : null}
          </button>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <WeekPicker
          weekStartISO={weekStartISO}
          goLiveWeek={SCHEDULE_GO_LIVE_WEEK}
          lang={lang}
          onCommit={(mondayISO) => {
            const offset = getOffsetForDate(parseISODateLocal(mondayISO));
            const delta = offset - weekOffset;
            if (delta !== 0) requestWeekOffsetChange(delta);
          }}
        />
          {canBuild && !isPastWeek ? (
            <div ref={weekActionsRef} style={{ position: "relative" }}>
              <button
                type="button"
                className="week-actions-menu"
                aria-label={lang === "gr" ? "Ενέργειες εβδομάδας" : "Week actions"}
                onClick={() => setWeekActionsMenuOpen((v) => !v)}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  border: "1.5px solid var(--border)",
                  background: "var(--surface2)",
                  fontSize: 14,
                  fontWeight: 800,
                  cursor: "pointer",
                  color: "var(--text-secondary)",
                }}
              >
                ⋯
              </button>
              {weekActionsMenuOpen ? (
                <div
                  className="week-actions-menu"
                  style={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    left: 0,
                    background: "var(--surface)",
                    border: "1.5px solid var(--border)",
                    borderRadius: 10,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    zIndex: 120,
                    minWidth: 180,
                    overflow: "hidden",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      copyPreviousWeek();
                      setWeekActionsMenuOpen(false);
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 14px",
                      fontSize: 13,
                      fontWeight: 600,
                      background: "var(--surface)",
                      color: "var(--text-primary)",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    {lang === "gr" ? "Αντιγραφή προηγ. εβδομάδας" : "Copy prev. week"}
                  </button>
                  {SHIFTIQ_ENABLED && canManualResetSchedule ? (
                    <button
                      type="button"
                      onClick={() => {
                        setWeekActionsMenuOpen(false);
                        setResetAutoFillConfirmStep2(false);
                        setResetAutoFillModalOpen(true);
                      }}
                      style={{
                        width: "100%",
                        padding: "8px 14px",
                        fontSize: 13,
                        fontWeight: 600,
                        background: "var(--surface)",
                        color: "#EF4444",
                        border: "none",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      {lang === "gr" ? "Αναίρεση μόνο αυτόματων πληρώσεων" : "Clear auto-fills only"}
                    </button>
                  ) : null}
                  {canManualResetSchedule ? (
                    <button
                      type="button"
                      onClick={() => {
                        setWeekActionsMenuOpen(false);
                        setResetWeekConfirmStep2(false);
                        setResetWeekStoreInput("");
                        setResetWeekModalOpen(true);
                      }}
                      style={{
                        width: "100%",
                        padding: "8px 14px",
                        fontSize: 13,
                        fontWeight: 600,
                        background: "var(--surface)",
                        color: "#EF4444",
                        border: "none",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      {lang === "gr"
                        ? "Επαναφορά εβδομάδας — διαγραφή όλων των βαρδιών"
                        : "Reset entire week — clears all shifts"}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div style={{ display: "flex", background: "var(--surface2)", borderRadius: 10, padding: 3, gap: 3, flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => {
              setTimelineActive(false);
              setVsOptimalActive(false);
            }}
            style={{
              background: !vsOptimalActive && !timelineActive ? "var(--green)" : "transparent",
              color: !vsOptimalActive && !timelineActive ? "#fff" : "var(--text-secondary)",
              borderRadius: 8,
              padding: "5px 14px",
              fontSize: 12,
              fontWeight: 700,
              border: "none",
              cursor: "pointer",
            }}
          >
            Schedule
          </button>
          {SHIFTIQ_ENABLED ? (
          <button
            type="button"
            onClick={() => {
              preGanttScrollY.current = window.scrollY;
              setTimelineActive(false);
              setVsOptimalActive(true);
              if (ganttNeedsRefreshRef.current) {
                ganttNeedsRefreshRef.current = false;
                setGanttRefreshKey((k) => k + 1);
              }
            }}
            style={{
              background: vsOptimalActive && !timelineActive ? "var(--green)" : "transparent",
              color: vsOptimalActive && !timelineActive ? "#fff" : "var(--text-secondary)",
              borderRadius: 8,
              padding: "5px 14px",
              fontSize: 12,
              fontWeight: 700,
              border: "none",
              cursor: "pointer",
            }}
          >
            vs Optimal
          </button>
          ) : null}
          {SHIFTIQ_ENABLED && BUILDER_TIMELINE_ENABLED ? (
          <button
            type="button"
            onClick={() => {
              setTimelineActive(true);
              setVsOptimalActive(false);
              setFocusDay(null);
            }}
            style={{
              background: timelineActive ? "var(--green)" : "transparent",
              color: timelineActive ? "#fff" : "var(--text-secondary)",
              borderRadius: 8,
              padding: "5px 14px",
              fontSize: 12,
              fontWeight: 700,
              border: "none",
              cursor: "pointer",
            }}
          >
            Timeline
            <span style={{ fontSize: 8, fontWeight: 800, background: "#818CF8", color: "#fff", borderRadius: 4, padding: "1px 5px", marginLeft: 4, verticalAlign: "middle" }}>
              Beta
            </span>
          </button>
          ) : null}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {totalIssueCount > 0 && builderTopWide ? (
            <>
              {criticalCount > 0 && (
                <button
                  type="button"
                  onClick={openWarningsPanelFromBadge}
                  title={`${criticalCount} critical issues`}
                  style={{
                    background: "var(--surface2)",
                    border: "1px solid var(--border)",
                    borderRadius: 999,
                    padding: "4px 10px",
                    cursor: "pointer",
                    minHeight: 32,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)" }}>{criticalCount}</span>
                  <span style={{ fontSize: 12, color: "#991B1B", lineHeight: 1 }} aria-hidden>●</span>
                </button>
              )}
              {scheduleAlertDigest.warningCount > 0 && scheduleAlertDigest.hasShifts && (
                <button
                  type="button"
                  onClick={openWarningsPanelFromBadge}
                  title={`${warningCount} warnings`}
                  style={{
                    background: "var(--surface2)",
                    border: "1px solid var(--border)",
                    borderRadius: 999,
                    padding: "4px 10px",
                    cursor: "pointer",
                    minHeight: 32,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)" }}>{warningCount}</span>
                  <span style={{ fontSize: 11, color: "#92400E", lineHeight: 1 }} aria-hidden>△</span>
                </button>
              )}
            </>
          ) : totalIssueCount > 0 ? (
            <button
              type="button"
              onClick={openWarningsPanelFromBadge}
              style={{
                background: "var(--surface2)",
                color: "var(--text-primary)",
                fontSize: 12,
                fontWeight: 800,
                borderRadius: 999,
                padding: "6px 14px",
                border: "1px solid var(--border)",
                cursor: "pointer",
                minHeight: 36,
                minWidth: 44,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span>{`${totalIssueCount} issues`}</span>
              <span style={{ color: "var(--text-muted)", fontWeight: 700 }}>·</span>
              <span
                style={{
                  color: "#991B1B",
                  background: "#FEE2E2",
                  border: "1px solid #FECACA",
                  borderRadius: 999,
                  padding: "2px 8px",
                  lineHeight: 1.1,
                }}
              >
                {`${criticalCount} critical`}
              </span>
              <span
                style={{
                  color: "#92400E",
                  background: "#FEF3C7",
                  border: "1px solid #FDE68A",
                  borderRadius: 999,
                  padding: "2px 8px",
                  lineHeight: 1.1,
                }}
              >
                {`${warningCount} warnings`}
              </span>
            </button>
          ) : null}
        </div>

        {ladderWeek ? (
          (() => {
            const rejectNotice = ladderRejectNotice(ladderWeek, lang);
            return (
              <div
                title={
                  rejectNotice
                    ? `${rejectNotice.title}: ${rejectNotice.comment}`
                    : `${lang === "gr" ? "Κατάσταση εβδομάδας" : "Week status"}: ${ladderStatusLabel(ladderStatus, lang)}`
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  flexShrink: 1,
                  minWidth: 0,
                  maxWidth: builderTopWide ? 280 : "100%",
                  padding: "6px 12px",
                  borderRadius: 999,
                  border: rejectNotice ? "1px solid #FECACA" : "1px solid var(--border)",
                  background: rejectNotice ? "#FEF2F2" : "var(--surface2)",
                  fontSize: 12,
                  fontWeight: 800,
                  color: "var(--text-primary)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <span style={{ color: rejectNotice ? "#B91C1C" : "var(--text-muted)", fontWeight: 700 }}>
                  {rejectNotice
                    ? lang === "gr"
                      ? "Απορρίφθηκε"
                      : "Rejected"
                    : lang === "gr"
                      ? "Κατάσταση"
                      : "Status"}
                </span>
                <span
                  style={{
                    color: rejectNotice ? "#991B1B" : "var(--green)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {ladderStatusLabel(ladderStatus, lang)}
                </span>
              </div>
            );
          })()
        ) : null}

        {builderTopWide ? <div style={{ flex: 1, minWidth: 16 }} /> : <div style={{ flex: 1 }} />}

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          {isPastWeek ? (
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--text-muted)",
                background: "var(--surface2)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "6px 14px",
                minHeight: 36,
                display: "inline-flex",
                alignItems: "center",
                whiteSpace: "nowrap",
              }}
            >
              {lang === "gr" ? "Παρελθούσα εβδομάδα — μόνο ανάγνωση" : "Past week — read only"}
            </span>
          ) : null}
          {!isPastWeek && !isMobile && (ladderAllowsEdit || showHeaderReject) ? (
            <>
              {ladderAllowsEdit ? (
                <button
                  type="button"
                  className="btn-base btn-outline"
                  onClick={() => saveSchedule()}
                  disabled={!hasUnsavedChanges || saveState === "saving"}
                  title={
                    !hasUnsavedChanges
                      ? lang === "gr"
                        ? "Δεν υπάρχουν μη αποθηκευμένες αλλαγές"
                        : "No unsaved changes"
                      : undefined
                  }
                  style={{
                    minHeight: 36,
                    opacity: !hasUnsavedChanges ? 0.45 : 1,
                    cursor: !hasUnsavedChanges || saveState === "saving" ? "not-allowed" : "pointer",
                  }}
                >
                  {saveScheduleButtonLabel()}
                </button>
              ) : null}
              {headerLadderAction ? (
                <button
                  type="button"
                  className="btn-base btn-green"
                  title={headerAdvanceDisabled ? publishDisabledTitle : ""}
                  onClick={() => requestLadderAdvance(headerLadderAction)}
                  disabled={headerAdvanceDisabled}
                  style={{
                    minHeight: 36,
                    opacity: headerAdvanceDisabled ? 0.45 : 1,
                    cursor: headerAdvanceDisabled ? "not-allowed" : "pointer",
                  }}
                >
                  {publishScheduleButtonLabel()}
                </button>
              ) : null}
              {showHeaderReject ? (
                <button
                  type="button"
                  className="btn-base btn-outline"
                  onClick={() => {
                    setLadderRejectComment("");
                    setLadderRejectOpen(true);
                  }}
                  disabled={ladderActionBusy || savePublishBusy}
                  style={{
                    minHeight: 36,
                    color: "#B91C1C",
                    borderColor: "#FECACA",
                    background: "#FEF2F2",
                  }}
                >
                  {ladderActionLabel("reject", lang)}
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {isPastWeek ? (
        <div
          style={{
            marginBottom: 10,
            padding: "8px 14px",
            borderRadius: 10,
            background: "var(--surface2)",
            border: "1px solid var(--border)",
            fontSize: 12,
            fontWeight: 600,
            color: "var(--text-muted)",
          }}
        >
          {lang === "gr"
            ? "Βλέπετε μια παρελθούσα εβδομάδα. Τα προγράμματα δεν μπορούν να επεξεργαστούν."
            : "You are viewing a past week. Schedules cannot be edited."}
        </div>
      ) : null}

      </>
      {saveOverrideOpen && validationIntent === "publish" && (
        <div style={{ marginBottom: 12, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 12, padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 12, color: "#991B1B", fontWeight: 700 }}>
            {lang === "gr"
              ? `Υπάρχουν ${blockingIssueCount} alerts/warnings. Διόρθωσέ τα πριν τη δημοσίευση ή κάνε override με acknowledge.`
              : `There are ${blockingIssueCount} alerts/warnings. Fix them before publishing or override by acknowledging.`}
          </div>
          {canOverrideSaveBlock ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#7F1D1D", fontWeight: 700 }}>
                <input type="checkbox" checked={saveOverrideAck} onChange={(e) => setSaveOverrideAck(e.target.checked)} />
                {lang === "gr" ? "Αναγνωρίζω τα alerts και συνεχίζω" : "I acknowledge alerts and continue"}
              </label>
              <button
                type="button"
                onClick={() => {
                  setSaveOverrideOpen(false);
                  setSaveOverrideAck(false);
                  pendingLadderActionRef.current = null;
                }}
                style={{
                  border: "1px solid #FCA5A5",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 800,
                  background: "#fff",
                  color: "#991B1B",
                  cursor: "pointer",
                }}
              >
                {lang === "gr" ? "Ακύρωση" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!saveOverrideAck) return;
                  publishSchedule({ forceOverride: true });
                }}
                disabled={!saveOverrideAck}
                style={{
                  border: "none",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 800,
                  background: "#991B1B",
                  color: "#fff",
                  cursor: saveOverrideAck ? "pointer" : "not-allowed",
                  opacity: saveOverrideAck ? 1 : 0.45,
                }}
              >
                {lang === "gr" ? "Acknowledge & Publish" : "Acknowledge & Publish"}
              </button>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "#991B1B", fontWeight: 700 }}>
              {lang === "gr" ? "Μόνο manager/admin μπορούν να κάνουν override." : "Only manager/admin can override."}
            </div>
          )}
        </div>
      )}

      {/* ── Alerts ── */}
      {scheduleAlertDigest.hasIssues && showAlerts ? (
      <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "var(--text-secondary)",
              marginBottom: 8,
              padding: "0 2px",
            }}
          >
            {lang === "gr"
              ? scheduleAlertDigest.dayCount > 0
                ? `${scheduleAlertDigest.criticalCount} κρίσιμα · ${scheduleAlertDigest.warningCount} προειδοποιήσεις σε ${scheduleAlertDigest.dayCount} μέρες`
                : `${scheduleAlertDigest.criticalCount} κρίσιμα · ${scheduleAlertDigest.warningCount} προειδοποιήσεις`
              : scheduleAlertDigest.dayCount > 0
                ? `${scheduleAlertDigest.criticalCount} critical issues · ${scheduleAlertDigest.warningCount} warnings across ${scheduleAlertDigest.dayCount} days`
                : `${scheduleAlertDigest.criticalCount} critical issues · ${scheduleAlertDigest.warningCount} warnings`}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {scheduleAlertDigest.dayBlocks.map((block) => {
              const isExpanded = Boolean(expandedScheduleAlertDays[block.dateIso]);
              const hasCritical = block.criticalCount > 0;
              const summaryParts = [];
              if (block.criticalCount > 0) {
                summaryParts.push(
                  lang === "gr"
                    ? `${block.criticalCount} κρίσιμα`
                    : `${block.criticalCount} critical`
                );
              }
              if (block.warningCount > 0) {
                summaryParts.push(
                  lang === "gr"
                    ? `${block.warningCount} προειδοποιήσεις`
                    : `${block.warningCount} warnings`
                );
              }
              return (
                <div
                  key={block.dateIso}
                  style={{
                    border: `1px solid ${hasCritical ? "#FECACA" : "#FDE68A"}`,
                    borderRadius: 12,
                    overflow: "hidden",
                    background: hasCritical ? "#FEF2F2" : "#FFFBEB",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleScheduleAlertDay(block.dateIso)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 14px",
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: hasCritical ? "#EF4444" : "#F59E0B",
                        flexShrink: 0,
                      }}
                      aria-hidden
                    />
                    <span style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)", flex: 1 }}>
                      {block.label}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: hasCritical ? "#991B1B" : "#92400E" }}>
                      {summaryParts.join(" · ")}
                    </span>
                    <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 4 }} aria-hidden>
                      {isExpanded ? "▲" : "▼"}
                    </span>
                  </button>
                  <div
                    style={{
                      maxHeight: isExpanded ? 800 : 0,
                      opacity: isExpanded ? 1 : 0,
                      overflow: "hidden",
                      transition: "max-height 0.2s ease, opacity 0.15s ease",
                    }}
                  >
                    <div
                      style={{
                        padding: "0 14px 10px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        borderTop: isExpanded ? `1px solid ${hasCritical ? "#FECACA" : "#FDE68A"}` : "none",
                      }}
                    >
                      {block.items.map((item) => {
                        const isBlocking = item.severity === "blocking";
                        return (
                          <div
                            key={item.key}
                            style={{
                              fontSize: 13,
                              color: isBlocking ? "#991B1B" : "#92400E",
                              padding: "6px 8px",
                              borderRadius: 8,
                              background: isBlocking ? "rgba(254,226,226,0.5)" : "rgba(253,230,138,0.25)",
                            }}
                          >
                            {item.text}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}

            {scheduleAlertDigest.employeeWarnings.length > 0 && (
              <div>
                <div
                  onClick={() => setWarningsExpanded(v => !v)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 14px",
                    background: "#FFFBEB",
                    border: "1px solid #FDE68A",
                    borderRadius: warningsExpanded ? "12px 12px 0 0" : 12,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <span style={{ fontSize: 15 }}>⚠️</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#92400E", flex: 1 }}>
                    {scheduleAlertDigest.incompleteShiftEmployeeCount} employee
                    {scheduleAlertDigest.incompleteShiftEmployeeCount !== 1 ? "s have" : " has"} incomplete
                    shifts this week
                  </span>
                  <span style={{ fontSize: 11, color: "#B45309", fontWeight: 600 }}>
                    {warningsExpanded ? "▲ Hide" : "▼ Show"}
                  </span>
                </div>

                {warningsExpanded && (
                  <div style={{
                    border: "1px solid #FDE68A",
                    borderTop: "none",
                    borderRadius: "0 0 12px 12px",
                    background: "#FFFDF0",
                    padding: "8px 14px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}>
                    {scheduleAlertDigest.employeeWarnings.map((a, i) => (
                      <div key={i} style={{
                        fontSize: 12,
                        color: "#92400E",
                        padding: "4px 0",
                        borderBottom: i < scheduleAlertDigest.employeeWarnings.length - 1 ? "1px solid #FDE68A" : "none",
                      }}>
                        {lang === "gr" ? a.msgGr : a.msg}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
      </div>
      ) : null}

      {!isMobile ? (
        <ScheduleBuilderGridLegend
          lang={lang}
          open={scheduleLegendOpen}
          onToggle={() => setScheduleLegendOpen((v) => !v)}
        />
      ) : null}

      {(["admin", "brand_manager", "store_manager", "hr"].includes(user.role)) && isMobile && (
        <select
          className="form-select"
          style={{ width: "100%", marginBottom: 12 }}
          value={store}
          onChange={e => {
            setStore(e.target.value);
            rememberStoreByName(e.target.value, storeRecords);
          }}
          disabled={builderPhysicalStores.length === 1}
        >
          {!store ? (
            <option value="">{lang === "gr" ? "Επιλέξτε κατάστημα" : "Select store"}</option>
          ) : null}
          {builderPhysicalStores.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      )}

      {((canBuild || canFoxlinePush || ["hr", "admin"].includes(user.role)) && store && store !== "All Stores") ? (
        <WorkMonitorCacheBar
          lang={lang}
          monitor={foxlineSync.monitor}
          actionsCache={foxlineSync.actionsCache}
          loading={foxlineSync.loading}
          refreshing={foxlineSync.refreshingMonitor}
          fetchingActions={foxlineSync.fetchingActions}
          buttonsDisabled={foxlineSync.actionBusy}
          error={foxlineSync.error}
          notice={foxlineSync.writeNotice}
          onRefresh={() => foxlineSync.refreshMonitor()}
          onFetchActions={() => foxlineSync.fetchActions()}
          actions={canFoxlinePush && softoneLadderReady && !foxlineSync.readOnly ? (
            <>
              {!foxlineSync.firstSent ? (
                <button
                  type="button"
                  className="btn-base btn-green"
                  disabled={foxlineSync.actionBusy || saveState === "saving" || foxlineSync.locationId == null || !foxlineSync.monitor?.available}
                  title={
                    hasUnsavedChanges || !weekSavedOnce
                      ? (lang === "gr" ? "Θα αποθηκευτεί πρώτα το Εσωτερικό, μετά SoftOne" : "Saves Internal first, then SoftOne")
                      : undefined
                  }
                  onClick={() => runSoftoneAfterInternalSaved(() => foxlineSync.firstSend())}
                  style={{ minHeight: 36, whiteSpace: "nowrap" }}
                >
                  {lang === "gr" ? "Αποστολή εβδομάδας στο SoftOne" : "Send week to SoftOne"}
                </button>
              ) : (foxlineSync.grouped?.[SLOT_CLASS.API_ELIGIBLE]?.length > 0 ? (
                <button
                  type="button"
                  className="btn-base btn-green"
                  disabled={foxlineSync.actionBusy || saveState === "saving"}
                  title={
                    hasUnsavedChanges || !weekSavedOnce
                      ? (lang === "gr" ? "Θα αποθηκευτεί πρώτα το Εσωτερικό, μετά SoftOne" : "Saves Internal first, then SoftOne")
                      : undefined
                  }
                  onClick={() => runSoftoneAfterInternalSaved(() => foxlineSync.uploadEligible())}
                  style={{ minHeight: 36, whiteSpace: "nowrap" }}
                >
                  {lang === "gr"
                    ? `Αποστολή ${foxlineSync.grouped[SLOT_CLASS.API_ELIGIBLE].length} κενών slots`
                    : `Upload ${foxlineSync.grouped[SLOT_CLASS.API_ELIGIBLE].length} blank slot(s)`}
                </button>
              ) : null)}
              {foxlineSync.pushDiffCount > 0 ? (
                <button
                  type="button"
                  className="btn-base btn-green"
                  disabled={foxlineSync.actionBusy || saveState === "saving"}
                  title={
                    hasUnsavedChanges || !weekSavedOnce
                      ? (lang === "gr" ? "Θα αποθηκευτεί πρώτα το Εσωτερικό, μετά SoftOne" : "Saves Internal first, then SoftOne")
                      : undefined
                  }
                  onClick={() => runSoftoneAfterInternalSaved(() => foxlineSync.pushSoftoneDiffs())}
                  style={{ minHeight: 36, whiteSpace: "nowrap" }}
                >
                  {lang === "gr"
                    ? `Αποστολή ${foxlineSync.pushDiffCount} ενημερώσεων SoftOne`
                    : `Push ${foxlineSync.pushDiffCount} SoftOne update(s)`}
                </button>
              ) : null}
            </>
          ) : null}
        />
      ) : null}
      {foxlineSync.lockedCount > 0 && store && store !== "All Stores" ? (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 12, background: "#FFFBEB", border: "1px solid #F59E0B", color: "#92400E", fontSize: 13, fontWeight: 700 }}>
          {lang === "gr"
            ? `${foxlineSync.lockedCount} βάρδιες κλειδωμένες — ανήκουν στο SoftOne (χωρίς action id ή αλλαγή από SoftOne UI).`
            : `${foxlineSync.lockedCount} shift${foxlineSync.lockedCount === 1 ? "" : "s"} locked — SoftOne owns them (no action id or SoftOne UI edit).`}
        </div>
      ) : null}
      {canFoxlinePush && store && store !== "All Stores" && foxlineSync.readOnly ? (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 12, background: "#FFFBEB", border: "1px solid #F59E0B", color: "#92400E", fontSize: 13, fontWeight: 700 }}>
          {lang === "gr"
            ? "Προσομοίωση παραγωγής — όλα τα καταστήματα και το προσωπικό. Ανάγνωση SoftOne ενεργή, αποστολές κλειδωμένες."
            : "Prod simulation — all stores and staff. SoftOne reads on, uploads locked."}
        </div>
      ) : null}

      <ScheduleLadderBar
        user={user}
        lang={lang}
        store={store}
        unifiedLocationId={fetchLocationId}
        weekStartISO={weekStartISO}
        onWeekChange={applyLadderWeek}
        refreshToken={ladderRefreshKey}
      />

      <ScheduleUnseenBanner
        lang={lang}
        slots={unseenChanges.slots}
        lastViewedAt={unseenChanges.lastViewedAt}
        onDismiss={unseenChanges.dismiss}
        onOpenSlot={(slot) => {
          const emp =
            storeEmployees.find((e) => rosterScheduleKey(e) === slot.employee_code) ||
            (liveStaff || []).find((e) => rosterScheduleKey(e) === slot.employee_code) ||
            { name: slot.employee_name, employee_code: slot.employee_code };
          const idx = weekDayDates.findIndex((iso) => iso === slot.date);
          const dk = idx >= 0 ? dayKey(weekDates[idx], idx) : "";
          openSlotHistory(emp, dk || slot.day_key, getCell(rosterScheduleKey(emp), dk));
        }}
      />

      {/* Draft people cost — same col widths as schedule table (Gantt / mobile: standalone) */}
      {!vsOptimalActive && (isMobile || (SHIFTIQ_ENABLED && timelineActive)) && (
        <div style={{ marginBottom: 10, overflowX: "auto" }}>
          <table style={{
            borderCollapse: "collapse",
            tableLayout: "fixed",
            width: "100%",
            minWidth: scheduleTableMinWidth,
            background: "var(--surface2)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            fontSize: 12,
          }}
          >
            {scheduleBuilderTableColGroup(weekDates, nameColWidth)}
            <thead>
              <ScheduleBuilderPeopleCostTheadRow
                user={user}
                lang={lang}
                weekDates={weekDates}
                builderPeopleCostPctByDay={builderPeopleCostPctByDay}
                builderPeopleCostRevenueByDay={builderPeopleCostRevenueByDay}
              />
            </thead>
          </table>
        </div>
      )}

      {/* ── Schedule table / compare view ── */}
      {SHIFTIQ_ENABLED && vsOptimalActive && sharedGanttLoading && !loadingAction && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 9999, display: 'flex', alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: '32px 40px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <div style={{
              width: 40, height: 40, border: '4px solid #e5e7eb',
              borderTop: '4px solid #1a3c2e', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
            <div style={{ fontWeight: 600, fontSize: 16, color: '#1a3c2e' }}>
              Loading optimal schedule...
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', maxWidth: 260 }}>
              Analysing demand and computing gaps.
            </div>
          </div>
        </div>
      )}
      {SHIFTIQ_ENABLED && timelineActive ? (
        <PersonTimelineView
          staff={liveStaff || []}
          schedule={schedule}
          ganttData={displayGanttData}
          store={store}
          weekStart={weekStartISO}
          openTime={(openingHours?.[dayNames[mobileDay]] || "10:00-22:00").split("-")[0] || "10:00"}
          closeTime={(openingHours?.[dayNames[mobileDay]] || "10:00-22:00").split("-")[1] || "22:00"}
          lang={lang}
          coverageData={{ hourlyTargets, openingHours }}
          hoursPerDay={dayNames.map((dn) => {
            const raw = openingHours[dn];
            if (!raw || raw === "CLOSED") return { closed: true };
            const parts = String(raw).split("-");
            if (parts.length < 2) return { closed: true };
            return { open: parts[0].trim(), close: parts[1].trim(), closed: false };
          })}
          builderAlerts={alerts}
          validationStatusByDate={dayColumnValidationStatus}
          activeDay={focusDay}
          onDayClick={(dayDate) => {
            setFocusPanelReady(false);
            setFocusDay(dayDate);
          }}
          onDayHover={(dayDate) => setHoveredDayDate(dayDate)}
          onCellClick={(emp, dayDate) => openCellEditor(emp, dayDate)}
          scheduleReadOnly={!gridEditable}
        />
      ) : SHIFTIQ_ENABLED && vsOptimalActive ? (
        <GanttCompareView
          key={ganttRefreshKey}
          store={store}
          unifiedLocationId={unifiedLocationId}
          weekStart={weekStartISO}
          lang={lang}
          allStaff={liveStaff || []}
          staff={liveStaff || []}
          sharedGanttData={displayGanttData}
          sharedGanttLoading={sharedGanttLoading}
          sharedGanttError={sharedGanttError}
          disableInternalFetch={true}
          onRequestRefresh={() => setSharedGanttRefreshTick((t) => t + 1)}
          ganttScrollTarget={ganttScrollTarget}
          setGanttScrollTarget={setGanttScrollTarget}
          vsOptimalActive={vsOptimalActive}
          setVsOptimalActive={setVsOptimalActive}
          onBackToSchedule={restorePreGanttScroll}
          openTime={(openingHours?.[dayNames[mobileDay]] || "10:00-22:00").split("-")[0] || "10:00"}
          closeTime={(openingHours?.[dayNames[mobileDay]] || "10:00-22:00").split("-")[1] || "22:00"}
          openingHoursByDay={openingHours}
          adminScheduleOverride={adminScheduleOverride}
          scheduleReadOnly={!gridEditable}
          loadingAction={loadingAction}
          setLoadingAction={setLoadingAction}
          onRequestResetWeek={() => {
            setResetWeekConfirmStep2(false);
            setResetWeekStoreInput("");
            setResetWeekModalOpen(true);
          }}
          onRequestResetAutoFills={() => {
            setResetAutoFillConfirmStep2(false);
            setResetAutoFillModalOpen(true);
          }}
        />
      ) : (
      <>
      <ScheduleSourceBar
        lang={lang}
        value={gridSource}
        onChange={setGridSource}
        pending={!scheduleSourceReady && !scheduleSourceError}
        error={scheduleSourceError}
      />
      {!isMobile ? <div
        style={
          scheduleGridWideBleed
            ? {
                overflowX: "auto",
                background: "transparent",
                border: "none",
                borderRadius: 0,
                boxShadow: "none",
                padding: "12px 0",
                marginLeft: -28,
                marginRight: -28,
                width: "calc(100% + 56px)",
                maxWidth: "none",
                boxSizing: "border-box",
              }
            : {
                overflowX: "auto",
                borderRadius: 16,
                border: "1px solid var(--border)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              }
        }
      >
        <style>{`
          .empty-cell-placeholder {
            opacity: 0.25;
            transition: opacity 0.15s;
          }
        `}</style>
        <table style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%", minWidth: scheduleTableMinWidth, background: "var(--surface)", fontSize: 12 }}>
          {scheduleBuilderTableColGroup(weekDates, nameColWidth)}
          <thead>
            <ScheduleBuilderPeopleCostTheadRow
              user={user}
              lang={lang}
              weekDates={weekDates}
              builderPeopleCostPctByDay={builderPeopleCostPctByDay}
              builderPeopleCostRevenueByDay={builderPeopleCostRevenueByDay}
            />
            <tr>
              <th
                className="name-col-header"
                title={lang === "gr" ? "Σύρετε το δεξί άκρο για αλλαγή πλάτους" : "Drag the right edge to resize"}
                style={{
                  background: "var(--header-dark)",
                  color: "#fff",
                  padding: "8px 14px",
                  textAlign: "left",
                  fontWeight: 900,
                  fontSize: 12,
                  letterSpacing: 0.5,
                  whiteSpace: "nowrap",
                  width: nameColWidth,
                  position: "relative",
                }}
              >
                ONOM/NYMO
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={lang === "gr" ? "Αλλαγή πλάτους στήλης ονομάτων" : "Resize name column"}
                  className="name-col-drag-handle"
                  onMouseDown={handleNameColDragStart}
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: 8,
                    cursor: "col-resize",
                    zIndex: 2,
                  }}
                />
                <button
                  type="button"
                  className="name-col-reset-width"
                  title={lang === "gr" ? "Επαναφορά πλάτους" : "Reset width"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setNameColWidth(SCHEDULE_NAME_COL_DEFAULT);
                    try { localStorage.removeItem("pg_schedule_name_col_width"); } catch { /* ignore */ }
                  }}
                  style={{
                    position: "absolute",
                    right: 10,
                    bottom: 2,
                    fontSize: 8,
                    color: "rgba(255,255,255,0.55)",
                    cursor: "pointer",
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    zIndex: 3,
                  }}
                >
                  Reset
                </button>
              </th>
              {weekDates.map((d, i) => {
                const dayDate = toLocalISO(d);
                const todayIso = toLocalISO(new Date());
                const dayOffThreshold = typeof peopleCost?.threshold_pct === "number" ? peopleCost.threshold_pct : 22;
                const dayPct = builderPeopleCostPctByDay[i];
                const showDayOffOffer = ["store_manager", "admin", "brand_manager", "ops_manager"].includes(user.role)
                  && dayDate > todayIso
                  && dayPct != null
                  && Number.isFinite(dayPct)
                  && dayPct > dayOffThreshold;
                const isFocusedDay = focusDay === dayDate;
                const dayValidation = dayColumnValidationStatus[dayDate];
                const dayHeaderBorderBottom =
                  dayValidation === "blocking"
                    ? "2px solid #EF4444"
                    : dayValidation === "warning"
                      ? "2px solid #F59E0B"
                      : isFocusedDay
                        ? "2px solid var(--green)"
                        : "2px solid transparent";
                return (
                <th
                  key={i}
                  className="day-header-cell day-header-clickable"
                  title="Click or press D to view day detail"
                  onMouseEnter={() => setHoveredDayDate(dayDate)}
                  onMouseLeave={() => setHoveredDayDate(null)}
                  onContextMenu={(e) => {
                    if (!canManualResetSchedule || isPastWeek) return;
                    e.preventDefault();
                    e.stopPropagation();
                    setDayHeaderMenuDate(dayDate);
                  }}
                  onClick={() => {
                    setFocusPanelReady(false);
                    setFocusDay(dayDate);
                  }}
                  style={{
                    background: isFocusedDay ? "#1A1F45" : "var(--header-dark)",
                    color: "#fff",
                    padding: "6px 8px",
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: 11,
                    borderLeft: "1px solid rgba(255,255,255,0.1)",
                    minWidth: 110,
                    cursor: "pointer",
                    borderBottom: dayHeaderBorderBottom,
                    position: "relative",
                  }}
                >
                  {canManualResetSchedule && !isPastWeek ? (
                    <button
                      type="button"
                      className="day-header-menu"
                      aria-label={lang === "gr" ? "Ενέργειες ημέρας" : "Day actions"}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDayHeaderMenuDate((prev) => (prev === dayDate ? null : dayDate));
                      }}
                      style={{
                        position: "absolute",
                        top: 4,
                        left: 4,
                        border: "none",
                        background: "rgba(0,0,0,0.2)",
                        color: "#fff",
                        borderRadius: 4,
                        padding: "0 5px",
                        fontSize: 11,
                        fontWeight: 800,
                        lineHeight: 1.4,
                        cursor: "pointer",
                      }}
                    >
                      ⋯
                    </button>
                  ) : null}
                  {dayHeaderMenuDate === dayDate ? (
                    <div
                      className="day-header-menu"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        right: 4,
                        zIndex: 120,
                        background: "var(--surface)",
                        border: "1.5px solid var(--border)",
                        borderRadius: 8,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        minWidth: 120,
                        overflow: "hidden",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => openResetDayConfirmFor(dayDate)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          fontSize: 12,
                          fontWeight: 600,
                          background: "var(--surface)",
                          color: "#EF4444",
                          border: "none",
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                      >
                        {lang === "gr" ? "Επαναφορά ημέρας" : "Reset day"}
                      </button>
                    </div>
                  ) : null}
                  <span
                    className="day-key-hint"
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      fontSize: 8,
                      fontWeight: 800,
                      background: "rgba(0,0,0,0.15)",
                      color: "#fff",
                      borderRadius: 3,
                      padding: "1px 4px",
                    }}
                  >
                    D
                  </span>
                  <div>{lang === "gr" ? dayNamesGr[i] : dayNames[i]}, {fmtDate(d)}</div>
                  <div style={{ color: "rgba(255,255,255,0.65)", fontWeight: 600, fontSize: 10, marginTop: 3 }}>
                    {openingHours[dayNames[i]]}
                    <span className="day-focus-icon" style={{ marginLeft: 6, fontSize: 10, color: "var(--green)", opacity: 0, transition: "opacity 0.15s ease" }}>
                      {"→"}
                    </span>
                  </div>
                  <ScheduleBuilderDayHeaderMetrics
                    dayDate={dayDate}
                    peopleCost={peopleCost}
                    peopleCostPct={builderPeopleCostPctByDay[i]}
                    pcLoading={pcLoading}
                    lang={lang}
                  />
                  {(() => {
                    const notices = dayOffNotices.filter((notice) => notice.offer_date === dayDate);
                    if (!notices.length) return null;
                    const yes = notices.filter((notice) => notice.reply === "accepted").length;
                    const no = notices.filter((notice) => notice.reply === "declined").length;
                    const waiting = notices.length - yes - no;
                    const parts = [];
                    if (yes) parts.push(lang === "gr" ? `${yes} ναι` : `${yes} yes`);
                    if (no) parts.push(lang === "gr" ? `${no} όχι` : `${no} no`);
                    if (waiting) parts.push(lang === "gr" ? `${waiting} αναμονή` : `${waiting} waiting`);
                    return (
                      <div style={{ marginTop: 4, fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.85)", lineHeight: 1.3 }}>
                        {parts.join(" · ")}
                      </div>
                    );
                  })()}
                  {showDayOffOffer ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        const specialties = {};
                        for (const emp of storeEmployees) {
                          const code = String(emp.employee_code || "").trim();
                          if (!code || specialties[code]) continue;
                          const label = employeeSpecialtyLabel(emp, lang);
                          if (label) specialties[code] = label;
                        }
                        setDayOffOffer({
                          date: dayDate,
                          pct: dayPct,
                          codes: scheduledCodesOnDay(i),
                          specialties,
                          ...dayOffCostSnapshot(i),
                        });
                      }}
                      style={{
                        marginTop: 6,
                        border: "1px solid rgba(255,255,255,0.4)",
                        background: "#B91C1C",
                        color: "#fff",
                        borderRadius: 999,
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "3px 8px",
                        cursor: "pointer",
                      }}
                    >
                      {lang === "gr" ? "Πρόταση άδειας" : "Offer day off"}
                    </button>
                  ) : null}
                </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {grouped.map(group => (
              <React.Fragment key={`group-${group.key}`}>
                <tr key={"grp-" + group.key}>
                  <td colSpan={8} style={{ background: "var(--header-dark)", color: "#fff", padding: "7px 14px", fontWeight: 900, fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>
                    {lang === "gr" ? group.labelGr : group.label}
                  </td>
                </tr>
                {group.members.map(emp => {
                  // Per-row alerts
                  const rowAlerts = alerts.filter(a => a.emp === emp.name);
                  const unitRow = Boolean(emp.shared_hub);
                  const unitOverlay = unitRow ? "rgba(250, 204, 21, 0.46)" : null;
                  const restRow = rowAlerts.some(a => a.type === "rest");
                  return (
                    <tr key={emp.id || emp.employee_id || emp.name} style={{ borderBottom: "1px solid var(--border)", background: restRow ? "#FFF5F5" : "transparent" }}>
                      <td style={{ position: "relative", padding: "8px 14px", fontWeight: 700, color: "var(--text-primary)", background: restRow || rowAlerts.length > 0 ? "inherit" : "var(--surface)", borderRight: "1px solid var(--border)" }}>
                        <div title={resolveEmployeeName(emp.name, liveStaff || [])} style={{ fontSize: 12, whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.25 }}>{resolveEmployeeName(emp.name, liveStaff || []).toUpperCase()}</div>
                        <div style={{ display: "flex", gap: 4, marginTop: 3, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 9, background: (emp.shiftHours ?? emp.shift_hours ?? 8) === 8 ? "#DCFCE7" : "#EDE9FE", color: (emp.shiftHours ?? emp.shift_hours ?? 8) === 8 ? "#15803D" : "#6D28D9", fontWeight: 700, padding: "1px 6px", borderRadius: 10 }}>{emp.shiftHours ?? emp.shift_hours ?? 8}H</span>
                          <span style={{ fontSize: 9, background: "#FEF9C3", color: "#854D0E", fontWeight: 700, padding: "1px 6px", borderRadius: 10 }}>{emp.workDays ?? emp.days_per_week ?? 5}D/W</span>
                          {emp.shared_hub ? (
                            <span title={lang === "gr" ? "Κοινό άτομο unit. Μία βάρδια, μισό κόστος σε αυτό το κατάστημα." : "Shared unit person. One shift, half the cost on this store."} style={{ fontSize: 9, background: "#1D4ED8", color: "#fff", fontWeight: 700, padding: "1px 6px", borderRadius: 10 }}>UNIT</span>
                          ) : null}
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 800,
                              padding: "1px 6px",
                              borderRadius: 10,
                              background: "var(--surface2)",
                              color:
                                countScheduledShiftDays(rosterScheduleKey(emp)) < getContractedWorkDays(emp)
                                  ? "#15803D"
                                  : countScheduledShiftDays(rosterScheduleKey(emp)) === getContractedWorkDays(emp)
                                    ? "#B45309"
                                    : "#B91C1C",
                            }}
                          >
                            {`${countScheduledShiftDays(rosterScheduleKey(emp))}/${getContractedWorkDays(emp)}`}
                          </span>
                          {rowAlerts.length > 0 ? (
                            <EmployeeWarningBadgePortalTooltip
                              badge={
                                <span style={{ fontSize: 9, background: "#FEE2E2", color: "#991B1B", fontWeight: 700, padding: "1px 5px", borderRadius: 10, cursor: "help" }}>
                                  {`⚠ ${rowAlerts.length}`}
                                </span>
                              }
                            >
                              <EmployeeWarningRichTooltip warnings={alerts} emp={emp} lang={lang} openingHours={openingHours} />
                            </EmployeeWarningBadgePortalTooltip>
                          ) : null}
                        </div>
                        {unitOverlay ? (
                          <div aria-hidden style={{ position: "absolute", inset: 0, background: unitOverlay, pointerEvents: "none" }} />
                        ) : null}
                      </td>
                      {weekDates.map((d, i) => {
                        const dk = dayKey(d, i);
                        const isFocusedCol = focusDay === toLocalISO(d);
                        const val = getCell(rosterScheduleKey(emp), dk);
                        const isActive = activeCell?.empId === rosterScheduleKey(emp) && activeCell?.dk === dk;
                        const sp = val ? SPECIAL_STATUSES.find(s => val === s.value) : null;

                        // Check rest violation for this specific cell (Internal, else SoftOne)
                        const prevDk = i > 0 ? dayKey(weekDates[i-1], i-1) : null;
                        const ruleVal = scheduleRuleValue(rosterScheduleKey(emp), dk);
                        const prevVal = prevDk ? scheduleRuleValue(rosterScheduleKey(emp), prevDk) : null;
                        const prevShift = parseShiftMins(prevVal);
                        const thisShift = parseShiftMins(ruleVal);
                        const restViolation = prevShift && thisShift && ((thisShift.start + 24*60) - prevShift.end) < 11*60;

                        const isLoanCell = val === "OTHER_STORE";
                        const empKey = rosterScheduleKey(emp);
                        const cellIso = toLocalISO(d);
                        const foxSlot = lookupSlotByKey(foxlineSync.slotByKey, empKey, cellIso);
                        const erganiRow = erganiSync.byKey[slotKey(empKey, cellIso)];
                        const sourceCell = scheduleSourceCell(gridSource, {
                          internal: val,
                          foxSlot,
                          erganiRow,
                          ready: scheduleSourceReady,
                        });
                        const changeLocked = slotSoftoneChangeLocked(foxSlot);
                        const foxHint = [foxlineGridHint(foxSlot, lang), erganiGridHint(val, erganiRow, lang)].filter(Boolean).join(" · ");
                        const foxDrift = !changeLocked && slotIsFoxlineDriftAlert(foxSlot?.classification);
                        const softoneFill = softoneValueWhenInternalBlank(foxSlot, val);
                        const matchesSoftone = !changeLocked && internalMatchesSoftone(val, foxSlot);
                        const lockTone = changeLocked
                          ? { bg: "#FEF3C7", color: "#92400E", border: "#F59E0B" }
                          : null;
                        const foxTone = changeLocked
                          ? lockTone
                          : (foxDrift || softoneFill || matchesSoftone)
                            ? slotClassTone(foxSlot.classification)
                            : null;
                        const cellDirty = isScheduleCellDirty(empKey, dk);
                        const cellConflicted = isScheduleCellConflicted(empKey, dk);
                        const conflictDetail = cellConflicted ? getScheduleCellConflictDetail(empKey, dk) : null;
                        const persistedStatus = getPersistedCellStatus(empKey, dk);
                        const statusLower = String(persistedStatus || "").toLowerCase();
                        const isTimeShiftCell = Boolean(thisShift && !sp && !isLoanCell && !matchesSoftone);
                        const timeShiftVisual = isTimeShiftCell
                          ? getTimeShiftCellVisual(statusLower, cellDirty, cellConflicted)
                          : null;
                        const sourceTone = sourceCell.active && sourceCell.value
                          ? (gridSource === "ergani"
                            ? { bg: "#E0E7FF", color: "#3730A3" }
                            : slotClassTone(SLOT_CLASS.PREEXISTING))
                          : null;
                        const cellBg = sourceCell.active
                          ? (sourceTone?.bg ?? "var(--surface)")
                          : changeLocked
                          ? (lockTone?.bg ?? "#FEF3C7")
                          : restViolation
                          ? "#FEE2E2"
                          : foxDrift || softoneFill || matchesSoftone
                            ? (foxTone?.bg ?? "var(--surface)")
                            : cellConflicted
                              ? "#FFF7ED"
                              : isLoanCell
                                ? "transparent"
                                : sp
                                  ? sp.color
                                  : val
                                    ? (timeShiftVisual?.bg ?? "#ECFDF5")
                                    : "var(--surface)";
                        const cellColor = sourceCell.active
                          ? (sourceTone?.color ?? "var(--text-muted)")
                          : changeLocked
                          ? (lockTone?.color ?? "#92400E")
                          : restViolation
                          ? "#991B1B"
                          : foxDrift || softoneFill || matchesSoftone
                            ? (foxTone?.color ?? "var(--text-muted)")
                          : cellConflicted
                            ? "#9A3412"
                            : isLoanCell
                              ? "inherit"
                              : sp
                                ? sp.textColor
                                : val
                                  ? (timeShiftVisual?.color ?? "#065F46")
                                  : "var(--text-muted)";
                        const jobTitleForCell = emp.job_title ?? emp.jobTitle;
                        const jobTitleForCellLabel = isKitchenStaffL(emp) ? `${jobTitleForCell} · Λαντζ.` : jobTitleForCell;
                        const showShiftJobTitle = Boolean(
                          !changeLocked && thisShift && jobTitleForCell && !sp && !isLoanCell
                        );
                        const isCopiedSource =
                          copiedShift?.sourceEmpId === empKey && copiedShift?.sourceDk === dk;
                        const canReceivePaste =
                          copiedShift &&
                          gridEditable &&
                          !isCellPasteBlocked(empKey, dk, val) &&
                          (isCellEmptyForPaste(empKey, dk, val) || isScheduleShiftValue(val));
                        const isPasteHoverTarget =
                          canReceivePaste &&
                          isCellEmptyForPaste(empKey, dk, val) &&
                          hoveredPasteCell?.empId === empKey &&
                          hoveredPasteCell?.dk === dk;
                        const showPasteReplace =
                          pasteReplaceConfirm?.empId === empKey && pasteReplaceConfirm?.dk === dk;

                        return (
                          <td
                            key={i}
                            style={{
                              borderLeft: "1px solid var(--border)",
                              padding: 0,
                              minWidth: 110,
                              position: "relative",
                              height: unitRow ? 1 : undefined,
                              background: isFocusedCol ? "rgba(16,185,129,0.04)" : "transparent",
                            }}
                          >
                            <div
                              onClick={() => {
                                if (sourceCell.active) return;
                                handleScheduleCellClick(emp, dk);
                              }}
                              onContextMenu={(e) => {
                                if (sourceCell.active) return;
                                handleScheduleCellContextMenu(e, emp, dk);
                              }}
                              onMouseEnter={() => {
                                if (copiedShift && isCellEmptyForPaste(empKey, dk, val)) {
                                  setHoveredPasteCell({ empId: empKey, dk });
                                }
                              }}
                              onMouseLeave={() => {
                                if (hoveredPasteCell?.empId === empKey && hoveredPasteCell?.dk === dk) {
                                  setHoveredPasteCell(null);
                                }
                              }}
                              title={
                                sourceCell.active
                                  ? (sourceCell.value ? displaySlotValue(sourceCell.value, lang) : "")
                                  : changeLocked
                                  ? softoneChangeLockLabel(lang)
                                  : cellConflicted
                                  ? lang === "gr"
                                    ? `⚠ Ενημερώθηκε αλλού (${conflictDetail?.fetchedVal || "—"}) — η δική σας επεξεργασία εμφανίζεται ακόμα`
                                    : `⚠ Updated elsewhere (${conflictDetail?.fetchedVal || "—"}) — your edit is still showing`
                                  : restViolation
                                    ? lang === "gr"
                                      ? "⚠️ Λιγότερο από 11ω ανάπαυση!"
                                      : "⚠️ Less than 11h rest!"
                                    : foxHint
                                      ? foxHint
                                    : copiedShift && isCellEmptyForPaste(empKey, dk, val)
                                      ? lang === "gr"
                                        ? "Κλικ για επικόλληση βάρδιας"
                                        : "Click to paste shift"
                                      : ""
                              }
                              className={!sourceCell.active && (changeLocked || foxDrift) ? "sb-foxline-drift" : undefined}
                              style={{
                                minHeight: 48,
                                height: "100%",
                                boxSizing: "border-box",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: sourceCell.active
                                  ? cellBg
                                  : isActive
                                  ? "#C8F13530"
                                  : isPasteHoverTarget
                                    ? "rgba(16,185,129,0.14)"
                                    : cellBg,
                                color: cellColor,
                                fontWeight: sourceCell.active || showShiftJobTitle || changeLocked || foxDrift || softoneFill || matchesSoftone ? 400 : (val ? 700 : 400),
                                fontSize: 11,
                                cursor: sourceCell.active || isPastWeek || !gridEditable ? "default" : copiedShift ? "copy" : "pointer",
                                outline: sourceCell.active
                                  ? "none"
                                  : isCopiedSource
                                  ? "2px dashed var(--green)"
                                  : isActive
                                    ? "2px solid #1B4332"
                                    : changeLocked
                                      ? `2px solid ${lockTone.border}`
                                    : foxDrift
                                      ? `2px dashed ${foxTone.border}`
                                    : cellConflicted
                                      ? "2px dashed #EA580C"
                                      : restViolation
                                        ? "2px solid #EF4444"
                                        : unseenChanges.keySet.has(slotKey(empKey, toLocalISO(d)))
                                          ? "2px solid #D97706"
                                        : isTimeShiftCell
                                          ? (timeShiftVisual?.outline ?? "none")
                                          : "none",
                                outlineOffset: -2,
                                transition: "background 0.1s",
                                userSelect: "none",
                                textAlign: "center",
                                padding: showShiftJobTitle ? "5px 4px" : "0 4px",
                                position: "relative",
                              }}
                            >
                              {showPasteReplace ? (
                                <div
                                  className="schedule-paste-replace-confirm"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 12,
                                    background: "rgba(255,255,255,0.96)",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 6,
                                    padding: 4,
                                  }}
                                >
                                  <span style={{ fontSize: 9, fontWeight: 700, color: "var(--text-primary)", textAlign: "center", lineHeight: 1.2 }}>
                                    {lang === "gr" ? "Αντικατάσταση;" : "Replace shift?"}
                                  </span>
                                  <div style={{ display: "flex", gap: 4 }}>
                                    <button
                                      type="button"
                                      className="btn-base btn-small"
                                      style={{ padding: "2px 8px", fontSize: 10, background: "var(--green)", color: "#fff" }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        applyCopiedShiftToCell(empKey, dk);
                                        setPasteReplaceConfirm(null);
                                      }}
                                    >
                                      {lang === "gr" ? "Ναι" : "Yes"}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-base btn-outline btn-small"
                                      style={{ padding: "2px 8px", fontSize: 10 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPasteReplaceConfirm(null);
                                      }}
                                    >
                                      {lang === "gr" ? "Όχι" : "No"}
                                    </button>
                                  </div>
                                </div>
                              ) : null}
                              {restViolation && !changeLocked && !sourceCell.active && <span style={{ position: "absolute", top: 3, right: 4, fontSize: 9 }}>⚠️</span>}
                              {!sourceCell.active && scheduleRibbonsVisible(softoneFill || val, foxSlot?.foxline, erganiRow) && (
                                <>
                              <FoxlineSlotMarker
                                classification={foxSlot?.classification}
                                internalValue={softoneFill || val}
                                foxlineValue={foxSlot?.foxline}
                                changeLocked={changeLocked}
                                known={Boolean(foxlineSync.monitor?.available)}
                                lang={lang}
                                onClick={() => setFoxlinePopupKey(foxSlot?.key || slotKey(empKey, cellIso))}
                              />
                              <ErganiSlotMarker
                                internalValue={softoneFill || val}
                                erganiRow={erganiRow}
                                known={!erganiSync.loading && !erganiSync.error}
                                lang={lang}
                                onClick={() => setFoxlinePopupKey(foxSlot?.key || slotKey(empKey, cellIso))}
                              />
                                </>
                              )}
                              {!sourceCell.active ? (
                                <SoftoneNotOpsMarker
                                  slot={foxSlot}
                                  lang={lang}
                                  onClick={() => setFoxlinePopupKey(foxSlot?.key || slotKey(empKey, cellIso))}
                                />
                              ) : null}
                              {!sourceCell.active && (
                                <>
                              <UnseenSlotMarker
                                lang={lang}
                                visible={unseenChanges.keySet.has(slotKey(empKey, cellIso))}
                              />
                              <PendingReviewMarker
                                lang={lang}
                                visible={pendingReviewKeySet.has(slotKey(empKey, cellIso))}
                                onClick={() => {
                                  setChangesPanelOpen(true);
                                  openSlotHistory(emp, dk, val);
                                }}
                              />
                                </>
                              )}
                              {sourceCell.active ? (
                                sourceCell.pending ? (
                                  <LoadingPulse size={16} color="var(--border)" />
                                ) : sourceCell.value ? (
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, lineHeight: 1.15 }}>
                                    <span style={{ fontWeight: 700, color: cellColor }}>{displaySlotValue(sourceCell.value, lang)}</span>
                                    {jobTitleForCell ? (
                                      <span style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 500 }}>{jobTitleForCellLabel}</span>
                                    ) : null}
                                  </div>
                                ) : (
                                  <span style={{ fontWeight: 500 }}>—</span>
                                )
                              ) : scheduleLoading ? (
                                <LoadingPulse size={16} color="var(--border)" />
                              ) : changeLocked ? (
                                <SoftoneLockedCellBody
                                  value={softoneFill || foxSlot?.foxline || val}
                                  lang={lang}
                                />
                              ) : (softoneFill || matchesSoftone) ? (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, lineHeight: 1.15, paddingTop: 12 }}>
                                  <span style={{ fontWeight: 700, color: cellColor }}>
                                    {displaySlotValue(softoneFill || val, lang)}
                                  </span>
                                  {jobTitleForCell ? (
                                    <span style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 500 }}>{jobTitleForCellLabel}</span>
                                  ) : null}
                                </div>
                              ) : foxDrift ? (
                                <FoxlineDriftCellBody slot={foxSlot} lang={lang} />
                              ) : !val ? (
                                <span
                                  className="empty-cell-placeholder"
                                  style={{
                                    color: "var(--border)",
                                    fontSize: 16,
                                    userSelect: "none",
                                    opacity: 0,
                                    transition: "opacity 0.15s",
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.7"; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.opacity = "0"; }}
                                >
                                  +
                                </span>
                              ) : val === "OTHER_STORE" ? (
                                <OtherStoreLoanCell
                                  employeeCode={rosterScheduleKey(emp)}
                                  employeeName={emp.name}
                                  dateISO={toLocalISO(d)}
                                  store={store}
                                  unifiedLocationId={unifiedLocationId}
                                  lang={lang}
                                  user={user}
                                  weekLoans={weekLoans}
                                  shiftType={getLoanShiftType(rosterScheduleKey(emp), dk)}
                                  homeStoreLabel={homeStoreForEmployee(emp)}
                                  onCreateLoan={isPastWeek ? undefined : () => openLoanModal(emp, dk)}
                                  onClearShift={isPastWeek ? undefined : (loanId) => clearOtherStoreShift(emp, dk, loanId)}
                                />
                              ) : showShiftJobTitle ? (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, lineHeight: 1.15 }}>
                                  <span style={{ fontWeight: 700, color: cellColor }}>{val}</span>
                                  <span style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 500 }}>{jobTitleForCellLabel}</span>
                                </div>
                              ) : val === "OFF" && isRestDayMarkedRequested(weekLeaveRequests, pendingLocalRestRequests, emp, toLocalISO(d)) ? (
                                <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, lineHeight: 1.15 }}>
                                  <span style={{ fontWeight: 700 }}>{specialStatusDisplay(val, lang)}</span>
                                  <span style={{ fontSize: 9, fontWeight: 500, fontStyle: "italic", color: "var(--text-muted)" }}>
                                    {lang === "gr" ? "Αιτήθηκε" : "Requested"}
                                  </span>
                                </span>
                              ) : (
                                <span style={{ fontWeight: val ? 700 : 400 }}>{sp ? specialStatusDisplay(val, lang) : val}</span>
                              )}
                            </div>
                            {unitOverlay ? (
                              <div aria-hidden style={{ position: "absolute", inset: 0, background: unitOverlay, pointerEvents: "none" }} />
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
            {storeEmployees.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                {lang === "gr" ? "Δεν υπάρχουν υπάλληλοι για αυτό το κατάστημα" : "No employees assigned to this store"}
              </td></tr>
            )}
            {incomingLoans.length > 0 && (
              <>
                <tr>
                  <td
                    colSpan={8}
                    style={{
                      background: "var(--header-dark)",
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: 12,
                      letterSpacing: 0.5,
                      padding: "10px 14px",
                      textTransform: "uppercase",
                    }}
                  >
                    {lang === "gr" ? "Κάλυψη Βάρδιας" : "Shift coverage"}
                  </td>
                </tr>
                {incomingLoans.map((loan) => (
                  <tr key={loan.id} style={{ borderBottom: "1px solid var(--border)", background: "#EFF6FF" }}>
                    <td style={{ padding: "8px 14px", fontWeight: 700, color: "var(--text-primary)", whiteSpace: "nowrap", borderRight: "1px solid var(--border)" }}>
                      <div style={{ fontSize: 12 }}>{resolveEmployeeName(loan.employee_name || loan.employee_code, liveStaff || [])}</div>
                      <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>{loan.home_store}</div>
                    </td>
                    {weekDates.map((d, i) => {
                      const iso = toLocalISO(d);
                      const isLoanDay = String(loan.loan_date).slice(0, 10) === iso;
                      return (
                        <td
                          key={i}
                          style={{
                            borderLeft: "1px solid var(--border)",
                            padding: "8px 6px",
                            textAlign: "center",
                            fontSize: 11,
                            color: isLoanDay ? "#1E3A8A" : "var(--text-muted)",
                            fontWeight: isLoanDay ? 700 : 400,
                          }}
                        >
                          {isLoanDay ? (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                              {loanShowsShiftAtHost(loan) ? (
                                <>
                                  <span>
                                    {(loan.shift_start || "").slice(0, 5)}–{(loan.shift_end || "").slice(0, 5)}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 9,
                                      fontWeight: 800,
                                      padding: "2px 8px",
                                      borderRadius: 8,
                                      background: "#DBEAFE",
                                      color: "#1D4ED8",
                                    }}
                                  >
                                    {lang === "gr" ? "Κάλυψη" : "Coverage"}
                                  </span>
                                </>
                              ) : (
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 800,
                                    padding: "2px 8px",
                                    borderRadius: 8,
                                    background: "#FEF9C3",
                                    color: "#854D0E",
                                  }}
                                >
                                  {lang === "gr" ? "Εκκρεμεί έγκριση" : "Pending approval"}
                                </span>
                              )}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div> : (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 12 }}>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 6, marginBottom: 8 }}>
            {dayNames.map((dn, i) => (
              <button
                key={dn}
                onClick={() => setMobileDay(i)}
                style={{
                  border: "none",
                  borderRadius: 999,
                  padding: "7px 12px",
                  whiteSpace: "nowrap",
                  background: mobileDay === i ? "var(--green)" : "var(--surface2)",
                  color: mobileDay === i ? "#fff" : "var(--text-secondary)",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "DM Sans,sans-serif"
                }}
              >
                {(lang === "gr" ? dayNamesGr[i] : dn)}
              </button>
            ))}
          </div>
          <div style={{ textAlign: "center", fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
            {dayNames[mobileDay]} {fmtDate(weekDates[mobileDay])} · {openingHours[dayNames[mobileDay]] || "—"}
          </div>

          {grouped.map(group => (
            <div key={group.key} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 6 }}>
                {lang === "gr" ? group.labelGr : group.label}
              </div>
              {group.members.map(emp => {
                const val = getCell(rosterScheduleKey(emp), mobileDk);
                const sp = val ? SPECIAL_STATUSES.find(s => s.value === val) : null;
                const mobileEmpKey = rosterScheduleKey(emp);
                const mobileCellConflicted = isScheduleCellConflicted(mobileEmpKey, mobileDk);
                const isOpen = mobileActiveEmp === mobileEmpKey;
                const mobileShift = parseShiftMins(val);
                const mobileJobTitle = emp.job_title ?? emp.jobTitle;
                const mobileJobTitleLabel = isKitchenStaffL(emp) ? `${mobileJobTitle} · Λαντζ.` : mobileJobTitle;
                const mobileShowJob = Boolean(val && mobileShift && mobileJobTitle && !sp);
                const mobileUnseen = unseenChanges.keySet.has(slotKey(mobileEmpKey, toLocalISO(weekDates[mobileDay])));
                const mobilePending = pendingReviewKeySet.has(slotKey(mobileEmpKey, toLocalISO(weekDates[mobileDay])));
                const mobileIso = toLocalISO(weekDates[mobileDay]);
                const mobileFoxSlot = lookupSlotByKey(foxlineSync.slotByKey, mobileEmpKey, mobileIso);
                const mobileSourceCell = scheduleSourceCell(gridSource, {
                  internal: val,
                  foxSlot: mobileFoxSlot,
                  erganiRow: erganiSync.byKey[slotKey(mobileEmpKey, mobileIso)],
                  ready: scheduleSourceReady,
                });
                const mobileChangeLocked = slotSoftoneChangeLocked(mobileFoxSlot);
                const mobileMatchesSoftone = !mobileChangeLocked && internalMatchesSoftone(val, mobileFoxSlot);
                const mobileFoxDrift = !mobileChangeLocked && slotIsFoxlineDriftAlert(mobileFoxSlot?.classification);
                const mobileSoftoneFill = softoneValueWhenInternalBlank(mobileFoxSlot, val);
                const mobileLockTone = mobileChangeLocked
                  ? { bg: "#FEF3C7", color: "#92400E", border: "#F59E0B" }
                  : null;
                const mobileFoxTone = mobileChangeLocked
                  ? mobileLockTone
                  : (mobileFoxDrift || mobileSoftoneFill || mobileMatchesSoftone)
                    ? slotClassTone(mobileFoxSlot.classification)
                    : null;
                const mobileShowJobLocked = Boolean(!mobileChangeLocked && mobileShowJob);
                return (
                  <div key={emp.id} style={{ marginBottom: 8 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        background: mobileSourceCell.active
                          ? "var(--surface2)"
                          : mobileChangeLocked || mobileFoxDrift || mobileSoftoneFill
                            ? mobileFoxTone.bg
                            : emp.shared_hub ? "#FFF4C2" : "var(--surface2)",
                        border: mobileSourceCell.active
                          ? "1px solid var(--border)"
                          : mobileChangeLocked
                          ? `2px solid ${mobileFoxTone.border}`
                          : mobileFoxDrift
                          ? `2px dashed ${mobileFoxTone.border}`
                          : mobilePending ? "1.5px solid #FBBF24" : mobileUnseen ? "1.5px solid #D97706" : "1px solid var(--border)",
                        borderRadius: 12,
                        padding: "8px 10px",
                      }}
                      onContextMenu={(e) => {
                        if (mobileSourceCell.active) return;
                        handleScheduleCellContextMenu(e, emp, mobileDk);
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text-primary)", display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.25 }}>{resolveEmployeeName(emp.name, liveStaff || [])}</span>
                          {emp.shared_hub ? (
                            <span style={{ fontSize: 9, background: "#1D4ED8", color: "#fff", fontWeight: 700, padding: "1px 6px", borderRadius: 10, flexShrink: 0 }}>UNIT</span>
                          ) : null}
                        </div>
                        {mobileSourceCell.active ? null : mobileChangeLocked ? (
                          <div style={{ fontSize: 10, fontWeight: 800, color: "#92400E", marginTop: 2, display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <Icon name="lock" size={11} />
                            {softoneChangeLockLabel(lang)}
                          </div>
                        ) : mobileFoxDrift ? (
                          <div style={{ fontSize: 10, fontWeight: 800, color: mobileFoxTone.color, marginTop: 2 }}>
                            {foxlineGridHint(mobileFoxSlot, lang)}
                            {" · "}
                            {erganiGridHint(val, erganiSync.byKey[slotKey(mobileEmpKey, toLocalISO(weekDates[mobileDay]))], lang)}
                          </div>
                        ) : mobilePending ? (
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#B45309", marginTop: 2 }}>
                            {lang === "gr" ? "Εκκρεμεί έλεγχος" : "Pending review"}
                          </div>
                        ) : mobileUnseen ? (
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#B45309", marginTop: 2 }}>
                            {lang === "gr" ? "Άλλαξε από την τελευταία προβολή" : "Changed since last view"}
                          </div>
                        ) : null}
                      </div>
                      <span style={{
                        fontSize: 11,
                        fontWeight: mobileShowJobLocked ? 500 : 700,
                        borderRadius: 20,
                        padding: "4px 9px",
                        background: mobileSourceCell.active && mobileSourceCell.value
                          ? (gridSource === "ergani" ? "#E0E7FF" : slotClassTone(SLOT_CLASS.PREEXISTING).bg)
                          : mobileSourceCell.active
                          ? "#F3F4F6"
                          : mobileChangeLocked || mobileFoxDrift || mobileSoftoneFill || mobileMatchesSoftone
                          ? mobileFoxTone.bg
                          : mobileCellConflicted
                          ? "#FFF7ED"
                          : val === "OTHER_STORE"
                            ? "#FEF3C7"
                            : sp
                              ? sp.color
                              : (val ? "#ECFDF5" : "#F3F4F6"),
                        color: mobileSourceCell.active && mobileSourceCell.value
                          ? (gridSource === "ergani" ? "#3730A3" : slotClassTone(SLOT_CLASS.PREEXISTING).color)
                          : mobileSourceCell.active
                          ? "var(--text-muted)"
                          : mobileChangeLocked || mobileFoxDrift || mobileSoftoneFill || mobileMatchesSoftone
                          ? mobileFoxTone.color
                          : mobileCellConflicted
                          ? "#9A3412"
                          : val === "OTHER_STORE"
                            ? "#92400E"
                            : sp
                              ? sp.textColor
                              : (val ? "#065F46" : "var(--text-muted)"),
                        border: mobileSourceCell.active
                          ? "none"
                          : mobileChangeLocked
                          ? `2px solid ${mobileFoxTone.border}`
                          : mobileFoxDrift
                          ? `2px dashed ${mobileFoxTone.border}`
                          : mobileCellConflicted ? "2px dashed #EA580C" : "none",
                        display: "inline-flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        lineHeight: 1.15,
                        textAlign: "center",
                      }}>
                        {mobileSourceCell.active ? (
                          mobileSourceCell.pending ? (
                            <LoadingPulse size={14} color="var(--border)" />
                          ) : (
                            <span style={{ fontWeight: 700 }}>
                              {mobileSourceCell.value ? displaySlotValue(mobileSourceCell.value, lang) : "—"}
                            </span>
                          )
                        ) : mobileChangeLocked ? (
                          <SoftoneLockedCellBody
                            value={mobileSoftoneFill || mobileFoxSlot?.foxline || val}
                            lang={lang}
                          />
                        ) : (mobileSoftoneFill || mobileMatchesSoftone) ? (
                          <span style={{ fontWeight: 700 }}>
                            {displaySlotValue(mobileSoftoneFill || val, lang)}
                          </span>
                        ) : mobileFoxDrift ? (
                          <FoxlineDriftCellBody slot={mobileFoxSlot} lang={lang} />
                        ) : val ? (
                          mobileShowJobLocked ? (
                            <>
                              <span style={{ fontWeight: 700 }}>{val}</span>
                              <span style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 500 }}>{mobileJobTitleLabel}</span>
                            </>
                          ) : val === "OTHER_STORE" ? (
                            <span style={{ fontWeight: 800 }}>{lang === "gr" ? "Δανεισμός" : "Loan"}</span>
                          ) : val === "OFF" && isRestDayMarkedRequested(weekLeaveRequests, pendingLocalRestRequests, emp, toLocalISO(weekDates[mobileDay])) ? (
                            <>
                              <span style={{ fontWeight: 700 }}>{specialStatusDisplay(val, lang)}</span>
                              <span style={{ fontSize: 9, fontWeight: 500, fontStyle: "italic", color: "var(--text-muted)" }}>
                                {lang === "gr" ? "Αιτήθηκε" : "Requested"}
                              </span>
                            </>
                          ) : (
                            <span style={{ fontWeight: 700 }}>{sp ? specialStatusDisplay(val, lang) : val}</span>
                          )
                        ) : (
                          "—"
                        )}
                      </span>
                      {!mobileSourceCell.active && (
                      <button
                        onClick={() => {
                          if (isSoftoneChangeLockedCell(mobileEmpKey, mobileDk)) {
                            openSoftoneLockPopup(mobileEmpKey, mobileDk);
                            return;
                          }
                          if (isOpen) {
                            setMobileActiveEmp(null);
                            setActiveCell(null);
                          } else {
                            // Open even at contracted-day max so Rest Day can be chosen;
                            // working shifts are blocked when applying a value.
                            setMobileActiveEmp(mobileEmpKey);
                            setActiveCell({
                              empId: mobileEmpKey,
                              dk: mobileDk,
                              shiftHours: emp.shiftHours ?? emp.shift_hours ?? 8,
                              empName: emp.name,
                              dayLabel: mobileDk,
                            });
                          }
                        }}
                        style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <Icon name="pencil" size={14} />
                      </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {incomingLoans.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  background: "var(--header-dark)",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 12,
                  letterSpacing: 0.5,
                  padding: "10px 12px",
                  borderRadius: "12px 12px 0 0",
                  textTransform: "uppercase",
                }}
              >
                {lang === "gr" ? "Κάλυψη Βάρδιας" : "Shift coverage"}
              </div>
              {incomingLoans.map((loan) => {
                const iso = weekDayDates[mobileDay];
                const isLoanDay = String(loan.loan_date).slice(0, 10) === iso;
                if (!isLoanDay) return null;
                return (
                  <div
                    key={loan.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 12px",
                      background: "#EFF6FF",
                      border: "1px solid var(--border)",
                      borderTop: "none",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{resolveEmployeeName(loan.employee_name || loan.employee_code, liveStaff || [])}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{loan.home_store}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {loanShowsShiftAtHost(loan) ? (
                        <>
                          <div style={{ fontWeight: 700, fontSize: 12, color: "#1E3A8A" }}>
                            {(loan.shift_start || "").slice(0, 5)}–{(loan.shift_end || "").slice(0, 5)}
                          </div>
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 800,
                              padding: "2px 8px",
                              borderRadius: 8,
                              background: "#DBEAFE",
                              color: "#1D4ED8",
                            }}
                          >
                            {lang === "gr" ? "Κάλυψη" : "Coverage"}
                          </span>
                        </>
                      ) : (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            padding: "2px 8px",
                            borderRadius: 8,
                            background: "#FEF9C3",
                            color: "#854D0E",
                          }}
                        >
                          {lang === "gr" ? "Εκκρεμεί έγκριση" : "Pending approval"}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <p style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "center", marginTop: 10, marginBottom: 4, lineHeight: 1.45 }}>
            {lang === "gr"
              ? "Πράσινο = προσχέδιο · Πράσινο περίγραμμα = δημοσιευμένο · Κεχριμπαρί = χρειάζεται επαναδημοσίευση"
              : "Green = draft · Green border = published · Amber = needs re-publishing"}
          </p>
          <div style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "center", marginTop: 4 }}>
            {mobileScheduled} staff scheduled · {mobileUnassigned} not assigned
          </div>
        </div>
      )}
      </>
      )}

      {user.role !== "hr" && (
        <WeeklyChangesPanel
          lang={lang}
          user={user}
          liveStaff={liveStaff}
          weekDates={weekDayDates}
          scheduleChangeRows={scheduleChangeRows}
          scheduleChangesLoading={scheduleChangesLoading}
          open={changesPanelOpen}
          onToggle={() => setChangesPanelOpen((v) => !v)}
          onReload={() => foxlineSync.reload()}
          onOpenSlot={(slot) => {
            const emp =
              storeEmployees.find((e) => rosterScheduleKey(e) === slot.employee_code) ||
              (liveStaff || []).find((e) => rosterScheduleKey(e) === slot.employee_code) ||
              { name: slot.employee_name, employee_code: slot.employee_code };
            const idx = weekDayDates.findIndex((iso) => iso === slot.rota_date);
            const dk = idx >= 0 ? dayKey(weekDates[idx], idx) : "";
            openSlotHistory(emp, dk || slot.day_key, getCell(rosterScheduleKey(emp), dk));
          }}
        />
      )}

      {SHIFTIQ_ENABLED && !isMobile && (
        <DailyStaffingPanel
          weeklyTargets={weeklyTargets}
          weeklyTargetsGeneratedAt={weeklyTargetsGeneratedAt}
          shiftiqLoadError={shiftiqLoadError}
          lang={lang}
          weekDates={weekDates}
          loading={shiftiqLoading}
          expanded={targetsExpanded}
          setExpanded={setTargetsExpanded}
          scheduledByDayIndex={scheduledByDayIndex}
          openingHours={openingHours}
          dayNames={dayNames}
        />
      )}

      {/* ── Shift picker ── */}
      {activeCell && gridEditable && (
        <>
          <div
            onClick={() => {
              setMobileActiveEmp(null);
              setActiveCell(null);
            }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 999,
              background: "rgba(0,0,0,0.25)",
            }}
          />
          <div style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "var(--surface)",
            borderTop: "2px solid var(--border)",
            borderRadius: "20px 20px 0 0",
            boxShadow: "0 -8px 40px rgba(27,67,50,0.15)",
            padding: "20px 20px 32px",
            maxHeight: "60vh",
            overflowY: "auto",
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}>
              <div>
                <span style={{
                  fontWeight: 800,
                  fontSize: 15,
                  color: "var(--text-primary)",
                }}>
                  {activeCell.empName}
                </span>
                <span style={{
                  fontSize: 13,
                  color: "var(--text-muted)",
                  marginLeft: 8,
                }}>
                  · {activeCell.dayLabel}
                </span>
              </div>
              <button
                onClick={() => {
                  setMobileActiveEmp(null);
                  setActiveCell(null);
                }}
                style={{
                  width: 32, height: 32,
                  border: "1.5px solid var(--border)",
                  background: "var(--surface2)",
                  borderRadius: 10,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  color: "var(--text-muted)",
                }}
              >
                ✕
              </button>
            </div>
            <ShiftPicker
              activeCell={activeCell}
              activeEmp={activeEmp}
              cellVal={cellVal}
              assignmentWarning={assignmentLimitWarning && assignmentLimitWarning.empId === activeCell.empId && assignmentLimitWarning.dk === activeCell.dk ? assignmentLimitWarning.message : ""}
              shiftOptions={shiftOptions}
              adminScheduleOverride={adminScheduleOverride}
              adminShiftPresetUnion={adminShiftPresetUnion}
              lang={lang}
              canMarkRestRequested={canMarkRestRequested}
              restDayAlreadyRequested={activeRestDayAlreadyRequested}
              markRestRequestedLoading={markRestRequestedLoading}
              removeRestRequestedLoading={removeRestRequestedLoading}
              onMarkRestRequested={markRestDayAsRequested}
              onRemoveRestRequested={removeRestDayRequested}
              onSelect={(val) => {
                if (!activeCell) return;
                if (val === "OTHER_STORE") {
                  const emp =
                    activeEmp ||
                    storeEmployees.find((e) => rosterScheduleKey(e) === activeCell.empId);
                  if (emp) openLoanModal(emp, activeCell.dk);
                  return;
                }
                const pickerEmp =
                  activeEmp ||
                  storeEmployees.find((e) => rosterScheduleKey(e) === activeCell.empId);
                if (pickerEmp && isCrossBranchEmployee(pickerEmp, store)) {
                  const home = homeStoreForEmployee(pickerEmp) || "another store";
                  setToast(
                    lang === "gr"
                      ? `Ο/Η υπάλληλος/ος ανήκει στο ${home}. Χρησιμοποιήστε τη ροή αιτήματος δανεισμού.`
                      : `This employee is from ${home}. Use the loan request flow to borrow them.`
                  );
                  return;
                }
                const empId = activeCell.empId;
                const dk = activeCell.dk;
                if (countsTowardContractDay(val)) {
                  const limit = contractedDaysLimitForCell(empId, dk, schedule);
                  if (!adminScheduleOverride && limit.blocked) {
                    setAssignmentLimitWarning({
                      empId,
                      dk,
                      message: `${String(limit.emp?.name || "").split(" ")[0] || "Employee"} is contracted for ${limit.contractedDays} days/week — already scheduled ${limit.currentScheduledDays} days. Remove a day first.`,
                    });
                    showContractedDaysToast(limit.emp, limit.contractedDays, limit.currentScheduledDays);
                    if (assignmentLimitWarningTimeoutRef.current) clearTimeout(assignmentLimitWarningTimeoutRef.current);
                    assignmentLimitWarningTimeoutRef.current = setTimeout(() => setAssignmentLimitWarning(null), 4000);
                    return;
                  }
                }
                const capFlags = getAdminShiftBeyondCapFlags(empId, dk, val);
                if (capFlags.length > 0) {
                  setAdminCapShiftConfirm({ empId, dk, val, capFlags });
                  return;
                }
                setCell(empId, dk, val);
              }}
              onClear={() => {
                clearCell(activeCell.empId, activeCell.dk);
                setMobileActiveEmp(null);
                setActiveCell(null);
              }}
              onClose={() => {
                setMobileActiveEmp(null);
                setActiveCell(null);
              }}
              onAddCustom={(sh, type) => {
                if (type === 8) setCustomShifts8(p => p.includes(sh) ? p : [...p, sh]);
                else setCustomShifts6(p => p.includes(sh) ? p : [...p, sh]);
              }}
              empShiftHours={activeEmp?.shiftHours ?? activeEmp?.shift_hours ?? activeCell.shiftHours ?? 8}
            />
          </div>
        </>
      )}

      {adminCapShiftConfirm &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 10050,
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
            }}
            onClick={() => setAdminCapShiftConfirm(null)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "var(--surface)",
                borderRadius: 14,
                padding: 22,
                maxWidth: 400,
                width: "100%",
                boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
                border: "1.5px solid var(--border)",
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 800, color: "var(--text-primary)", marginBottom: 8 }}>
                {lang === "gr" ? "Υπέρβαση ορίων" : "Beyond recommended limits"}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.45 }}>
                {lang === "gr"
                  ? "Η επιλογή σου ξεφεύγει από τα συνήθη επιχειρησιακά όρια για αυτό το κελί:"
                  : "This choice goes beyond the usual operational limits for this cell:"}
              </div>
              <ul style={{ margin: "0 0 16px 18px", padding: 0, fontSize: 13, fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.5 }}>
                {adminCapShiftConfirm.capFlags.map((f, idx) =>
                  f.type === "opening" ? (
                    <li key={`cap-${idx}-oh`} style={{ marginBottom: 6 }}>
                      {lang === "gr"
                        ? `Η βάρδια ${f.shiftLabel} είναι εκτός ωραρίου λειτουργίας (${f.oh}).`
                        : `Shift ${f.shiftLabel} is outside published opening hours (${f.oh}).`}
                    </li>
                  ) : f.type === "contractDays" ? (
                    <li key={`cap-${idx}-cd`} style={{ marginBottom: 6 }}>
                      {lang === "gr" ? (
                        <>
                          <strong>{f.firstName}</strong>
                          {`: θα μετρήσει `}
                          <strong>{f.hypDays}</strong>
                          {` εργάσιμες μέρες αυτή την εβδομάδα (συμβόλαιο: `}
                          <strong>{f.contracted}</strong>
                          {` ημέρες/εβδ.).`}
                        </>
                      ) : (
                        <>
                          <strong>{f.firstName}</strong>
                          {` would work `}
                          <strong>{f.hypDays}</strong>
                          {` days this week (contracted limit: `}
                          <strong>{f.contracted}</strong>
                          {` days/week).`}
                        </>
                      )}
                    </li>
                  ) : (
                    <li key={`cap-${idx}-pc`} style={{ marginBottom: 6 }}>
                      {lang === "gr"
                        ? `Κόστος προσωπικού ${f.dayShort}: θα φτάσει ~${f.hypPct.toFixed(1)}% (στόχος ≤${f.threshold}%).`
                        : `People cost on ${f.dayShort}: would reach ~${f.hypPct.toFixed(1)}% (target ≤${f.threshold}%).`}
                    </li>
                  ),
                )}
              </ul>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 16 }}>
                {lang === "gr" ? "Να εφαρμοστεί η αλλαγή;" : "Apply this change anyway?"}
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => setAdminCapShiftConfirm(null)}
                  style={{
                    border: "1.5px solid var(--border)",
                    background: "var(--surface2)",
                    color: "var(--text-secondary)",
                    borderRadius: 10,
                    padding: "8px 16px",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {lang === "gr" ? "Ακύρωση" : "Cancel"}
                </button>
                <button
                  type="button"
                  className="btn-base btn-green"
                  onClick={() => {
                    const p = adminCapShiftConfirm;
                    if (!p) return;
                    setAdminCapShiftConfirm(null);
                    setCell(p.empId, p.dk, p.val);
                  }}
                  style={{ padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                >
                  {lang === "gr" ? "Ναι, εφαρμογή" : "Yes, apply"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {scheduleCellContextMenu &&
        createPortal(
          (() => {
            const { x, y, emp, dk, empId, val } = scheduleCellContextMenu;
            const isFilledShift = isScheduleShiftValue(val);
            const isEmptyPasteable = isCellEmptyForPaste(empId, dk, val);
            const isPasteBlocked = isCellPasteBlocked(empId, dk, val);
            const sameEmployee = copiedShift?.sourceEmpId === empId;
            const allowEdits = gridEditable;
            const menuItemStyle = {
              width: "100%",
              padding: "8px 14px",
              fontSize: 13,
              fontWeight: 600,
              background: "var(--surface)",
              color: "var(--text-primary)",
              border: "none",
              cursor: "pointer",
              textAlign: "left",
            };
            const items = [];

            if (allowEdits && copiedShift) {
              if (!isPasteBlocked && (isEmptyPasteable || isFilledShift)) {
                items.push({
                  key: "paste",
                  label: lang === "gr" ? "Επικόλληση βάρδιας" : "Paste shift",
                  onClick: () => {
                    closeScheduleCellContextMenu();
                    handlePasteShiftClick(emp, dk);
                  },
                });
              }
              if (sameEmployee && isEmptyPasteable && dk !== copiedShift.sourceDk) {
                items.push({
                  key: "paste-week",
                  label: lang === "gr" ? "Επικόλληση σε υπόλοιπες μέρες" : "Paste to remaining week days",
                  onClick: () => pasteCopiedShiftToRemainingWeek(empId),
                });
              }
              items.push({
                key: "cancel-copy",
                label: lang === "gr" ? "Ακύρωση αντιγραφής" : "Cancel copy mode",
                danger: true,
                onClick: clearCopiedShiftMode,
              });
            } else if (allowEdits && isFilledShift) {
              items.push({
                key: "copy",
                label: lang === "gr" ? "Αντιγραφή βάρδιας" : "Copy shift",
                onClick: () => copyShiftFromCell(empId, dk, val, emp.group || "service"),
              });
              items.push({
                key: "clear",
                label: lang === "gr" ? "Καθαρισμός βάρδιας" : "Clear shift",
                danger: true,
                onClick: () => {
                  clearCell(empId, dk);
                  closeScheduleCellContextMenu();
                },
              });
            }

            items.push({
              key: "history",
              label: lang === "gr" ? "Εμφάνιση ιστορικού" : "Show history",
              onClick: () => openSlotHistory(emp, dk, val),
            });

            return (
              <div
                className="schedule-cell-context-menu"
                style={{
                  position: "fixed",
                  left: Math.min(x, window.innerWidth - 220),
                  top: Math.min(y, window.innerHeight - items.length * 40 - 16),
                  zIndex: 10060,
                  background: "var(--surface)",
                  border: "1.5px solid var(--border)",
                  borderRadius: 10,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  minWidth: 200,
                  overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    style={{
                      ...menuItemStyle,
                      color: item.danger ? "#EF4444" : menuItemStyle.color,
                    }}
                    onClick={item.onClick}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            );
          })(),
          document.body,
        )}

      {isMobile && !isPastWeek && (ladderAllowsEdit || showHeaderReject) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
          {ladderAllowsEdit ? (
            <button
              type="button"
              className="btn-base btn-outline"
              onClick={() => saveSchedule()}
              disabled={!hasUnsavedChanges || saveState === "saving"}
              title={
                !hasUnsavedChanges
                  ? lang === "gr"
                    ? "Δεν υπάρχουν μη αποθηκευμένες αλλαγές"
                    : "No unsaved changes"
                  : undefined
              }
              style={{
                width: "100%",
                justifyContent: "center",
                minHeight: 40,
                opacity: !hasUnsavedChanges ? 0.45 : 1,
                cursor: !hasUnsavedChanges || saveState === "saving" ? "not-allowed" : "pointer",
              }}
            >
              {saveScheduleButtonLabel()}
            </button>
          ) : null}
          {headerLadderAction ? (
            <button
              type="button"
              className="btn-base btn-green"
              title={headerAdvanceDisabled ? publishDisabledTitle : ""}
              onClick={() => requestLadderAdvance(headerLadderAction)}
              disabled={headerAdvanceDisabled}
              style={{
                width: "100%",
                justifyContent: "center",
                minHeight: 40,
                opacity: headerAdvanceDisabled ? 0.45 : 1,
                cursor: headerAdvanceDisabled ? "not-allowed" : "pointer",
              }}
            >
              {publishScheduleButtonLabel()}
            </button>
          ) : null}
          {showHeaderReject ? (
            <button
              type="button"
              className="btn-base btn-outline"
              onClick={() => {
                setLadderRejectComment("");
                setLadderRejectOpen(true);
              }}
              disabled={ladderActionBusy || savePublishBusy}
              style={{
                width: "100%",
                justifyContent: "center",
                minHeight: 40,
                color: "#B91C1C",
                borderColor: "#FECACA",
                background: "#FEF2F2",
              }}
            >
              {ladderActionLabel("reject", lang)}
            </button>
          ) : null}
        </div>
      )}

      {/* Custom shift creator now lives inside the shift picker popup */}

      <DayOffOfferDialog
        open={Boolean(dayOffOffer)}
        date={dayOffOffer?.date || ""}
        storeId={fetchStore || store}
        unifiedLocationId={fetchLocationId ?? unifiedLocationId}
        employeeCodes={dayOffOffer?.codes || []}
        specialties={dayOffOffer?.specialties || {}}
        shifts={dayOffOffer?.shifts || []}
        revenue={dayOffOffer?.revenue}
        labourCostEur={dayOffOffer?.labourCostEur}
        thresholdPct={dayOffOffer?.thresholdPct}
        lang={lang}
        onClose={() => setDayOffOffer(null)}
        onSent={() => setDayOffNoticeTick((tick) => tick + 1)}
      />

      <FoxlineSlotPopup
        open={Boolean(foxlineOpenSlot)}
        onClose={() => setFoxlinePopupKey(null)}
        slot={foxlineOpenSlot}
        history={foxlineOpenSlot ? foxlineSync.historyBySlot[foxlineOpenSlot.key] || [] : []}
        lang={lang}
        isHr={canFoxlinePush}
        readOnly={foxlineSync.readOnly || !softoneLadderReady}
        busy={foxlineSync.actionBusy}
        onUploadBlank={() => foxlineOpenSlot && runSoftoneAfterInternalSaved(() => foxlineSync.uploadEligible([foxlineOpenSlot.key]))}
        onMarkSubmitted={() => foxlineOpenSlot && foxlineSync.markSubmitted([foxlineOpenSlot.key])}
        onAcceptDeletion={() => foxlineOpenSlot && foxlineSync.acceptDeletion([foxlineOpenSlot.key])}
        onRestore={() => foxlineOpenSlot && runSoftoneAfterInternalSaved(() => foxlineSync.restoreDeleted([foxlineOpenSlot.key]))}
        onReplace={() => foxlineOpenSlot && runSoftoneAfterInternalSaved(() => foxlineSync.replaceModified([foxlineOpenSlot.key]))}
        erganiRow={foxlineOpenSlot ? erganiSync.byKey[foxlineOpenSlot.key] : null}
        erganiHistory={erganiHistory}
      />

      <SlotHistoryModal
        open={Boolean(slotHistoryTarget)}
        onClose={() => setSlotHistoryTarget(null)}
        lang={lang}
        unifiedLocationId={unifiedLocationId}
        weekStartISO={weekStartISO}
        employeeCode={slotHistoryTarget?.empId}
        employeeId={slotHistoryTarget?.emp?.id}
        employeeName={slotHistoryTarget?.emp?.name}
        dateISO={slotHistoryTarget?.dateISO}
        currentValue={slotHistoryTarget?.val}
        extraRows={[...(foxlineSync.changeRows || []), ...(foxlineSync.snapshot?.events || [])]}
      />

      {SHIFTIQ_ENABLED ? (
        !coverageExpanded ? (
        <ScheduleSectionCollapsedHeader
          badge="COVERAGE"
          summary={`${belowTargetHours} below-target hours this week`}
          actionLabel="Show coverage ▾"
          onExpand={() => setCoverageExpanded(true)}
        />
      ) : (
        <StaffingHeatmap
          schedule={schedule}
          weekDates={weekDates}
          dayNames={dayNames}
          dayNamesGr={dayNamesGr}
          allUsers={employees}
          store={store}
          openingHours={openingHours}
          lang={lang}
          parseShiftMins={parseShiftMins}
          fmtDate={fmtDate}
          demandIntensity={demandIntensity}
          hourlyTargets={hourlyTargets}
          isMobile={isMobile}
          mobileDay={mobileDay}
          onHeaderClick={() => setCoverageExpanded(false)}
          headerRightAction={
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCoverageExpanded(false);
              }}
              style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 12, color: "var(--green)", fontWeight: 700, padding: "4px 8px" }}
            >
              Hide ▴
            </button>
          }
        />
        )
      ) : null}

      {focusDay && !sidebarFullscreen && !isWideFocusLayout && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setFocusDay(null);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.15)",
            zIndex: 199,
          }}
        />
      )}

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          left: sidebarFullscreen ? 0 : "auto",
          height: "100vh",
          width: sidebarFullscreen ? "100vw" : sidebarWidth,
          zIndex: sidebarFullscreen ? 400 : 200,
          background: "var(--surface)",
          borderLeft: "1.5px solid var(--border)",
          borderRadius: sidebarFullscreen ? 0 : undefined,
          boxShadow: sidebarFullscreen ? "none" : "-4px 0 24px rgba(0,0,0,0.08)",
          transition: "transform 0.25s ease",
          transform: sidebarFullscreen ? "none" : (focusDay ? "translateX(0)" : `translateX(${sidebarWidth}px)`),
          display: "flex",
          flexDirection: "column",
          overflowY: "hidden",
          pointerEvents: focusDay ? "auto" : "none",
        }}
      >
        {!sidebarFullscreen && (
          <div
            className="sidebar-drag-handle"
            onMouseDown={handleSidebarDragStart}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 6,
              cursor: "ew-resize",
              background: "transparent",
              zIndex: 10,
            }}
          >
            <button
              type="button"
              className="sidebar-reset-width"
              onClick={(e) => {
                e.stopPropagation();
                setSidebarWidth(360);
                try { localStorage.removeItem("pg_sidebar_width"); } catch {}
              }}
              style={{
                position: "absolute",
                left: 8,
                bottom: 8,
                fontSize: 9,
                color: "var(--text-muted)",
                cursor: "pointer",
                border: "none",
                background: "transparent",
                padding: 0,
                zIndex: 11,
              }}
            >
              Reset width
            </button>
          </div>
        )}
        <div style={{ padding: "16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "var(--text-primary)" }}>{focusedDayName || "—"}</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{focusedDateLabel || "—"}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              title={sidebarFullscreen ? "Collapse view" : "Expand to full view"}
              onClick={() => setSidebarFullscreen((v) => !v)}
              style={{ border: "none", background: "none", fontSize: 14, cursor: "pointer", color: "var(--text-muted)" }}
            >
              {sidebarFullscreen ? "⤡" : "⤢"}
            </button>
            <button
              type="button"
              onClick={() => setFocusDay(null)}
              style={{ border: "none", background: "none", fontSize: 18, color: "var(--text-muted)", cursor: "pointer" }}
            >
              ✕
            </button>
          </div>
        </div>
        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>
          {(() => {
            const notices = dayOffNotices.filter((notice) => notice.offer_date === focusDay);
            if (!notices.length) return null;
            const replyStyle = {
              accepted: { bg: "#DCFCE7", color: "#166534", label: lang === "gr" ? "Θα την πάρει" : "Accepted" },
              declined: { bg: "#FEE2E2", color: "#991B1B", label: lang === "gr" ? "Δεν θα την πάρει" : "Declined" },
              waiting: { bg: "#F3F4F6", color: "#4B5563", label: lang === "gr" ? "Χωρίς απάντηση" : "No reply" },
            };
            return (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
                  {lang === "gr" ? "Πρόταση άδειας" : "Day off offers"}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {notices.map((notice) => {
                    const tone = replyStyle[notice.reply] || replyStyle.waiting;
                    const emp = storeEmployees.find((row) => String(row.employee_code || "").trim() === String(notice.employee_code || "").trim());
                    const specialty = emp ? employeeSpecialtyLabel(emp, lang) : "";
                    return (
                      <div
                        key={notice.employee_code}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          padding: "8px 10px",
                          border: "1px solid var(--border)",
                          borderRadius: 10,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)" }}>{notice.name}</div>
                          {specialty ? (
                            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", marginTop: 1 }}>{specialty}</div>
                          ) : null}
                        </div>
                        <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 800, borderRadius: 999, padding: "3px 8px", background: tone.bg, color: tone.color }}>
                          {tone.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {!focusPanelReady ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "28px 0" }}>
              <LoadingPulse size={22} />
            </div>
          ) : !sidebarDisplayDayData && sharedGanttLoading ? (
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 18 }}>
              <LoadingPulse size={20} />
            </div>
          ) : !sidebarDisplayDayData ? (
            <div style={{ fontSize: 12, color: "var(--text-muted)", paddingTop: 8 }}>
              {lang === "gr" ? "Δεν υπάρχουν δεδομένα για αυτή την ημέρα." : "No data for this day."}
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
                {[
                  { id: "all", label: "All" },
                  { id: "supervisor", label: "Sup" },
                  { id: "service", label: "Srv" },
                  { id: "boh", label: "BOH" },
                ].map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    className={`filter-chip ${sidebarRoleFilter === chip.id ? "chip-active" : ""}`}
                    onClick={() => setSidebarRoleFilter(chip.id)}
                    style={{ fontSize: 11, padding: "4px 10px", borderRadius: 999, border: "1px solid var(--border)", cursor: "pointer" }}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              {SHIFTIQ_ENABLED ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={async () => {
                    if (!focusDay) return;
                    setLoadingAction("Filling gaps for this day...");
                    try {
                      const res = await fetch(`${SHIFTIQ_API}/optimal-schedule/apply`, {
                        method: "POST",
                        headers: getAuthHeaders(),
                        body: JSON.stringify({ store_id: store, week_start: weekStartISO, date: focusDay }),
                      });
                      if (!res.ok) return;
                      window.dispatchEvent(new CustomEvent("rota-updated", {
                        detail: { store_id: store, week_start: weekStartISO, refreshGantt: true },
                      }));
                      setSharedGanttRefreshTick((t) => t + 1);
                    } finally {
                      setLoadingAction(null);
                    }
                  }}
                  style={{ border: "1.5px solid var(--green)", background: "transparent", color: "var(--green)", fontSize: 11, fontWeight: 700, borderRadius: 8, padding: "4px 12px" }}
                >
                  Fill gaps
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!focusDay) return;
                    setLoadingAction("Resetting day...");
                    try {
                      const res = await fetch(`${SHIFTIQ_API}/optimal-schedule/reset`, {
                        method: "POST",
                        headers: getAuthHeaders(),
                        body: JSON.stringify({
                          store_id: store,
                          week_start: weekStartISO,
                          date: focusDay,
                          force: false,
                        }),
                      });
                      if (!res.ok) return;
                      window.dispatchEvent(new CustomEvent("rota-updated", {
                        detail: { store_id: store, week_start: weekStartISO, refreshGantt: true },
                      }));
                      setSharedGanttRefreshTick((t) => t + 1);
                    } finally {
                      setLoadingAction(null);
                    }
                  }}
                  style={{ border: "1.5px solid var(--border)", background: "transparent", color: "var(--text-secondary)", fontSize: 11, fontWeight: 700, borderRadius: 8, padding: "4px 12px" }}
                >
                  Reset auto-fills
                </button>
              </div>
              ) : null}
              {canManualResetSchedule && focusDay ? (
                <div style={{ marginBottom: 12 }}>
                  <button
                    type="button"
                    onClick={() => openResetDayConfirmFor(focusDay)}
                    style={{
                      width: "100%",
                      border: "1.5px solid #FECACA",
                      background: "#FEF2F2",
                      color: "#B91C1C",
                      fontSize: 12,
                      fontWeight: 700,
                      borderRadius: 8,
                      padding: "8px 12px",
                      cursor: "pointer",
                    }}
                  >
                    {lang === "gr" ? "Καθαρισμός ημέρας" : "Clear day"}
                  </button>
                </div>
              ) : null}
              {SHIFTIQ_ENABLED ? (
              <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 10 }}>
                {focusedDayIsClosed
                  ? "Store closed"
                  : `${focusedCoveredOptimalCount}/${focusedOptimalSlots.length} optimal slots covered`}
              </div>
              ) : null}
              <div style={{ position: "relative", marginBottom: 8 }}>
                {!focusedDayIsClosed ? (
                  <>
                    <div
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: sidebarOpenLinePct,
                        top: 0,
                        bottom: 0,
                        width: 2,
                        background: "var(--green)",
                        zIndex: 5,
                        pointerEvents: "none",
                        transform: "translateX(-50%)",
                      }}
                    />
                    <div
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: sidebarCloseLinePct,
                        top: 0,
                        bottom: 0,
                        width: 2,
                        background: "#EF4444",
                        zIndex: 5,
                        pointerEvents: "none",
                        transform: "translateX(-50%)",
                      }}
                    />
                    <div
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: sidebarOpenLinePct,
                        top: 0,
                        transform: "translateX(-50%)",
                        fontSize: 8,
                        fontWeight: 700,
                        color: "var(--green)",
                        zIndex: 6,
                        pointerEvents: "none",
                        lineHeight: 1,
                      }}
                    >
                      {lang === "gr" ? "Άνοιγμα" : "Open"}
                    </div>
                    <div
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: sidebarCloseLinePct,
                        top: 0,
                        transform: "translateX(-50%)",
                        fontSize: 8,
                        fontWeight: 700,
                        color: "#EF4444",
                        zIndex: 6,
                        pointerEvents: "none",
                        lineHeight: 1,
                      }}
                    >
                      {lang === "gr" ? "Κλείσιμο" : "Close"}
                    </div>
                  </>
                ) : null}
              {sidebarRoles.map((role) => {
                if (sidebarRoleFilter !== "all" && sidebarRoleFilter !== role) return null;
                const meta = sidebarRoleMeta[role];
                const roleOptimal = (sidebarDisplayDayData?.optimal_slots || []).filter((s) => ganttDisplayRoleKey(s.role) === role);
                const filteredDraftSlots = (sidebarDisplayDayData?.draft_slots || [])
                  .filter((s) => ganttDisplayRoleKey(s.role) === role)
                  .filter((s) => {
                    const label = slotDraftDisplayName(s, liveStaff || []);
                    return (
                      label &&
                      label !== "__gap__" &&
                      !label.startsWith("Optimal_") &&
                      !label.startsWith("OPTIMAL_")
                    );
                  });
                const seen = new Set();
                const dedupedDraftSlots = filteredDraftSlots.filter((s) => {
                  const key = slotDraftDisplayName(s, liveStaff || []) + (s.start || "") + (s.end || "") + s.role;
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                });
                const roleMissingRaw = (sidebarDisplayDayData?.missing_slots || []).filter((s) => ganttDisplayRoleKey(s.role) === role);
                const hasOverlapWithRealDraft = (missingSlot) => {
                  const mStart = toHour(missingSlot?.start);
                  const mEnd = intervalEndHourFloat(missingSlot?.start, missingSlot?.end);
                  return dedupedDraftSlots.some((d) => {
                    const dStart = toHour(d?.start);
                    const dEnd = intervalEndHourFloat(d?.start, d?.end);
                    const overlap = Math.max(0, Math.min(mEnd, dEnd) - Math.max(mStart, dStart));
                    return overlap > 0;
                  });
                };
                const roleMissing = roleMissingRaw.filter((slot) => !hasOverlapWithRealDraft(slot));
                if (roleOptimal.length === 0 && dedupedDraftSlots.length === 0 && roleMissing.length === 0) return null;
                const sortedDraft = [...dedupedDraftSlots].sort((a, b) => toHour(a?.start) - toHour(b?.start));
                const lanes = [];
                const laneByKey = new Map();
                sortedDraft.forEach((slot, idx) => {
                  const geo = timelineBarGeometry(slot?.start, slot?.end, sidebarTStart, sidebarTEnd);
                  const start = geo.startHour;
                  const end = geo.endHour;
                  let laneIdx = 0;
                  while (laneIdx < lanes.length && start < lanes[laneIdx]) laneIdx += 1;
                  if (laneIdx === lanes.length) lanes.push(end);
                  else lanes[laneIdx] = end;
                  laneByKey.set(`${slot?.employee_name || "emp"}-${slot?.start}-${slot?.end}-${idx}`, laneIdx);
                });
                const laneCount = Math.max(1, lanes.length);
                const roleHeight = Math.max(36, laneCount * 30 + (roleMissing.length > 0 ? 26 : 0) + 8);
                return (
                  <div key={`sidebar-role-${role}`} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 3 }}>
                      {role}
                    </div>
                    <div style={{ width: "100%", position: "relative", background: "var(--surface2)", borderRadius: 6, overflow: "hidden", marginBottom: 8, minHeight: roleHeight, height: roleHeight }}>
                      {!focusedDayIsClosed ? (
                        <>
                          <div
                            aria-hidden
                            style={{
                              position: "absolute",
                              left: 0,
                              width: sidebarOpenLinePct,
                              top: 0,
                              bottom: 0,
                              background: "rgba(0,0,0,0.04)",
                              pointerEvents: "none",
                              zIndex: 0,
                            }}
                          />
                          <div
                            aria-hidden
                            style={{
                              position: "absolute",
                              left: sidebarCloseLinePct,
                              right: 0,
                              top: 0,
                              bottom: 0,
                              background: "rgba(0,0,0,0.04)",
                              pointerEvents: "none",
                              zIndex: 0,
                            }}
                          />
                        </>
                      ) : null}
                      {SHIFTIQ_ENABLED ? roleOptimal.map((slot, i) => (
                        <div
                          key={`opt-${role}-${slot?.start}-${slot?.end}-${i}`}
                          style={{
                            position: "absolute",
                            left: sidebarLeftPct(slot),
                            width: sidebarWidthPct(slot),
                            height: 14,
                            top: 4,
                            background: meta.tint,
                            border: `1.5px dashed ${meta.fill}`,
                            borderRadius: 4,
                            fontSize: 8,
                            color: meta.fill,
                            paddingLeft: 3,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            opacity: 0.6,
                          }}
                        >
                          {`${meta.label} ${String(slot?.start || "").slice(0, 5)}–${String(slot?.end || "").slice(0, 5)}`}
                        </div>
                      )) : null}
                      {sortedDraft.map((slot, i) => {
                        const key = `${slot?.employee_name || "emp"}-${slot?.start}-${slot?.end}-${i}`;
                        const laneIdx = laneByKey.get(key) || 0;
                        const firstName = slotDraftDisplayName(slot, liveStaff || []).split(/\s+/)[0] || "Shift";
                        return (
                          <div
                            key={`drf-${role}-${key}`}
                            style={{
                              position: "absolute",
                              left: sidebarLeftPct(slot),
                              width: sidebarWidthPct(slot),
                              height: 22,
                              top: laneIdx * 28 + 4,
                              background: meta.fill,
                              borderLeft: slot?.drifts ? "3px solid #F97316" : "none",
                              borderRadius: 4,
                              color: "#fff",
                              fontSize: 8,
                              fontWeight: 700,
                              paddingLeft: 4,
                              paddingTop: 3,
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {`${firstName} ${String(slot?.start || "").slice(0, 5)}–${String(slot?.end || "").slice(0, 5)}`}
                          </div>
                        );
                      })}
                      {roleMissing.map((slot, i) => (
                        <div
                          key={`mis-${role}-${slot?.start}-${slot?.end}-${i}`}
                          style={{
                            position: "absolute",
                            left: sidebarLeftPct(slot),
                            width: sidebarWidthPct(slot),
                            top: laneCount * 28 + 4,
                            height: 18,
                            background: "transparent",
                            border: "1.5px dashed #EF4444",
                            borderRadius: 4,
                            color: "#EF4444",
                            fontSize: 8,
                            paddingLeft: 4,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {`✕ ${meta.label} ${String(slot?.start || "").slice(0, 5)}–${String(slot?.end || "").slice(0, 5)}`}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              </div>
              <div style={{ position: "relative", height: 16, marginTop: 6 }}>
                {Array.from({ length: Math.floor(sidebarTW / 2) + 1 }, (_, idx) => sidebarTStart + idx * 2).map((hour) => (
                  <div
                    key={`sx-${hour}`}
                    style={{
                      position: "absolute",
                      left: `${((hour - sidebarTStart) / sidebarTW) * 100}%`,
                      transform: "translateX(-50%)",
                      fontSize: 8,
                      color: "var(--text-muted)",
                    }}
                  >
                    {`${String(Math.floor(hour)).padStart(2, "0")}:00`}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
                  Working today
                </div>
                {sidebarRoles
                  .filter((role) => (sidebarRoleFilter === "all" ? true : sidebarRoleFilter === role))
                  .map((role) => {
                    const meta = sidebarRoleMeta[role];
                    const filteredDraftSlots = (sidebarDisplayDayData?.draft_slots || [])
                      .filter((s) => ganttDisplayRoleKey(s.role) === role)
                      .filter((s) => {
                        const label = slotDraftDisplayName(s, liveStaff || []);
                        return (
                          label &&
                          label !== "__gap__" &&
                          !label.startsWith("Optimal_") &&
                          !label.startsWith("OPTIMAL_")
                        );
                      });
                    const seen = new Set();
                    const dedupedDraftSlots = filteredDraftSlots.filter((s) => {
                      const key = slotDraftDisplayName(s, liveStaff || []) + (s.start || "") + (s.end || "") + s.role;
                      if (seen.has(key)) return false;
                      seen.add(key);
                      return true;
                    });
                    if (dedupedDraftSlots.length === 0) return null;
                    const roleMissingRaw = (sidebarDisplayDayData?.missing_slots || []).filter((s) => ganttDisplayRoleKey(s.role) === role);
                    const roleMissingUncovered = roleMissingRaw.filter((missingSlot) => {
                      const mStart = toHour(missingSlot?.start);
                      const mEnd = intervalEndHourFloat(missingSlot?.start, missingSlot?.end);
                      return !dedupedDraftSlots.some((d) => {
                        const dStart = toHour(d?.start);
                        const dEnd = intervalEndHourFloat(d?.start, d?.end);
                        const overlap = Math.max(0, Math.min(mEnd, dEnd) - Math.max(mStart, dStart));
                        return overlap > 0;
                      });
                    });
                    return (
                      <div key={`roster-${role}`} style={{ marginTop: 6 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", color: meta.fill, marginBottom: 3 }}>
                          {meta.label}
                        </div>
                        {dedupedDraftSlots.map((slot, i) => {
                          const firstName = slotDraftDisplayName(slot, liveStaff || []).split(/\s+/)[0] || "—";
                          return (
                            <div
                              key={`roster-line-${role}-${slot?.employee_name || "emp"}-${slot?.start}-${slot?.end}-${i}`}
                              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 0" }}
                            >
                              <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                {slot?.drifts ? (
                                  <span style={{ width: 6, height: 6, borderRadius: 999, background: "#F97316", display: "inline-block", marginRight: 4 }} />
                                ) : null}
                                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {firstName}
                                </span>
                              </div>
                              <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "monospace", marginLeft: 8 }}>
                                {`${String(slot?.start || "").slice(0, 5)}–${String(slot?.end || "").slice(0, 5)}`}
                              </span>
                            </div>
                          );
                        })}
                        {roleMissingUncovered.map((slot, i) => (
                          <div
                            key={`roster-uncovered-${role}-${slot?.start}-${slot?.end}-${i}`}
                            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 0" }}
                          >
                            <span style={{ fontSize: 12, color: "#EF4444", fontStyle: "italic" }}>Uncovered</span>
                            <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "monospace", marginLeft: 8 }}>
                              {`${String(slot?.start || "").slice(0, 5)}–${String(slot?.end || "").slice(0, 5)}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                {(() => {
                  const visibleRoles = sidebarRoleFilter === "all" ? sidebarRoles : [sidebarRoleFilter];
                  const uncoveredRoles = new Set();
                  visibleRoles.forEach((role) => {
                    const filteredDraftSlots = (sidebarDisplayDayData?.draft_slots || [])
                      .filter((s) => ganttDisplayRoleKey(s.role) === role)
                      .filter((s) => {
                        const label = slotDraftDisplayName(s, liveStaff || []);
                        return (
                          label &&
                          label !== "__gap__" &&
                          !label.startsWith("Optimal_") &&
                          !label.startsWith("OPTIMAL_")
                        );
                      });
                    const seen = new Set();
                    const dedupedDraftSlots = filteredDraftSlots.filter((s) => {
                      const key = slotDraftDisplayName(s, liveStaff || []) + (s.start || "") + (s.end || "") + s.role;
                      if (seen.has(key)) return false;
                      seen.add(key);
                      return true;
                    });
                    const roleMissingRaw = (sidebarDisplayDayData?.missing_slots || []).filter((s) => ganttDisplayRoleKey(s.role) === role);
                    const hasUncoveredMissing = roleMissingRaw.some((missingSlot) => {
                      const mStart = toHour(missingSlot?.start);
                      const mEnd = intervalEndHourFloat(missingSlot?.start, missingSlot?.end);
                      return !dedupedDraftSlots.some((d) => {
                        const dStart = toHour(d?.start);
                        const dEnd = intervalEndHourFloat(d?.start, d?.end);
                        const overlap = Math.max(0, Math.min(mEnd, dEnd) - Math.max(mStart, dStart));
                        return overlap > 0;
                      });
                    });
                    if (hasUncoveredMissing) uncoveredRoles.add(role);
                  });
                  if (uncoveredRoles.size === 0) return null;
                  return SHIFTIQ_ENABLED ? (
                    <div style={{ marginTop: 8, fontSize: 11, color: "#EF4444", fontWeight: 700 }}>
                      {`${uncoveredRoles.size} role(s) uncovered — use Fill gaps or assign manually`}
                    </div>
                  ) : (
                    <div style={{ marginTop: 8, fontSize: 11, color: "#EF4444", fontWeight: 700 }}>
                      {`${uncoveredRoles.size} role(s) uncovered — assign manually`}
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </div>
        {SHIFTIQ_ENABLED ? (
        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", background: "var(--surface)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
            {`${focusedCoveredOptimalCount}/${focusedOptimalSlots.length} optimal slots covered`}
          </div>
          <button
            type="button"
            className="btn-base btn-green"
            onClick={() => {
              const targetDay = focusDay;
              preGanttScrollY.current = window.scrollY;
              setVsOptimalActive(true);
              if (ganttNeedsRefreshRef.current) {
                ganttNeedsRefreshRef.current = false;
                setGanttRefreshKey((k) => k + 1);
              }
              setGanttScrollTarget(targetDay || null);
              setFocusDay(null);
            }}
            style={{ fontSize: 11, fontWeight: 700, padding: "6px 12px" }}
          >
            Open full Gantt →
          </button>
        </div>
        ) : null}
      </div>

      <BlockingActionOverlay
        open={Boolean(foxlineSync.transmitting) || Boolean(loadingAction && !vsOptimalActive)}
        title={
          foxlineSync.transmitting
            ? (lang === "gr" ? "Αποστολή στο SoftOne" : "Sending to SoftOne")
            : loadingAction
        }
        subtitle={
          foxlineSync.transmitting
            ? (lang === "gr"
              ? "Μην πατήσετε πίσω και μην ανανεώσετε τη σελίδα."
              : "Do not go back or refresh this page.")
            : (lang === "gr" ? "Παρακαλώ περιμένετε…" : "Please wait...")
        }
      />

    </div>
  );
}


